/*
 * ProfanityCensorTool — shared engine for /profanity-remover (audio) and
 * /profanity-remover-video.
 *
 * Pipeline:
 *   file -> FFmpeg extract 16k mono PCM (WORKERFS streaming, works for any
 *   container incl. video) -> GigaAM-v3 e2e-CTC worker (word timestamps)
 *   -> ProfanityDetector -> editable hit list + waveform with censored
 *   regions (click-to-seek, drag-to-censor manually) + live censored
 *   preview -> FFmpeg filter_complex export (atrim/concat, per-segment
 *   fades; beep frequency/volume configurable; custom replacement sound
 *   rides in as a second input) + timecode report.
 *
 * Blade provides the DOM skeleton; this module owns all logic.
 */
(function (window) {
    'use strict';

    var SR = 16000;
    // WHERE each censor goes (word edges, coverage, what joins what) lives in
    // profanity-regions.js — one owner, shared with the node guards, so the
    // numbers measured there are the numbers the page runs. Resolved in
    // create(): a fallback-loaded copy can arrive after this file parsed.
    var PR = null;
    // Region caps are per EXPORT ARCHITECTURE. Measured 2026-08-06 (300 s
    // file, ffmpeg-wasm): the atrim/concat graph is superlinear in region
    // count — 42 s at 150 regions, 281 s at 500 — so its old blanket cap of
    // 150 silently inflated the merge gap on dense files (275 hits → 5 s gap
    // → whole sentences beeped; real uploads reach 1672 hits). beep/silence/
    // duck now export through a 3-chain timeline-gate graph (buildFilterGraph)
    // that did 1672 regions in 6.6 s, so their cap is a distant safety net.
    // Since 09.10.2026 the replacement sound rides the same gate graph as an
    // underlay (it starts at each censor's beginning by construction), so it
    // has no ceiling of its own: its old 150 joined everything closer than
    // ~3 s — 414 censors became 141, «Заглушено» 110 → 444 s (chat 640).
    var MAX_REGIONS = 2000;                      // timeline-gate graph safety cap
    var PEAK_BUCKET = 256;                       // samples per master-peak bucket (16 kHz -> 62.5/s)
    var CUSTOM_MAX_MB = 20;                      // replacement-sound file cap
    var CUSTOM_MAX_S = 30;                       // replacement sound: first 30 s used, looped

    function $(id) { return document.getElementById(id); }
    function fmtTime(s) {
        s = Math.max(0, s);
        var m = Math.floor(s / 60), sec = Math.floor(s % 60);
        return m + ':' + (sec < 10 ? '0' : '') + sec;
    }
    /* A short length the way a person reads it: «0,4 с» for a rustle, «1:05»
       past a minute. fmtTime floors to whole seconds, so a 0.4 s sound read
       «0:00» — as if nothing had loaded (chat 640). */
    function fmtShortDur(s, sec) {
        if (!(s > 0)) return '0';
        if (s >= 60) return fmtTime(s);
        var v = s < 10 ? (Math.round(s * 10) / 10) : Math.round(s);
        var txt = String(v);
        try { txt = v.toLocaleString(window.__intlLocale ? window.__intlLocale() : undefined); } catch (e) { /* plain */ }
        return txt + ' ' + String(sec || 's').trim();
    }
    function fmtTimecode(s) {
        /* Whole ms first: 3.9996 s is 0:04.000, not 0:03.1000. */
        var total = Math.round(s * 1000);
        return fmtTime(Math.floor(total / 1000)) + '.' + ('00' + (total % 1000)).slice(-3);
    }

    function create(cfg) {
        var i18n = cfg.i18n || {};
        var slug = cfg.slug;
        PR = window.ProfanityRegions;
        if (!PR) throw new Error('ProfanityRegions is not loaded');

        /* ---------------- settings ---------------- */
        function loadSetting(key, dflt) {
            try { return window.__toolSettings ? window.__toolSettings.load(slug, key, dflt) : dflt; } catch (e) { return dflt; }
        }
        function saveSetting(key, val) {
            try { if (window.__toolSettings) window.__toolSettings.save(slug, key, val); } catch (e) { /* noop */ }
        }
        // pre-rework saved modes 'beep1000'/'beep440' migrate to mode 'beep' + freq
        var savedMode = loadSetting('mode', 'beep');
        var migratedFreq = savedMode === 'beep440' ? 440 : 1000;
        if (savedMode === 'beep1000' || savedMode === 'beep440') savedMode = 'beep';
        if (['beep', 'silence', 'duck', 'custom'].indexOf(savedMode) === -1) savedMode = 'beep';
        // 'coverage' replaces the old binary edgePreserve toggle (VideoCensor
        // parity, Oleg 14.06): % of the word that gets censored. 100 = full
        // censor (default, our historical behaviour). Lower = keep more of the
        // word audible on each side, so "б…ть" stays recognizable. Migrate the
        // old edgePreserve=1 to 60% (≈ the 7% per-side edge it used to keep,
        // bumped to the VideoCensor-familiar 60 default so the slider lands
        // somewhere visible).
        var savedCoverage = loadSetting('coverage', '');
        if (savedCoverage === '') savedCoverage = (loadSetting('edgePreserve', '0') === '1') ? '60' : '100';
        var savedVideoFx = loadSetting('videoFx', 'none');
        if (['none', 'darken', 'shake', 'float', 'border'].indexOf(savedVideoFx) === -1) savedVideoFx = 'none';
        // Spoken language of the AUDIO — independent of the interface locale
        // (an Arabic-speaking user may upload an English podcast). ALWAYS
        // explicit: measured, the Whisper wrapper does not auto-detect, it
        // forces English and translates (see asr-adapter.js). Persisted;
        // defaults to the page locale when we have a lexicon for it, else 'en'.
        var SUPPORTED_LANGS = (window.ProfanityLexicons && window.ProfanityLexicons.supported()) || ['ru'];
        var savedLang = loadSetting('lang', '');
        if (!savedLang || SUPPORTED_LANGS.indexOf(savedLang) === -1) {
            savedLang = (cfg.defaultLang && SUPPORTED_LANGS.indexOf(cfg.defaultLang) >= 0) ? cfg.defaultLang : 'en';
        }

        var settings = {
            mode: savedMode,                                 // beep|silence|duck|custom
            lang: savedLang,                                 // audio language | 'auto'
            beepFreq: parseInt(loadSetting('beepFreq', String(migratedFreq)), 10) || 1000,
            beepVol: parseInt(loadSetting('beepVol', '50'), 10) || 50,   // percent
            // beep level follows the speech around each censor (ProfanitySound);
            // ON by default, like /audio-censor — the slider is the manual override
            beepAuto: loadSetting('beepAuto', '1') === '1',
            // tier2 (rude words: «сука», «дебил»…) is ON by default — Farid's
            // call 2026-08-07: most users expect these censored too; legally
            // they are not мат, which the checkbox caption now explains. A
            // user's explicit un-check is persisted and respected.
            tier2: loadSetting('tier2', '1') === '1',
            tier3: loadSetting('tier3', '0') === '1',
            coverage: parseInt(savedCoverage, 10) || 100,    // 0..100, % of word censored
            videoFx: savedVideoFx,                           // none|darken|shake|border (video tool only)
            blacklist: loadSetting('blacklist', ''),
            whitelist: loadSetting('whitelist', ''),
            bitrate: parseInt(loadSetting('bitrate', '192'), 10) || 192,
            // «глушить только голос» (vocal-only-export.js): под заглушкой звучит
            // то, что разделитель считает не голосом — игра, музыка, подложка
            voiceOnly: loadSetting('voiceOnly', '0') === '1',
            // the replacement sound plays ONCE per censor unless asked to repeat
            customLoop: loadSetting('customLoop', '0') === '1'
        };
        settings.beepFreq = Math.min(2000, Math.max(200, settings.beepFreq));
        settings.beepVol = Math.min(100, Math.max(10, settings.beepVol));
        settings.coverage = Math.min(100, Math.max(40, settings.coverage));

        function volGain() { return settings.beepVol / 100; }
        var SND = window.ProfanitySound;
        function beepSettings() { return { volume: volGain(), auto: settings.beepAuto }; }
        // Beep level of each rendered region — ONE number read by the preview
        // and by the export graph (see profanity-sound.js for why that matters).
        function levelRegions() {
            var s = beepSettings();
            for (var i = 0; i < regions.length; i++) regions[i].peak = SND.beepPeak(pcmAudio, regions[i], s);
            // the preview's gate carries each region's level with its bounds
            pushGateRegions();
        }
        // What «▶ послушать заглушку» plays: the typical level of this file's
        // censors when auto is on, the slider level otherwise.
        function typicalBeepPeak() {
            if (!settings.beepAuto || !regions.length) return SND.beepPeak(null, null, beepSettings());
            var a = regions.map(function (r) { return r.peak; }).sort(function (x, y) { return x - y; });
            return a[a.length >> 1];
        }

        /* ---------------- state ---------------- */
        var file = null, fileExt = '', isVideoFile = false;
        var duration = 0, channels = 2, srcRate = 44100;
        // FFmpeg's zero for this file («start:» of its probe): every run renders
        // from it, so the zero-copy lane puts the soundtrack back at that point
        // of the original timeline (renderLead). 0 for nearly every file.
        var srcStartS = 0;
        var pcm = null;                 // Int16Array 16k mono (zero-snap + peaks; f32 copy goes to the worker)
        var words = [];                 // [{text,start,end,conf}]
        var hits = [];                  // detector output
        var overrides = {};             // wordIndex -> true (force mute) / false (force keep)
        var manualRegions = [];         // [{id,start,end}] waveform drag-created
        var manualSeq = 1;
        /* Every censor is ONE editable item — the detector's and the user's
         * alike. Until 24.09.2026 only drawn regions could be resized; a
         * detected one could only be switched off and redrawn by hand, and
         * that is exactly what Ангелина did: 193 of the 233 censors in her
         * 37-minute export were redrawn replacements (2.6 hours, 216
         * `manual_region` events, three Ctrl+Z presses that did nothing).
         * `edits` holds the bounds the user set for a detected word; the word
         * keeps its identity, so re-detection (tiers, lists) keeps the edit
         * and «как нашла нейросеть» can put the computed bounds back. */
        var edits = {};                 // wordIndex -> {start,end}
        var autoCache = {};             // wordIndex -> computed region (coverage-dependent)
        var items = [];                 // [{id,kind:'auto'|'manual',wi|mid,start,end,label,edited,hitKind}]
        var regions = [];               // what is rendered/exported: items laid out (overlaps joined)
        var selectedId = null;
        var capMergeReported = false;   // one region_cap_merge event per file
        var pcmAudio = null;            // ProfanityRegions.audio(pcm) — edge walks, zero crossings
        var workerReady = false, workerInitPromise = null;   // `asr` (the engine adapter) lives with the ASR block
        var swWaitMs = null, swGateWhy = '';   // сколько ждали Service Worker и чем кончилось (для телеметрии)
        var detector = null;
        var mediaEl = null, mediaUrl = null, srcNode = null;
        var audioCtx = null, gainNode = null, beepOsc = null, beepGain = null, customSrc = null, rafId = 0;
        // "audition" = play ONE hit's original audio uncensored so the user can
        // hear what was actually recognized (right word? worth keeping?) without
        // flipping the global "preview with censor" toggle. Holds {start,end} of
        // the region being auditioned, or null. Cleared on pause/seek/new-file.
        var auditionRegion = null;
        var phase = 'idle';             // idle|extract|asr|ready|export

        /* Единственная точка смены фазы. Раньше `phase` присваивалась в 10
         * местах — с таким разбросом «взвести/снять защиту от ухода» неизбежно
         * где-нибудь забылось бы (а забытый взвод хуже отсутствия: юзер не
         * может уйти со страницы, где всё давно закончилось). Работа идёт ⇒
         * защита взведена; любое другое состояние ⇒ снята. */
        /* ── Детектор жёсткой смерти вкладки ──────────────────────────────
         * iOS убивает вкладку по памяти БЕЗ единого события: ни error, ни
         * pagehide, ни телеметрии — юзер видит перезагрузившуюся пустую
         * страницу («Не дал аудио», репорт 2026-08-03). Замер: пик процесса
         * ~1.3 ГБ на создании ORT-сессии GigaAM — 60% iOS-сессий с видео
         * умирали беззвучно, в статистике класс был неотличим от «ушёл».
         * Маркер в localStorage ставится на входе в тяжёлую фазу и снимается
         * на ЛЮБОМ штатном исходе (ready/error/сброс) и на pagehide (штатный
         * уход). Живой маркер при следующей загрузке = прошлый заход был убит.
         * bfcache-возврат в занятую фазу ставит маркер обратно (pageshow).
         *
         * Детектор родился здесь (03.08.2026), а с 07.10.2026 живёт в одном
         * месте — `window.__crashMarker` (timbrica-core.js); здесь остались
         * только фазы и окно свежести. Своей копии правила тут быть не должно:
         * она считала маркер соседней РАБОТАЮЩЕЙ вкладки некрологом (заявка на
         * возврат №39 — тот же дефект у /vocal-remover), а чинить его в двух
         * местах значит однажды починить в одном. */

        /* Сколько маркер считается «про этот заход».
         *
         * ⚠️Здесь стояло 45 минут — и это было НЕ ПРОСТО магическое число, а
         * ошибка: предельная длина файла берётся из тиров (`max_duration_s`,
         * у Премиума — часы), распознавание идёт медленнее реального времени,
         * поэтому честный прогон на длинном файле легко живёт дольше 45 минут.
         * Пользователь, у которого вкладку убило на 90-й минуте, не получал ни
         * баннера, ни события — то есть детектор молчал ровно на самых тяжёлых
         * случаях, ради которых и делался.
         *
         * Считаем от того же источника правды, что и вся длительностная логика
         * тула: потолок тира (или устройства, если тир не назван) плюс запас на
         * скачивание модели и загрузку страницы. Верхняя граница — сутки:
         * маркер старше этого возраста уже ничего не доказывает про сейчас. */
        function crashMarkerMaxAgeS() {
            var ceilS = 0;
            try { ceilS = Math.max(tierCeilingS() | 0, premiumCeilingS() | 0, longFileCeilingS() | 0); } catch (e) { ceilS = 0; }
            if (!ceilS) ceilS = 3600;
            // ×2 — распознавание медленнее реального времени на слабых
            // устройствах; +30 мин — модель и подготовка.
            return Math.min(86400, ceilS * 2 + 1800);
        }
        function hotMark(p) {
            try {
                if (window.__crashMarker) window.__crashMarker.arm(slug, p, {
                    video: isVideoFile ? 1 : 0,
                    mb: file ? file.size / 1048576 : 0
                });
            } catch (e) { /* детектор просто молчит */ }
        }
        function hotClear() { try { if (window.__crashMarker) window.__crashMarker.disarm(slug); } catch (e) { /* noop */ } }

        function setPhase(p) {
            var was = phase;
            phase = p;
            // лейн «только голос» ждёт конца распознавания: модель разделителя
            // рядом с моделью распознавания — второй горб памяти на телефоне
            if (p === 'ready' && was !== 'ready' && was !== 'export') voiceLaneUpdate();
            try {
                var busy = (p === 'extract' || p === 'asr' || p === 'export');
                if (window.__unsavedWork) {
                    if (busy) window.__unsavedWork.arm(slug + ':' + p);
                    else window.__unsavedWork.disarm();
                }
                if (busy) hotMark(p); else hotClear();
            } catch (e) { /* noop */ }
        }
        // First-visit RTF estimate so the predictive progress bar is ALIVE from
        // t=0. Without a seed, observedRtf is 0 on the first visit → tickProgress
        // skips prediction (rtf>0.05 gate) → the bar sits FROZEN at 0% for one
        // whole ASR window while the worker runs (up to ~2 min on a weak phone
        // at RTF 0.2). That frozen-0% bar is exactly what the 2026-06-29 reporter
        // (Yandex-search first-visit, Samsung A04s) read as "не загружается".
        // The estimate is conservative-low per device class (under-shooting RTF
        // makes the bar advance slower than reality — safe, never overshoots the
        // 99% cap and stalls) and is replaced by the REAL measured RTF the moment
        // window 1 completes. Returning visitors keep their persisted real value.
        function defaultRtf() {
            var ua = navigator.userAgent || '';
            var mob = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
            var mem = navigator.deviceMemory || (mob ? 3 : 8);
            if (mob) return mem <= 3 ? 0.25 : (mem <= 4 ? 0.5 : 1.0);
            return mem <= 4 ? 1.5 : 4.0;
        }
        var observedRtf = parseFloat(loadSetting('observed_rtf', '0')) || defaultRtf();
        var jobSeq = 0;                 // generation counter: stale async work bails out

        // waveform
        var masterPeaks = null;         // {mins,maxs,count} at PEAK_BUCKET resolution
        var peaksCache = null;          // downsampled to current css width
        var waveW = 0, waveH = 0;       // ЛЕНТА целиком, в CSS-px (окно × zoom)
        var resizeObs = null;
        // Увеличение дорожки (Ангелина, 21.09.2026). 1 = весь файл в окне.
        // Верх считается от длительности: в максимуме в окне остаётся
        // MIN_VISIBLE_S секунд, поэтому на 10-минутной записи зум доходит
        // до ~140×, а на 20-секундной — до 7×, и дальше увеличивать нечего.
        var zoom = 1, zoomReported = false;
        var MIN_VISIBLE_S = 4, ZOOM_STEP = 2, ZOOM_HARD_MAX = 512;

        // progress predictor (smooth bar between coarse worker callbacks)
        var prog = { active: false, stage: '', realPct: 0, realAt: 0, rtf: 0, totalS: 0, jobStart: 0, timer: 0 };

        // custom replacement sound
        var customFile = null, customBuffer = null, customDur = 0;
        var customResampled = null;     // {sr, ch, data:[Float32Array]} at the SOURCE rate — the export's underlay

        // Language the LEXICON runs in — always the picked one; the transcript
        // is only inspected afterwards to suggest a correction.
        var activeLang = settings.lang;

        /* Единственный разбор пользовательских списков — им же пользуется
         * импорт из файла, иначе «через файл работает, руками нет» вернётся
         * с другой стороны. Пробел/таб/перевод строки/запятая/точка с запятой. */
        function splitUserList(raw) {
            return String(raw || '').split(/[\s,;]+/).map(function (s) { return s.trim(); }).filter(Boolean);
        }

        function makeDetector() {
            return window.ProfanityDetector.create({
                lang: activeLang || 'ru',
                tiers: { 0: true, 1: true, 2: settings.tier2, 3: settings.tier3 },
                // ⚡ПРОБЕЛ — ТОЖЕ разделитель (жалоба Kaypon #1243, 31.07:
                // «пишу туда слова, но не срабатывает»). Раньше резали только по
                // \n , ; — и «слово1 слово2» становилось ОДНОЙ записью, которая
                // не совпадёт НИКОГДА: детектор сравнивает запись с отдельным
                // токеном (`norm === be.w`), то есть многословные записи мертвы
                // по построению. Значит резать по пробелу строго безопасно и
                // строго лучше: ничего рабочего не ломает, а тихий провал
                // убирает. `\s` покрывает и перевод строки, и таб.
                blacklist: splitUserList(settings.blacklist),
                whitelist: splitUserList(settings.whitelist)
            });
        }

        /* What the person's own censoring says about the SHARED dictionary
         * (lexicon-signals.js): a word they censor that it misses, a word it
         * catches that they un-censor. Asked against the dictionary alone — all
         * tiers, no personal lists — and sent once per word per page. */
        var lexBaseDet = null, lexBaseLang = '', lexSent = {}, lexSentN = 0;
        function lexBase(lang) {
            if (!lexBaseDet || lexBaseLang !== lang) {
                try { lexBaseDet = window.ProfanityDetector.create({ lang: lang, tiers: { 0: true, 1: true, 2: true, 3: true } }); }
                catch (e) { lexBaseDet = null; }
                lexBaseLang = lang;
            }
            return lexBaseDet;
        }
        function lexVersion(lang) { var PL = window.ProfanityLexicons; return PL && PL.version ? PL.version(lang) : ''; }
        function lexSignal(kind, src, token) {
            var LXS = window.ProfanityLexiconSignals;
            if (!LXS || !window.__ae || !window.ProfanityDetector || lexSentN >= 40) return;
            var lang = activeLang || 'ru', base = lexBase(lang);
            if (!base) return;
            var r = kind === 'gap' ? LXS.gap(token, lang, base) : LXS.falseHit(token, lang, base);
            if (!r) return;
            var w = kind === 'gap' ? r : r.w;
            if (lexSent[kind + ':' + w]) return;
            lexSent[kind + ':' + w] = 1; lexSentN++;
            // d — this page also says which words it flagged (lexSeenSignal): a removal
            // is weighed against the people the word was met by, and only then
            var p = { tool: slug, feature: kind === 'gap' ? 'lex_gap' : 'lex_fp', src: src, lang: lang, w: w,
                rule: kind === 'gap' ? '' : r.rule, lv: lexVersion(lang) };
            if (kind !== 'gap' && LXS.seen) p.d = 1;
            __ae('feature_use', p);
        }
        /* Which dictionary words were flagged in this recording — once per
         * recognition (and per restored work: the words stand flagged in front of
         * the person again). Without it «блядь — 10 people took the censor off»
         * read as a fault of the dictionary; against the hundreds who met the
         * word it is ten people's choice. */
        var lexSeenFor = '';
        function lexSeenSignal() {
            var LXS = window.ProfanityLexiconSignals;
            if (!LXS || !LXS.seen || !window.__ae || !window.ProfanityDetector || !hits.length) return;
            var lang = activeLang || 'ru', base = lexBase(lang);
            if (!base) return;
            var s = LXS.seen(hits.map(function (h) { return h.text; }), lang, base);
            if (!s.n || lexSeenFor === lang + '|' + s.w) return;       // the same words, the same page: said already
            lexSeenFor = lang + '|' + s.w;
            __ae('feature_use', { tool: slug, feature: 'lex_seen', lang: lang, n: s.n, w: s.w, lv: lexVersion(lang) });
        }
        // the lists hold the person's standing answers: read once the words are
        // there, and again a while after they edit them (not per keystroke)
        var lexListT = 0;
        function lexListSignalsSoon(ms) {
            clearTimeout(lexListT);
            lexListT = setTimeout(function () {
                splitUserList(settings.blacklist).forEach(function (e) { lexSignal('gap', 'list', e); });
                splitUserList(settings.whitelist).forEach(function (e) { lexSignal('fp', 'list', e); });
            }, ms || 0);
        }

        /* ---------------- progress UI ----------------
         * Worker progress arrives once per 24 s ASR window, so a short file
         * gets a single 0->100 jump. The ticker interpolates between real
         * callbacks using the measured (or last-session) RTF, and always
         * shows elapsed/ETA so the card never looks frozen. */
        function tmpl(key, t) { return (i18n[key] || ':t').replace(':t', t); }

        function setStage(name, pct, extra) {
            var el = $('pc-stage');
            if (el) el.textContent = name + (extra ? ' ' + extra : '');
            var wrap = $('pc-progress');
            if (wrap) wrap.style.display = pct < 0 ? 'none' : '';
            // Stage-aware honest note: the download stage explains the one-time
            // ~225 MB model fetch; the recognition stage sets the expectation
            // that ASR runs on-device and can take minutes on a weak phone — so
            // a slow (but moving) bar reads as "working", not "не загружается".
            var note = $('pc-model-note');
            if (note) {
                if (name === i18n.stage_model && i18n.model_note) {
                    // model size differs per engine (GigaAM 225 MB for ru, Whisper
                    // ~150 MB otherwise) — don't quote a number that is wrong half
                    // the time; the honest copy just says "downloaded once".
                    note.textContent = i18n.model_note; note.style.display = '';
                }
                else if (name === i18n.stage_asr && i18n.asr_note) { note.textContent = i18n.asr_note; note.style.display = ''; }
                else { note.style.display = 'none'; }
            }
            if (pct < 0) { stopTicker(); return; }
            drawPct(Math.max(0, Math.min(100, pct)));
        }
        function drawPct(pct) {
            var bar = $('pc-stage-bar');
            if (bar) bar.style.width = pct + '%';
            var lbl = $('pc-stage-pct');
            if (lbl) lbl.textContent = Math.round(pct) + '%';
        }
        function progReal(stage, pct, opts) {
            opts = opts || {};
            prog.stage = stage;
            prog.realPct = pct;
            prog.realAt = performance.now();
            if (opts.rtf) prog.rtf = opts.rtf;
            if (opts.totalS) prog.totalS = opts.totalS;
        }
        function startTicker() {
            if (prog.timer) return;
            prog.active = true;
            prog.timer = setInterval(tickProgress, 250);
        }
        function stopTicker() {
            prog.active = false;
            if (prog.timer) { clearInterval(prog.timer); prog.timer = 0; }
            var meta = $('pc-progress-meta');
            if (meta) meta.textContent = '';
        }
        function tickProgress() {
            if (!prog.active) return;
            var now = performance.now();
            var elapsedS = (now - prog.jobStart) / 1000;
            var pct = prog.realPct;
            var etaS = 0;
            if (prog.stage === 'asr' && prog.totalS > 0) {
                var rtf = prog.rtf || observedRtf;
                if (rtf > 0.02) {
                    // Predict the bar between the worker's per-window callbacks
                    // so it never sits still. One window is `windowPct` of the
                    // file; we advance LINEARLY by the rtf estimate inside that
                    // band, then switch to an ASYMPTOTIC creep for the last tenth
                    // so an OVER-estimated rtf can never freeze the bar at a hard
                    // cap — that freeze (bar stuck while the worker still grinds
                    // one slow window) is what read as "не загружается" on slow
                    // first-visit devices. A real callback resets realPct/realAt
                    // and the band slides forward; the bar jumps to catch up.
                    // One full ASR window advances the real bar by
                    // 100*WINDOW/totalS; we predict up to 95% of that (always
                    // BELOW the real per-window jump, so a real callback only
                    // ever moves the bar FORWARD, never backward). The absolute
                    // cap is 70% (not 30%): a short clip is ~1 window, so 30%
                    // pinned the bar there for the whole inference — the
                    // long-asymptote "stuck number" on files under ~76 s. 70%
                    // only binds for clips under ~33 s (~1 window) where a
                    // 0→~63% climb is the honest, proportionate progress.
                    var WINDOW_SEC_HINT = 24;
                    var windowPct = Math.min(95 * WINDOW_SEC_HINT / prog.totalS, 70);
                    var linAdvance = ((now - prog.realAt) / 1000) * rtf / prog.totalS * 100;
                    var knee = windowPct * 0.9;
                    var advance = linAdvance <= knee
                        ? linAdvance
                        : knee + (windowPct - knee) * (1 - Math.exp(-(linAdvance - knee) / (windowPct * 0.4 + 0.001)));
                    pct = Math.min(prog.realPct + advance, 99);
                    etaS = Math.max(0, prog.totalS * (1 - pct / 100) / rtf);
                }
                drawPct(pct);
            }
            var meta = $('pc-progress-meta');
            if (meta) {
                var s = tmpl('elapsed', fmtTime(elapsedS));
                if (etaS > 0.5) s += ' · ' + tmpl('eta', fmtTime(Math.ceil(etaS)));
                meta.textContent = s;
            }
        }

        /* ---------------- ASR engine (per language) ----------------
         * AsrAdapter picks GigaAM (ru, self-hosted) or Whisper (everything
         * else) and normalizes both protocols to {words:[{text,start,end,conf}]}.
         * `asr` is rebuilt whenever the audio language changes. */
        var asr = null, asrLang = null;

        /* Состояние Service Worker на момент старта загрузки — ТОЛЬКО для
         * телеметрии, БЕЗ ожидания.
         *
         * Ждать контроллер пробовали и отвергли ЗАМЕРОМ (2026-08-01): на проде
         * SW регистрируется отложенно и берёт управление через 4.5–7.4 с
         * (разброс между чистыми профилями), то есть каждый новый юзер
         * простаивал бы до восьми секунд. Вместо этого модель кладёт в кэш сам
         * воркер (`cacheModelBytes` в gigaam-asr-worker.js) — ноль ожидания и
         * никакой зависимости от гонки регистрации. Здесь лишь фиксируем,
         * управлял ли SW, чтобы в проде было видно долю таких загрузок. */
        function swStateNow() {
            try {
                if (!('serviceWorker' in navigator)) return 'no-sw';
                return navigator.serviceWorker.controller ? 'controlled' : 'uncontrolled';
            } catch (e) { return 'error'; }
        }

        function ensureWorker() {
            // Hard SIMD stop at the DOWNLOADER: this is the single place the
            // ASR model bytes (GigaAM 225 MB / Whisper 39–244 MB) start
            // moving, and process() kicks it off IN PARALLEL with the FFmpeg
            // extract (line ~2040) — a stop in runAsr alone let the download
            // race ahead (E2E matrix caught gigaam-v3 fetching on a no-SIMD
            // run). ORT ≥1.19 ships only the SIMD binary; pre-2010 CPUs fail
            // at compile guaranteed. The reject lands in the existing process
            // catch → reportAndToast shows the gate's localized explanation.
            // Covers BOTH slugs (profanity-remover + -video) — shared core.
            if (window.DeviceCapabilityGate && window.DeviceCapabilityGate.requireSimd) {
                var _gS = window.DeviceCapabilityGate.requireSimd(slug);
                if (!_gS.ok) return Promise.reject(new Error(_gS.message));
            }
            var want = settings.lang;   // 'auto' → whisper auto-detect
            // A Whisper engine killed by cancel() cannot be reused — rebuild it.
            if (asr && asr.isDead && asr.isDead()) { asr = null; asrLang = null; workerInitPromise = null; }
            if (asr && asrLang === want && workerInitPromise) return workerInitPromise;
            if (asr) { try { asr.terminate(); } catch (e) { /* noop */ } asr = null; }
            asrLang = want;
            // Гейт стоит ЗДЕСЬ — это единственное место, откуда стронутся байты
            // модели. Ставить его в processFile нельзя: смена языка тоже зовёт
            // ensureWorker() и качает новую модель.
            var swAtStart = swStateNow();
            asr = window.AsrAdapter.create(want === 'auto' ? 'auto' : want, cfg, {
                onModelProgress: function (loadedMB, totalMB) {
                    setStage(i18n.stage_model, totalMB ? Math.round(loadedMB / totalMB * 100) : 0,
                        totalMB === 100 ? '' : '(' + loadedMB + (totalMB ? ' / ' + totalMB : '') + ' MB)');
                },
                onModelPreparing: function () { setStage(i18n.stage_preparing || i18n.stage_model, 100); },
                onModelReady: function (ms, info) {
                    workerReady = true;
                    // ⚠️Один `ms` НЕ ГОДИТСЯ как метрика кэша: он смешивает
                    // сеть, чтение с диска и компиляцию ONNX. Из-за этого
                    // «37% медленных» месяц читались как «кэш не работает»,
                    // хотя тёплый прогон = 3.6 с, а холодный = 85 с (замер).
                    // Теперь шлём ФАКТ: `cached` из Resource Timing
                    // (transferSize===0 при непустом теле = отдано кэшем),
                    // `sw` — дождались ли управления и сколько ждали.
                    var meta = { tool: slug, feature: 'asr_model_ready', ms: ms || 0, engine: asr.engine, lang: want };
                    // ⚡Факты берём из ВОРКЕРА: модель качает он, и запись
                    // Resource Timing живёт в его таймлайне. Замер со страницы
                    // давал undefined — та же ловушка, что с CDP-сессией.
                    if (info) {
                        if (info.cached !== undefined) meta.cached = info.cached;
                        if (info.netMB !== undefined) meta.netmb = info.netMB;
                        if (info.modelMB !== undefined) meta.mb = info.modelMB;
                        if (info.selfCached !== undefined) meta.self_cached = info.selfCached;
                    }
                    if (swWaitMs !== null) { meta.sw_wait_ms = swWaitMs; meta.sw = swGateWhy || ''; }
                    if (window.__ae) __ae('feature_use', meta);
                },
                onProgress: function (doneS, totalS, rtf) {
                    lastAsrMsgAt = Date.now();
                    // REAL decode progress: from here on the watchdog can trust
                    // the adaptive per-window budget instead of the cold-start
                    // grace (see runAsr — first-window budget must not trust the
                    // optimistic first-visit rtf seed).
                    if (doneS > 0) { asrRealProgressAt = Date.now(); asrDecodeProgressed = true; }
                    // Mid-run checkpoint (first-visit backstop). Decode rate is
                    // measured between THIS job's progress callbacks — the rtf
                    // argument can't be used here: whisper's includes the whole
                    // up-front mel phase in elapsed, overstating the remainder
                    // several-fold on long files. One full window between
                    // callbacks (span >= 24 s of audio) makes the delta real.
                    // If the remainder is long, say so once, honestly — the
                    // user decides whether to wait or pick a shorter clip.
                    // Skipped when the pre-flight dialog already asked (the
                    // user consented to the estimate; don't nag twice).
                    if (doneS > 0 && longJobToasted !== jobSeq && longJobAsked !== jobSeq) {
                        if (pcProgBase.job !== jobSeq) pcProgBase = { job: jobSeq, doneS: doneS, at: Date.now() };
                        var spanS = doneS - pcProgBase.doneS;
                        var spanT = (Date.now() - pcProgBase.at) / 1000;
                        // Two full windows: the first decode window runs slower
                        // (WASM warmup) — a one-window rate overstated the
                        // remainder ~2× (measured: est 885 s vs ~430 s actual).
                        if (spanS >= 48 && spanT > 1) {
                            var remS = (totalS - doneS) / (spanS / spanT);
                            if (remS > (window.__pcLongJobMidS || 1800)) {
                                longJobToasted = jobSeq;
                                try { window.__toast((i18n.long_job_toast || 'About :t left on this device. Keep this tab open.').replace(':t', fmtCoarse(remS)), 'warning'); } catch (e) { /* noop */ }
                                if (window.__ae) __ae('feature_use', {
                                    tool: slug, feature: 'long_job_confirm', src: 'midrun',
                                    est_s: Math.round(remS), rate: Math.round(spanS / spanT * 100) / 100
                                });
                            }
                        }
                    }
                    var pct = totalS ? Math.round(doneS / totalS * 100) : 0;
                    setStage(i18n.stage_asr, pct);
                    progReal('asr', pct, { rtf: rtf, totalS: totalS });
                },
                // Whisper preprocesses the mel of the ENTIRE file before the
                // first token (transformers.js does one processor() call per
                // ~29 s window, all up front). These callbacks are the only
                // heartbeat during that phase — minutes long on big files —
                // and double as an honest "analyzing audio N%" stage instead
                // of a predictive bar creeping over a phase it knows nothing
                // about. prog.stage='analyze' parks the asr predictor.
                onPreprocess: function (done, total) {
                    lastAsrMsgAt = Date.now();
                    // Mel advancing is REAL forward motion (feeds the 30-min
                    // ceiling) but does NOT lift the cold-start grace: the
                    // silent encoder pass right after the last mel window still
                    // needs the big budget on slow devices.
                    asrRealProgressAt = Date.now();
                    var pct = total ? Math.round(done / total * 100) : 0;
                    setStage(i18n.stage_analyze || i18n.stage_asr, pct);
                    progReal('analyze', pct, {});
                },
                // Liveness only (tokens streaming / in-worker recovery): the
                // worker is alive, so don't let the watchdog abort it — but
                // do NOT feed the progress predictor from here.
                onAlive: function () { lastAsrMsgAt = Date.now(); },
                onPartialWords: function (w) { lastAsrMsgAt = Date.now(); appendTranscript(w); },
                isStale: function (job) { return job !== jobSeq; }
            });
            // Стронуть байты ТОЛЬКО после того, как SW взял управление —
            // иначе запрос уйдёт мимо кэша и эти 225 МБ придётся качать снова
            // на следующем визите (замерено).
            swGateWhy = swAtStart;
            swWaitMs = 0;                      // ожидания больше нет — см. коммент выше
            workerInitPromise = asr.init();
            workerInitPromise.catch(function () {
                // one transient failure must not brick the tool: drop the dead
                // engine so the next upload retries (cached bytes come back fast)
                workerInitPromise = null; workerReady = false;
                try { if (asr) asr.terminate(); } catch (e) { /* noop */ }
                asr = null; asrLang = null;
            });
            return workerInitPromise;
        }

        /* ---------------- extract PCM ---------------- */
        async function extractPcm() {
            setStage(i18n.stage_extract, 5);
            var ok = await FFmpegProcessor.init(function (p) {
                setStage(i18n.stage_extract, Math.round(p * 30));
            });
            if (!ok) throw new Error('ffmpeg init failed');
            var onProg = function (e) {
                var p = e && e.detail; // ffmpeg-progress detail IS the 0..1 number
                if (typeof p === 'number') setStage(i18n.stage_extract, 30 + Math.round(p * 65));
            };
            window.addEventListener('ffmpeg-progress', onProg);
            try {
                // ⚠️ `aresample=async=1:first_pts=0` — не косметика. У записи
                // с игры/OBS звуковая дорожка часто начинается ПОЗЖЕ картинки
                // (у проверенного файла — на 1.46 с). Сырой PCM меток времени
                // не несёт, поэтому без этой добавки зазор просто исчезает: в
                // расшифровке слово стоит на 9.9 с, а в ролике звучит на 11.3,
                // и заглушка ложится в полутора секундах ДО мата — то есть мат
                // остаётся слышен, а тишина съедает соседнюю речь. Добавка
                // заполняет зазор тишиной, и дорожка, слова, превью и экспорт
                // живут в ОДНОМ времени — времени контейнера. Для файла без
                // сдвига это ничего не меняет.
                var res = await FFmpegProcessor.process(
                    file,
                    ['-vn', '-af', 'aresample=async=1:first_pts=0',
                        '-ac', '1', '-ar', String(SR), '-f', 's16le', '-acodec', 'pcm_s16le'],
                    'asr-audio.pcm'
                );
                // This PCM blob is an INTERNAL intermediate, not the tool's
                // output. FFmpegProcessor.process calls createObjectURL on it,
                // which the global blob auto-tracker would otherwise count as a
                // phantom tool_complete ~2 s later — inflating the funnel with
                // ~198 fake "successes" / 48 h (the real video export is a
                // separate, much rarer event). Register it to be skipped.
                try { if (window.__skipBlobAutoTrack) window.__skipBlobAutoTrack(res.blob); } catch (e) { /* noop */ }
                var buf = await res.blob.arrayBuffer();
                return new Int16Array(buf); // kept as i16 (half the RAM); f32 made per-transfer
            } finally {
                window.removeEventListener('ffmpeg-progress', onProg);
            }
        }

        /* ---------------- long-job time estimate ----------------
         * Honest pre-flight: recognition time ≈ duration / rtf_e2e, where
         * rtf_e2e is THIS device's measured end-to-end ASR rate (mel+decode,
         * NOT extract/model-download), keyed per engine+backend — a WebGPU
         * measurement must never predict a WASM run (≈10× apart), and vice
         * versa. Measured on every successful run; the first visit has no
         * measurement and uses the conservative device-class seed with a
         * higher warn bar (details in maybeConfirmLongJob). */
        function rtfKey() {
            var backend = (asr && asr.backend && asr.backend()) || 'wasm';
            return 'rtf_e2e_' + (asr ? asr.engine : 'whisper') + '_' + backend;
        }
        function loadRtfE2e() { return parseFloat(loadSetting(rtfKey(), '0')) || 0; }

        // "1 h 20 min" / "25 min" via locale templates. Minutes rounded to 5
        // on the hours scale — the estimate is honest about being coarse.
        function fmtCoarse(sec) {
            var h = Math.floor(sec / 3600);
            var m = h > 0 ? Math.round((sec % 3600) / 60 / 5) * 5 : Math.max(1, Math.round(sec / 60));
            if (h > 0 && m === 60) { h++; m = 0; }
            if (m === 60) { h = 1; m = 0; }
            var tpl = h > 0 ? (i18n.long_hm || ':h h :m min') : (i18n.long_m || ':m min');
            var out = tpl.replace(':h', h).replace(':m', m);
            // "2 h 0 min" → "2 h" (every locale template ends with its minute
            // unit; dropping the zero-minutes tail is safe and reads better)
            if (h > 0 && m === 0) out = out.replace(/\s*\b0\s*\S+\s*$/, '');
            return out;
        }

        // Confirm dialog that also works for declined-cookies users
        // (__confirmDialog lives inside the consent-gated telemetry block).
        function pcConfirm(opts) {
            if (window.__confirmDialog) return window.__confirmDialog(opts);
            return new Promise(function (resolve) {
                var ov = document.createElement('div');
                ov.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,.6);z-index:9999;display:flex;align-items:center;justify-content:center;padding:16px';
                var d = document.createElement('div');
                d.setAttribute('role', 'dialog'); d.setAttribute('aria-modal', 'true');
                d.style.cssText = 'background:var(--bg-primary,#1a1a2e);border:1px solid var(--border,#333);border-radius:16px;padding:24px;max-width:480px;width:100%;color:var(--text-primary,#fff)';
                var html = '<div style="font-size:16px;font-weight:600;margin-bottom:8px">' + (opts.icon || '⚠️') + ' ' + (opts.title || '') + '</div>';
                html += '<div style="font-size:14px;color:var(--text-secondary,#aaa);margin-bottom:16px;line-height:1.4">' + (opts.message || '') + '</div>';
                html += '<div style="display:flex;flex-direction:column;gap:8px">';
                (opts.choices || []).forEach(function (c) {
                    var st = c.primary
                        ? 'padding:12px;background:var(--accent-gradient,linear-gradient(135deg,#38bdf8,#0ea5e9));color:var(--on-accent);border:none;border-radius:10px;font-size:14px;font-weight:600;cursor:pointer'
                        : 'padding:10px;background:transparent;color:var(--text-secondary,#aaa);border:1px solid var(--border,#333);border-radius:10px;font-size:13px;cursor:pointer';
                    html += '<button data-id="' + c.id + '" style="' + st + '">' + c.label + '</button>';
                });
                html += '</div>';
                d.innerHTML = html;
                ov.appendChild(d); document.body.appendChild(ov);
                function close(id) { try { document.body.removeChild(ov); } catch (e) { /* noop */ } resolve(id); }
                d.addEventListener('click', function (e) { var b = e.target.closest('[data-id]'); if (b) close(b.dataset.id); });
                ov.addEventListener('click', function (e) { if (e.target === ov) close('cancel'); });
            });
        }

        var longJobAsked = 0;   // jobSeq that already saw the pre-flight dialog (skip the mid-run nag)
        var longJobToasted = 0; // jobSeq that already got the one mid-run toast
        var pcProgBase = { job: 0, doneS: 0, at: 0 };   // first progress callback of the job — decode-rate baseline

        /* Decision point: duration and backend are both known (after extract +
         * model ready, before transcription). Two tiers of confidence:
         *   • measured rtf for THIS engine+backend (a previous run) → warn
         *     when the estimate exceeds 30 min — the estimate is trustworthy.
         *   • no measurement (first visit): WebGPU → never warn (measured
         *     10-30× realtime; the mid-run checkpoint catches surprises).
         *     WASM → the conservative device-class seed with a 60 min bar:
         *     an unproven estimate must clear a higher bar before it is
         *     allowed to interrupt the user.
         * Returns true to proceed, false when the user cancelled. */
        /* ---------------- too-long guard (memory, not patience) --------------
         * ЗАЧЕМ. Пайплайн держит аудио в памяти ТРИЖДЫ: PCM внутри WASM-кучи
         * ffmpeg + его ArrayBuffer-копия в JS (i16) + f32-копия для ASR
         * (строка ~529). Это ≈ dur × 128 КБ/с и растёт ЛИНЕЙНО с длительностью.
         * ЗАМЕРЕНО на 8-ГБ десктопе (tests/scratch, 07-28): 2 ч и 4 ч проходят,
         * 6 ч УБИВАЮТ вкладку; изолированный `new Float32Array(2 ГБ)` падает с
         * ровно той строкой, что прислал юзер, — «Array buffer allocation
         * failed» (жалоба viljoen.hj, 9-часовая аудиокнига .m4b 246 МБ).
         *
         * ПОЧЕМУ КАП В МЕГАБАЙТАХ НЕ СПАСАЕТ: связывает ДЛИТЕЛЬНОСТЬ, а не
         * размер. Аудиокнига 64 кбит/с даёт 9 ч в 246 МБ, а WAV тех же 246 МБ —
         * 24 минуты. Один и тот же «вес» = 20-кратная разница в памяти.
         *
         * ПОЧЕМУ ПРОВЕРКА ЗДЕСЬ, А НЕ ПОСЛЕ: длительность раньше узнавалась
         * ТОЛЬКО из `pcm.length / SR`, то есть ценой самого дорогого и
         * необратимого шага. HTMLMediaElement отдаёт её из заголовка за ~6 мс
         * без WASM и без единого мегабайта (замерено на тех же файлах). */
        function longFileCeilingS() {
            var dm = navigator.deviceMemory || 0;
            var mob = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent || '');
            if (mob) return dm >= 6 ? 5400 : 3600;
            if (dm && dm <= 2) return 3600;
            if (dm && dm <= 4) return 7200;
            return 14400;   // 4 ч — замерено «проходит» на 8 ГБ, 6 ч уже смерть
        }

        /* Длительность из заголовка контейнера, без WASM. Любая осечка →
         * 0 = «не знаю», и тул работает ровно как раньше (гейт не срабатывает,
         * регресса нет): гадать мы не имеем права, а ложный отказ хуже краха. */
        function probeDurationFast(f) {
            return headerDurationS(f).then(function (d) { return d > 0 ? d : mediaElementDurationS(f); });
        }
        /* Заголовок контейнера — ПЕРВЫМ, до элемента <video>/<audio>. Превью на
         * части устройств не получает метаданных вовсе (Android/Яндекс,
         * readyState 0 — обращение 22.09.2026 на /auto-captions), и тогда эта
         * проба отдавала 0: предупреждение о тарифе не показывалось, а стена на
         * скачивании (tierWallDurS) не взводилась — файл сверх потолка уходил
         * бесплатно. headerAudioInfo читает moov (в т.ч. в хвосте) за
         * десятки мс, без элемента. MP3-заголовок лжёт штатно (Xing от склейки,
         * VBR без Xing) — ему не верим: для MP3 остаются элемент и длина PCM. */
        function headerDurationS(f) {
            var A = window.AudioDecoderRobust;
            if (!A || typeof A.headerAudioInfo !== 'function') return Promise.resolve(0);
            var cap = new Promise(function (r) { setTimeout(function () { r(0); }, 4000); });
            var read = Promise.resolve().then(function () { return A.headerAudioInfo(f); }).then(function (info) {
                return (info && !/mp3/i.test(info.method || '') && info.durSec > 0 && isFinite(info.durSec)) ? info.durSec : 0;
            }, function () { return 0; });
            return Promise.race([read, cap]);
        }
        function mediaElementDurationS(f) {
            return new Promise(function (resolve) {
                var el, url, done = false;
                function fin(v) {
                    if (done) return; done = true;
                    try { if (el) { el.removeAttribute('src'); el.load(); } } catch (e) { /* noop */ }
                    try { if (url) URL.revokeObjectURL(url); } catch (e) { /* noop */ }
                    resolve(v > 0 && isFinite(v) ? v : 0);
                }
                try {
                    el = document.createElement(/^(mp4|mov|m4v|webm|mkv|avi|wmv|flv|ts|3gp)$/.test(fileExt) ? 'video' : 'audio');
                    el.preload = 'metadata';
                    el.onloadedmetadata = function () { fin(el.duration); };
                    el.onerror = function () { fin(0); };
                    url = URL.createObjectURL(f);
                    el.src = url;
                    setTimeout(function () { fin(0); }, 4000);
                } catch (e) { fin(0); }
            });
        }

        /* Честный pre-flight ДО работы: называем факт (сколько здесь часов),
         * предел этого устройства и даём рабочий выход — наш же разделитель.
         * «Всё равно попробовать» оставлено сознательно (принцип: не запрещать,
         * а честно предупредить), но помечено риском — на этом пределе вкладка
         * не «медленная», а падает. */
        /* Потолок ТАРИФА (config/tiers.php → tools.<slug>.max_duration_s →
         * User::tierLimits → window.__tierLimits). 0/отсутствует = без
         * ограничения: так приходит гость на локали, где Премиум не продаётся
         * (ставить платную стену там, где не купить, запрещено — то же правило,
         * что у daily_runs и quality). Читаем ПО СЛАГУ: аудио и видео —
         * отдельные записи. */
        /* ⚡window.__tierLimits появляется ТОЛЬКО после /api/session-bootstrap —
         * синхронного снапшота в layout нет. Без ожидания гейт молча пропускал
         * бы любого, кто бросил файл в первые ~200 мс (поймано контрактом).
         * Ждём ограниченно; не дождались — тарифного потолка нет (fail-open:
         * лучше пропустить файл, чем ошибочно упереть в платную стену).
         * ⚡Событие прод шлёт на document (в layout: document.dispatchEvent) —
         * слушаем оба таргета, чтобы не зависеть от этой детали. */
        function tierSnapshotReady(timeoutMs) {
            if (window.__tierLimits) return Promise.resolve();
            return new Promise(function (resolve) {
                var done = false;
                var fin = function () { if (!done) { done = true; resolve(); } };
                try { document.addEventListener('tier-bootstrap:ready', fin, { once: true }); } catch (e) { /* noop */ }
                try { window.addEventListener('tier-bootstrap:ready', fin, { once: true }); } catch (e) { /* noop */ }
                setTimeout(fin, timeoutMs || 3000);
            });
        }

        function tierCeilingS() {
            try {
                var t = window.__tierLimits;
                if (!t || !t.tool_max_duration) return 0;
                return (t.tool_max_duration[slug] | 0) || 0;
            } catch (e) { return 0; }
        }
        /* Потолок ПРЕМИУМА — из конфига, отрендеренного в страницу (не хардкод),
         * чтобы показать не-Премиуму, что он получит. */
        function premiumCeilingS() { return (window.__pcPremiumMaxDurS | 0) || 0; }

        /* Set when the file exceeds the tariff ceiling: the run proceeds, the
           export asks for Premium. 0 = no tariff wall pending. */
        var tierWallDurS = 0;

        /* Up-front, non-blocking notice: «we will process it and show you what
           we found; downloading this length needs Premium». Shown once, right
           above the workspace, so the export wall is never a surprise. */
        function showTierNotice(durS, tier, prem) {
            var host = $('pc-workspace');
            if (!host || $('pc-tier-notice')) return;
            var box = document.createElement('div');
            box.id = 'pc-tier-notice';
            box.setAttribute('role', 'status');
            box.style.cssText = 'margin:0 0 12px;padding:12px 14px;border:1px solid var(--warning,#eab308);' +
                'border-radius:12px;background:var(--glass,rgba(255,255,255,.04));font-size:13px;line-height:1.5;color:var(--text-primary,#fff)';
            var msg = (i18n.tierlim_notice || 'This file is :d. We will recognise all of it and show every match — downloading a file this long needs Premium (up to :p).')
                .replace(':d', fmtCoarse(durS)).replace(':c', fmtCoarse(tier))
                .replace(':p', prem > 0 ? fmtCoarse(prem) : fmtCoarse(tier));
            box.textContent = '⏱️ ' + msg;
            host.parentNode.insertBefore(box, host);
        }

        /* The video the zone took but the browser cannot assemble back in its
           own container (copyWallRefusal) — said when the file is picked, not
           at the download after minutes of recognition. Same box as the tariff
           notice, above the workspace. */
        function showWallNotice(msg) {
            var host = $('pc-workspace');
            if (!host || $('pc-wall-notice')) return;
            var box = document.createElement('div');
            box.id = 'pc-wall-notice';
            box.setAttribute('role', 'status');
            box.style.cssText = 'margin:0 0 12px;padding:12px 14px;border:1px solid var(--warning,#eab308);' +
                'border-radius:12px;background:var(--glass,rgba(255,255,255,.04));font-size:13px;line-height:1.5;color:var(--text-primary,#fff)';
            box.textContent = '📦 ' + msg;
            host.parentNode.insertBefore(box, host);
        }

        async function maybeConfirmTooLong(durS, job) {
            await tierSnapshotReady(3000);
            if (job !== jobSeq) return false;
            var device = window.__pcMaxDurS || longFileCeilingS();
            var tier = tierCeilingS();
            var prem = premiumCeilingS();
            var effective = tier > 0 ? Math.min(device, tier) : device;
            if (!durS || durS <= effective) return true;          // проходит — работаем

            // Файл не проходит. Единственный честный вопрос: ПОМОЖЕТ ЛИ АПГРЕЙД
            // ИМЕННО С ЭТИМ файлом? Апгрейд поднимает только тарифный потолок —
            // устройство остаётся тем же. Поэтому предлагать Премиум можно лишь
            // когда файл влезает в min(устройство, Премиум). Иначе это продажа
            // заведомо нерабочего: 9-часовая аудиокнига не пойдёт и на Премиуме
            // (замерено: 6 ч убивают вкладку) — там честный ответ «разделите».
            var afterUpgrade = prem > 0 ? Math.min(device, prem) : device;
            if (tier > 0 && tier < afterUpgrade && durS <= afterUpgrade) {
                /* TARIFF ceiling — deliberately NOT a stop here (2026-08-08).
                 *
                 * Measured over 30 d: 4632 sessions, 1376 finished results, and
                 * ZERO visits to the pricing page after this wall — because it
                 * fired BEFORE the tool had shown it can do anything. A wall in
                 * front of an unproven tool only loses the user.
                 *
                 * So: recognise the whole file, show every hit found, and put
                 * the tariff wall on the DOWNLOAD (see exportFile). The user is
                 * told up front, plainly, that the export will need Premium —
                 * being surprised at the finish line would be a bait, and this
                 * warning is what keeps it honest.
                 *
                 * Side effect that fixes an old leak: by export time the tier
                 * snapshot has long arrived, so the 3 s fail-open in
                 * tierSnapshotReady() can no longer let a long file through by
                 * accident (measured: the wall caught only 15 of ~55 cases).
                 */
                tierWallDurS = durS;
                if (window.__ae) __ae('feature_use', {
                    tool: slug, feature: 'tier_duration_wall', src: 'notice',
                    dur_s: Math.round(durS), tier_s: tier, premium_s: prem
                });
                showTierNotice(durS, tier, prem);
                return true;   // let the work run — the wall waits at the export
            }
            // Сюда доходим только когда апгрейд НЕ спасает: это предел браузера.
            var ceil = effective;
            if (window.__ae) __ae('feature_use', {
                tool: slug, feature: 'too_long_guard', src: 'preflight',
                dur_s: Math.round(durS), ceil_s: ceil, dm: navigator.deviceMemory || 0
            });
            var splitUrl = '/' + (window.__LOCALE || 'en') + (isVideoFile ? '/video-trim' : '/chapter-splitter');
            var choice = await pcConfirm({
                icon: '📚',
                title: i18n.toolong_title || 'This file is too long for one pass',
                message: (i18n.toolong_body || 'It is :d long. This device can handle about :c in one pass — beyond that the browser runs out of memory. Split it into parts and clean each one.')
                    .replace(':d', fmtCoarse(durS)).replace(':c', fmtCoarse(ceil)),
                choices: [
                    { id: 'split', label: i18n.toolong_split || 'Split into parts', primary: true },
                    { id: 'anyway', label: i18n.toolong_anyway || 'Try anyway (may crash the tab)' },
                    { id: 'cancel', label: i18n.toolong_cancel || 'Choose another file' }
                ]
            });
            if (window.__ae) __ae('feature_use', {
                tool: slug, feature: 'too_long_guard', src: 'preflight_choice',
                action: choice, dur_s: Math.round(durS), ceil_s: ceil
            });
            if (choice === 'split') { location.href = splitUrl; return false; }
            return choice === 'anyway';
        }

        async function maybeConfirmLongJob(job) {
            var backend = (asr && asr.backend && asr.backend()) || 'wasm';
            var measured = loadRtfE2e();
            var estS, barS;
            if (measured > 0.02) {
                estS = duration / measured;
                barS = window.__pcLongJobPreS || 1800;
            } else {
                if (backend === 'webgpu') return true;
                estS = duration / (defaultRtf() || 0.3);
                barS = window.__pcLongJobPreSeedS || 3600;
            }
            if (estS <= barS) return true;
            longJobAsked = job;
            var t = fmtCoarse(estS);
            if (window.__ae) __ae('feature_use', {
                tool: slug, feature: 'long_job_confirm', src: 'preflight',
                est_s: Math.round(estS), measured: measured > 0.02 ? 1 : 0, backend: backend
            });
            var choice = await pcConfirm({
                icon: '⏳',
                title: i18n.long_job_title || 'This may take a while',
                message: (i18n.long_job_body || 'About :t on this device.').replace(':t', t),
                choices: [
                    { id: 'go', label: (i18n.long_job_start || 'Start — about :t').replace(':t', t), primary: true },
                    { id: 'cancel', label: i18n.long_job_cancel || 'Choose another file' }
                ]
            });
            if (window.__ae) __ae('feature_use', {
                tool: slug, feature: 'long_job_confirm', src: 'preflight_choice',
                action: choice === 'go' ? 'go' : 'cancel', est_s: Math.round(estS)
            });
            return choice === 'go';
        }

        /* ---------------- ASR ---------------- */
        var lastAsrMsgAt = 0;        // liveness: written by ALL the adapter's worker-message hooks
        var asrRealProgressAt = 0;   // last REAL forward motion (mel window done OR decode advanced)
        var asrDecodeProgressed = false; // first real DECODE progress lifts the cold-start grace

        function runAsr(pcmI16, job) {
            // Страховка deferSession: если сигнал после извлечения не дошёл
            // (нестандартный путь — смена языка без нового файла), сессия
            // обязана развернуться сейчас, иначе init повиснет навсегда.
            if (asr && asr.go) asr.go();
            // Liveness watchdog, engine-agnostic. If the worker DIES
            // mid-transcribe (OS OOM-kill on a low-RAM phone) no result and no
            // error ever arrive — runAsr would hang forever and the predictive
            // bar would creep on an empty promise. Two-tier budget:
            //   • LIVENESS (any worker message): adaptive, 6× the expected
            //     per-window time clamped to [90 s, 480 s] — but never below
            //     COLD_START_MS until the FIRST real decode progress, because
            //     until then the rtf is a first-visit guess (desktop seed 4.0
            //     → 90 s floor), and one real window on a weak device takes
            //     longer than that. Prod 2026-07-16 (comment #852): a healthy
            //     56-min run was killed at 90 s three times in two browsers.
            //   • REAL PROGRESS: if the decode position hasn't advanced in
            //     30 min while the worker still chatters (degenerate loop that
            //     the no_repeat_ngram guard didn't break), give up honestly.
            lastAsrMsgAt = Date.now();
            asrRealProgressAt = Date.now();
            asrDecodeProgressed = false;
            var watchdog = null;
            var settled = false;
            var COLD_START_MS = 300000;
            var REAL_PROGRESS_CEILING_MS = 1800000;

            var f32 = new Float32Array(pcmI16.length);
            for (var i = 0; i < pcmI16.length; i++) f32[i] = pcmI16[i] / 32768;

            var run = asr.transcribe(f32, job, { debugHang: !!window.__pcDebugHang });

            var guard = new Promise(function (_resolve, reject) {
                function fail(kind, sinceMs) {
                    clearInterval(watchdog);
                    try { asr.abort(); } catch (e) { /* noop */ }
                    try {
                        if (window.__aeCritical) window.__aeCritical('tool_stalled', {
                            slug: slug, stage: 'asr', kind: kind, engine: asr.engine, lang: asrLang,
                            no_msg_ms: Date.now() - lastAsrMsgAt,
                            no_real_ms: Date.now() - asrRealProgressAt,
                            dec: asrDecodeProgressed ? 1 : 0,
                            rtf: Math.round((prog.rtf || 0) * 100) / 100, audioS: Math.round(prog.totalS || 0)
                        });
                    } catch (e) { /* noop */ }
                    // dec= is the DISCRIMINATOR the 2026-07-23 watch could not
                    // settle from telemetry: below COLD_START_MS the budget is
                    // only legitimate once real decode progress has been seen,
                    // and nothing in the stored payload said whether it had.
                    // The suffix rides the message because that is what reaches
                    // analytics_events (tool_stalled goes to Redis counters, not
                    // the events table) — errorToastFor() maps pcCode 'stalled'
                    // to a localized string, so users never see this text.
                    var se = new Error('asr stalled — no worker ' + kind + ' for ' + Math.round(sinceMs / 1000) + 's'
                        + ' (dec=' + (asrDecodeProgressed ? 1 : 0)
                        + ' audio=' + Math.round(prog.totalS || 0) + 's'
                        + ' rtf=' + (Math.round((prog.rtf || 0) * 100) / 100) + ')');
                    se.pcCode = 'stalled';
                    reject(se);
                }
                watchdog = setInterval(function () {
                    if (settled) { clearInterval(watchdog); return; }
                    if (job !== jobSeq) { clearInterval(watchdog); return; }  // superseded — stop quietly
                    var rtf = prog.rtf || observedRtf || 0.3;
                    var windowMs = (24 / Math.max(rtf, 0.05)) * 1000;
                    // __pcStallMs is a test-only override (inert in prod, undefined).
                    var stallMs = window.__pcStallMs || Math.max(90000, Math.min(480000, 6 * windowMs));
                    if (!window.__pcStallMs && !asrDecodeProgressed) stallMs = Math.max(stallMs, COLD_START_MS);
                    var quietMs = Date.now() - lastAsrMsgAt;
                    if (quietMs > stallMs) { fail('liveness', quietMs); return; }
                    var stuckMs = Date.now() - asrRealProgressAt;
                    if (stuckMs > (window.__pcRealCeilMs || REAL_PROGRESS_CEILING_MS)) fail('progress', stuckMs);
                }, 5000);
            });

            return Promise.race([run, guard]).then(function (r) {
                settled = true; clearInterval(watchdog);
                return r;
            }, function (e) {
                settled = true; clearInterval(watchdog);
                throw e;
            });
        }

        /* ---------------- language ----------------
         * `settings.lang` = the language the USER picked; the ASR always runs in
         * it. After recognition we look at the transcript: if it reads like a
         * DIFFERENT supported language, offer a one-click re-run. This catches
         * the real mistake ("I forgot to switch the language") without ever
         * pretending we can auto-detect (we measured that we cannot). */
        function applyDetectedLang(detected) {
            activeLang = settings.lang;
            renderLangState();
        }

        // What the transcript looks like, when that is knowable.
        // MEASURED CAVEAT: Whisper does not merely mis-transcribe a clip whose
        // language was set wrong — it TRANSLATES it into the chosen language
        // ("Este puto trabajo…" recognized as en → "This job is completely
        // art."). So a text-based check can only ever catch the near-miss cases
        // (pt audio read as es, etc.). The reliable signal that the language is
        // wrong is a transcript with NO profanity in it, which is why the hint
        // below leads with that.
        function transcriptLooksLike() {
            try {
                var text = words.map(function (w) { return w.text; }).join(' ');
                var g = window.AsrAdapter.guessLangFromText(text);
                return (g && g !== activeLang) ? g : null;
            } catch (e) { return null; }
        }

        // Language chosen AFTER the file was processed: re-run ASR on the PCM we
        // already extracted (no re-upload, no re-decode). Russian↔other also
        // swaps the engine — the adapter handles that.
        async function changeLanguage(lang) {
            settings.lang = lang;
            saveSetting('lang', lang);
            if (window.__ae) __ae('feature_use', { tool: slug, feature: 'lang_change', lang: lang, had_audio: pcm ? 1 : 0 });
            if (!pcm) { activeLang = lang; renderLangState(null); return; }

            var job = ++jobSeq;
            setPhase('asr');
            prog.jobStart = performance.now();
            startTicker();
            setStage(i18n.stage_model, 1);
            try {
                await ensureWorker();               // rebuilds the engine for the new language
                if (job !== jobSeq) return;
                // Same honest pre-flight as processFile: a language change
                // re-runs the FULL recognition (engine may differ → new
                // engine+backend estimate). The current transcript is only
                // cleared AFTER the user commits — cancel keeps the result.
                if (!await maybeConfirmLongJob(job)) {
                    if (job !== jobSeq) return;
                    phase = words.length ? 'ready' : 'idle';
                    setStage('', -1);
                    return;
                }
                if (job !== jobSeq) return;
                words = []; hits = []; overrides = {};
                // Word-indexed decisions (re-bounded censors, undo snapshots)
                // point at the OLD transcript — carrying them over would move
                // an edit onto whatever word now has that index. Drawn
                // fragments are time-based and stay.
                edits = {}; selectedId = null;
                invalidateAuto();
                resetHistory();
                var tEl = $('pc-transcript'); if (tEl) tEl.innerHTML = '';
                $('pc-results').style.display = 'none';
                syncWide();
                progReal('asr', 0, { rtf: observedRtf, totalS: duration });
                setStage(i18n.stage_asr, 0);
                var relangT0 = Date.now();
                var res = await runAsr(pcm, job);
                if (job !== jobSeq) return;
                saveSetting(rtfKey(), String(Math.round(duration / Math.max(0.001, (Date.now() - relangT0) / 1000) * 100) / 100));
                applyDetectedLang(res.detectedLang);
                setPhase('ready');
                setStage('', -1);
                $('pc-results').style.display = 'block';
                syncWide();
                renderTranscript(res.words || []);
                var ns = $('pc-no-speech'); if (ns) ns.style.display = words.length ? 'none' : '';
                rescan();
                sessionKey = sessionKeyFor(file);     // the key carries the language
                saveSessionNow();
                if (window.__ae) __ae('feature_use', {
                    tool: slug, feature: 'relang_done', lang: activeLang || 'unknown',
                    engine: asr.engine, found: hits.length, words: words.length
                });
            } catch (err) {
                if (job !== jobSeq) return;
                setStage('', -1);
                setPhase('idle');
                if (String(err && err.message) !== 'aborted') {
                    reportAndToast(err, slug + ':relang');
                }
            }
        }

        // Honest surface, in priority order:
        //   1. transcript reads like ANOTHER supported language → offer a
        //      one-click re-run in it (only fires on near-miss pairs, see
        //      transcriptLooksLike);
        //   2. speech was recognized but NOTHING was flagged → the most common
        //      cause is the wrong audio language, so say so plainly;
        //   3. otherwise stay quiet.
        function renderLangState() {
            var sel = $('pc-lang');
            if (sel && sel.value !== settings.lang) sel.value = settings.lang;
            // The accuracy caveat belongs to the Whisper engine (per-language
            // recall varies); the Russian GigaAM path has its own measured
            // quality and doesn't need it.
            var acc = $('pc-accuracy-note');
            if (acc) acc.style.display = (activeLang && activeLang !== 'ru') ? '' : 'none';

            var note = $('pc-lang-note');
            var btn = $('pc-lang-switch');
            if (!note || !btn) return;

            var mismatch = (phase === 'ready') ? transcriptLooksLike() : null;
            if (mismatch) {
                note.textContent = (i18n.lang_mismatch || 'This sounds like :lang.').replace(':lang', langName(mismatch));
                note.style.display = '';
                btn.textContent = (i18n.lang_switch_to || 'Switch to :lang').replace(':lang', langName(mismatch));
                btn.style.display = '';
                btn.onclick = function () { changeLanguage(mismatch); };
                if (window.__ae) __ae('feature_use', { tool: slug, feature: 'lang_mismatch_hint', ran: activeLang, looks: mismatch });
                return;
            }
            btn.style.display = 'none';
            btn.onclick = null;
            if (phase === 'ready' && words.length > 0 && hits.length === 0) {
                note.textContent = i18n.lang_no_hits || 'Nothing was flagged — check that the audio language is right.';
                note.style.display = '';
            } else {
                note.style.display = 'none';
            }
        }
        function langName(code) {
            var sel = $('pc-lang');
            if (sel) {
                for (var i = 0; i < sel.options.length; i++) if (sel.options[i].value === code) return sel.options[i].textContent;
            }
            return code;
        }

        /* ---------------- detection + regions ---------------- */
        /* Счётчик под списком: «принято N · найдено M». Отвечает ровно на тот
         * вопрос, из-за которого пришла жалоба #1243 — «список вообще
         * прочитан?». Раньше «список не распознан» и «этих слов нет в записи»
         * выглядели одинаково: не менялось НИЧЕГО. */
        function renderListCounts() {
            [['pc-blacklist-count', settings.blacklist, true],
             ['pc-whitelist-count', settings.whitelist, false]].forEach(function (row) {
                var el = $(row[0]);
                if (!el) return;
                var n = splitUserList(row[1]).length;
                if (!n) { el.textContent = ''; return; }
                var txt = (i18n.list_count || ':n words').replace(':n', n);
                if (row[2] && words.length) {
                    var m = 0;
                    for (var i = 0; i < hits.length; i++) if (hits[i].kind === 'custom') m++;
                    txt += ' · ' + (i18n.list_found || 'found :m').replace(':m', m);
                }
                el.textContent = txt;
            });
        }

        function rescan() {
            detector = makeDetector();
            hits = detector.scan(words);
            invalidateAuto();
            renderListCounts();
            refreshAll();
            // the "nothing flagged → wrong language?" hint depends on `hits`,
            // which the tier checkboxes and the custom word lists also change
            renderLangState();
        }
        function refreshAll() {
            scheduleSessionSave();
            buildRegions();
            renderTranscriptMarks();
            renderHitList();
            renderStats();
            renderOverlay();
            renderInspector();
            updateExportEnabled();
        }
        /* The cheap half, for every pointermove of a drag: the list and the
         * transcript are rebuilt once, when the gesture ends (a dense file
         * has a thousand rows — rebuilding them per mouse move stalls). */
        function refreshRegionsOnly() {
            buildRegions();
            renderStats();
            renderOverlay();
            renderInspector();
        }

        function activeHitIndices() {
            var set = {};
            hits.forEach(function (h) { set[h.index] = true; });
            Object.keys(overrides).forEach(function (k) {
                if (overrides[k]) set[k] = true; else delete set[k];
            });
            return Object.keys(set).map(Number).sort(function (a, b) { return a - b; });
        }

        function regionCtx() {
            return { words: words, audio: pcmAudio, detector: detector, duration: duration, noGlue: !!window.__pcNoGlue };
        }
        // The computed censor of word i at the current coverage. Cached: the
        // edge walks read PCM, and a drag rebuilds the items on every move.
        function autoRegion(i) {
            var c = autoCache[i];
            if (!c) c = autoCache[i] = PR.hitRegion(regionCtx(), i, settings.coverage);
            return c;
        }
        function invalidateAuto() { autoCache = {}; }

        function buildItems() {
            var byIndex = {};
            hits.forEach(function (h) { byIndex[h.index] = h; });
            var out = [];
            activeHitIndices().forEach(function (i) {
                var w = words[i];
                if (!w) return;
                var a = autoRegion(i), e = edits[i];
                out.push({
                    id: 'w' + i, kind: 'auto', wi: i,
                    start: e ? e.start : a.start, end: e ? e.end : a.end,
                    label: a.label || w.text, edited: !!e,
                    hitKind: (byIndex[i] && byIndex[i].kind) || 'word-manual'
                });
            });
            manualRegions.forEach(function (r) {
                out.push({
                    id: 'm' + r.id, kind: 'manual', mid: r.id, start: r.start, end: r.end,
                    label: (i18n.region_label || 'Region') + ' ' + fmtTime(r.start) + '–' + fmtTime(r.end),
                    edited: false, hitKind: 'manual'
                });
            });
            out.sort(function (a, b) { return a.start - b.start || a.end - b.end; });
            items = out;
            if (selectedId && !itemById(selectedId)) selectedId = null;
        }
        function itemById(id) {
            for (var i = 0; i < items.length; i++) if (items[i].id === id) return items[i];
            return null;
        }

        function buildRegions() {
            buildItems();
            var L = PR.layout(items, { cap: MAX_REGIONS, snap: pcmAudio ? pcmAudio.snapZero : null });
            // The cap widening the bridge swallows speech between censors —
            // exactly the defect it once hid (vlad.dok 06.08: 275 hits →
            // beeped sentences). If it ever engages, say so in telemetry.
            if (L.widened && !capMergeReported) {
                capMergeReported = true;
                if (window.__ae) __ae('feature_use', {
                    tool: slug, feature: 'region_cap_merge',
                    hits: items.length, regions: L.regions.length,
                    gapS: Math.round(L.bridge * 100) / 100, mode: settings.mode
                });
            }
            regions = L.regions;
            levelRegions();
            voiceLaneUpdate();
        }

        /* ---------------- «глушить только голос» ----------------
         * Что звучит ПОД заглушкой, решает внешний лейн (vocal-only-export.js:
         * разделитель и подложка); ядро владеет графом, предпросмотром и
         * экспортом. Лейн регистрируется сам — без него флажок не показывается.
         * Со своим звуком не сочетается: там граф другой (atrim/concat). */
        var voiceLane = null;
        function voiceOnlyActive() {
            return !!(settings.voiceOnly && voiceLane && settings.mode !== 'custom');
        }
        function voiceLaneUpdate() {
            if (!voiceLane) return;
            try {
                voiceLane.update({
                    active: voiceOnlyActive() && !!file && phase !== 'extract' && phase !== 'asr',
                    file: file, sampleRate: srcRate, channels: channels === 1 ? 1 : 2,
                    duration: duration, regions: regions
                });
            } catch (e) { /* лейн не смеет ломать редактор */ }
        }

        /* ---------------- history (undo / redo) ----------------
         * Snapshots of what the USER decided — switched words, drawn and
         * edited censors. Settings (coverage, sound, tiers) are not history:
         * they are re-applied, not undone. Small (a few KB even at a
         * thousand censors), so a snapshot per action is simpler and safer
         * than inverse operations. */
        var HISTORY_MAX = 300;
        var undoStack = [], redoStack = [], undoCount = 0, lastNudge = null;
        var editStats = { resize: 0, move: 0, del: 0, reset: 0, nudge: 0 };
        function snapState() {
            return JSON.stringify({ o: overrides, m: manualRegions, e: edits, s: manualSeq });
        }
        function pushHistory(before) {
            undoStack.push(before === undefined ? snapState() : before);
            if (undoStack.length > HISTORY_MAX) undoStack.shift();
            redoStack.length = 0;
            lastNudge = null;
            syncUndoUI();
        }
        function restoreState(s) {
            var o = JSON.parse(s);
            overrides = o.o || {}; manualRegions = o.m || []; edits = o.e || {}; manualSeq = o.s || 1;
            refreshAll();
        }
        function undo() {
            if (!undoStack.length) return;
            redoStack.push(snapState());
            restoreState(undoStack.pop());
            undoCount++; lastNudge = null;
            syncUndoUI();
            if (window.__ae) __ae('feature_use', { tool: slug, feature: 'region_undo', dir: 'undo', depth: undoStack.length });
        }
        function redo() {
            if (!redoStack.length) return;
            undoStack.push(snapState());
            restoreState(redoStack.pop());
            lastNudge = null;
            syncUndoUI();
            if (window.__ae) __ae('feature_use', { tool: slug, feature: 'region_undo', dir: 'redo', depth: redoStack.length });
        }
        function resetHistory() { undoStack = []; redoStack = []; undoCount = 0; lastNudge = null; editStats = { resize: 0, move: 0, del: 0, reset: 0, nudge: 0 }; syncUndoUI(); }
        function syncUndoUI() {
            var u = $('pc-undo'), r = $('pc-redo');
            if (u) u.disabled = !undoStack.length;
            if (r) r.disabled = !redoStack.length;
        }
        function reportEdit(op, item, via) {
            if (editStats[op] !== undefined) editStats[op]++;
            if (window.__ae) __ae('feature_use', { tool: slug, feature: 'region_edit', op: op, kind: item ? item.kind : '', via: via || 'wave' });
        }

        /* ---------------- editing a censor ---------------- */
        function setItemBounds(it, start, end) {
            start = Math.max(0, Math.min(start, duration));
            end = Math.max(0, Math.min(end, duration));
            if (end - start < MIN_ITEM_S) return false;
            // pv:2 — placed against the measured preview (see PREVIEW_VERSION)
            if (it.kind === 'auto') edits[it.wi] = { start: start, end: end, pv: PREVIEW_VERSION };
            else {
                for (var i = 0; i < manualRegions.length; i++) {
                    if (manualRegions[i].id === it.mid) {
                        manualRegions[i].start = start; manualRegions[i].end = end; manualRegions[i].pv = PREVIEW_VERSION;
                        break;
                    }
                }
            }
            return true;
        }
        var MIN_ITEM_S = 0.02;
        /* Which preview a censor was placed against. A hand edit is placed BY
         * EAR, so it is only as right as the preview it was heard in: before
         * 09.10.2026 that preview silenced (outputLatency + 29 ms − lag) ahead
         * of the sound (see previewLookahead). Edits saved without this stamp
         * were heard that way — maybeOfferLegacyShift() can put them where
         * they were heard, on this device, measured. */
        var PREVIEW_VERSION = 2;
        function deleteItem(id, via) {
            var it = itemById(id);
            if (!it) return;
            // the selection moves to the next censor, so «Delete, Delete…»
            // walks through a run of false hits without reaching for the mouse
            var idx = items.indexOf(it), nxt = items[idx + 1] || items[idx - 1] || null;
            pushHistory();
            if (it.kind === 'auto') {
                if (it.hitKind !== 'word-manual' && words[it.wi]) lexSignal('fp', 'remove', words[it.wi].text);
                setWordActive(it.wi, false);
            } else manualRegions = manualRegions.filter(function (r) { return r.id !== it.mid; });
            selectedId = nxt ? nxt.id : null;
            refreshAll();
            reportEdit('del', it, via);
        }
        function resetItem(id, via) {
            var it = itemById(id);
            if (!it || it.kind !== 'auto' || !edits[it.wi]) return;
            pushHistory();
            delete edits[it.wi];
            refreshAll();
            reportEdit('reset', it, via);
        }
        /* Nudge one edge by `dt` s. A run of nudges on the same censor is ONE
         * undo step: ten clicks of «+20 ms» are one decision, not ten. */
        function nudgeItem(id, edge, dt) {
            var it = itemById(id);
            if (!it) return;
            var now = Date.now();
            if (!(lastNudge && lastNudge.id === id && now - lastNudge.at < 1500)) {
                pushHistory();
                reportEdit('nudge', it, 'inspector');
            }
            var s = it.start, e = it.end;
            if (edge === 'start') s = Math.max(0, Math.min(e - MIN_ITEM_S, s + dt));
            else e = Math.min(duration, Math.max(s + MIN_ITEM_S, e + dt));
            if (setItemBounds(it, s, e)) refreshAll();
            lastNudge = { id: id, at: now };
            syncUndoUI();
        }
        function setWordActive(i, on) {
            var isHit = hits.some(function (h) { return h.index === i; });
            if (on === isHit) delete overrides[i]; else overrides[i] = on;
        }

        /* ---------------- transcript UI ---------------- */
        // GigaAM streams `words_partial`, so `words` fills in as it decodes.
        // Whisper only returns the full list at the end — this re-seats the
        // transcript from a final word list (also used when the language is
        // changed and the file is re-transcribed).
        function renderTranscript(finalWords) {
            var box = $('pc-transcript');
            if (finalWords) words = finalWords;
            if (!box) return;
            box.innerHTML = '';
            var frag = document.createDocumentFragment();
            words.forEach(function (w, i) {
                var span = document.createElement('span');
                span.className = 'pc-word';
                span.textContent = w.text;
                span.dataset.i = String(i);
                frag.appendChild(span);
                frag.appendChild(document.createTextNode(' '));
            });
            box.appendChild(frag);
        }

        /* Show what we found WHILE we are still recognising.
         *
         * Measured 2026-08-08 on an 8-min file: the transcript starts flowing
         * at 51 s, but the "found N" counter, the hit list and the red marks on
         * the waveform stayed EMPTY for the whole run — the tool only proved
         * its worth at the very end. That is the tool's biggest loss class:
         * 1878 sessions in 30 days left DURING processing, after ~5.7 min of
         * watching a progress bar (60x more than every paywall combined).
         *
         * So scan each streamed batch as it lands and refresh the counters.
         * Rendering is throttled — on a dense hour-long file the hit list and
         * the overlay would otherwise be rebuilt dozens of times a second.
         * The final rescan() still re-scans everything, so this can only make
         * the result appear EARLIER, never differently.
         */
        var STREAM_REFRESH_MS = 1200;
        var lastStreamPaint = 0, streamPaintTimer = null;
        function scanStreamedWords(startIdx) {
            if (!detector) {
                try { detector = makeDetector(); } catch (e) { return; }
            }
            var fresh = words.slice(startIdx);
            if (!fresh.length) return;
            var found = detector.scan(fresh);
            if (!found.length) return;
            for (var i = 0; i < found.length; i++) {
                found[i].index += startIdx;          // scan() indexes its own slice
                hits.push(found[i]);
            }
            var now = Date.now();
            var due = STREAM_REFRESH_MS - (now - lastStreamPaint);
            if (due <= 0) { lastStreamPaint = now; refreshAll(); return; }
            if (!streamPaintTimer) {
                streamPaintTimer = setTimeout(function () {
                    streamPaintTimer = null;
                    lastStreamPaint = Date.now();
                    if (phase === 'asr') refreshAll();
                }, due);
            }
        }

        function appendTranscript(newWords) {
            var box = $('pc-transcript');
            if (!box) return;
            var startIdx = words.length;
            words = words.concat(newWords);
            var frag = document.createDocumentFragment();
            newWords.forEach(function (w, j) {
                var span = document.createElement('span');
                span.className = 'pc-word';
                span.textContent = w.text;
                span.dataset.i = String(startIdx + j);
                frag.appendChild(span);
                frag.appendChild(document.createTextNode(' '));
            });
            box.appendChild(frag);
            scanStreamedWords(startIdx);   // find + show hits as they stream in
        }

        function renderTranscriptMarks() {
            var box = $('pc-transcript');
            if (!box) return;
            var active = {};
            activeHitIndices().forEach(function (i) { active[i] = true; });
            var byIndex = {};
            hits.forEach(function (h) { byIndex[h.index] = h; });
            var spans = box.querySelectorAll('.pc-word');
            for (var i = 0; i < spans.length; i++) {
                var idx = Number(spans[i].dataset.i);
                var h = byIndex[idx];
                spans[i].classList.toggle('hit', !!active[idx]);
                spans[i].classList.toggle('hit-fuzzy', !!(active[idx] && h && h.kind === 'fuzzy'));
                spans[i].classList.toggle('hit-manual', !!(active[idx] && !h));
                spans[i].title = active[idx] ? (i18n.click_unmute || '') : (i18n.click_mute || '');
            }
        }

        /* The list shows the CENSORS (what will be muted, with the bounds the
         * export uses), not the raw ASR word times — otherwise an edited or
         * glued censor read one time on the wave and another in the list. */
        function renderHitList() {
            var list = $('pc-hits');
            if (!list) return;
            list.innerHTML = '';
            listRowFor = {};
            listCurrentId = null;
            items.forEach(function (it) {
                var div = document.createElement('div');
                div.className = 'pc-hit-row' + (it.hitKind === 'fuzzy' ? ' fuzzy' : '') + (it.kind === 'manual' ? ' manual' : '') +
                    (it.edited ? ' edited' : '') + (it.id === selectedId ? ' selected' : '');
                div.dataset.id = it.id;
                var play = document.createElement('button');
                play.className = 'pc-hit-play';
                play.type = 'button';
                play.setAttribute('aria-label', i18n.play_from_here || 'play');
                play.innerHTML = '&#9658;';
                play.onclick = function () { seekTo(Math.max(0, it.start - 1)); };
                var ear = document.createElement('button');
                ear.className = 'pc-hit-ear';
                ear.type = 'button';
                ear.setAttribute('aria-label', i18n.audition_original || 'hear original');
                ear.title = i18n.audition_original || '';
                ear.textContent = '👂';
                ear.onclick = function () { auditionHit(it.start, it.end); };
                var tc = document.createElement('span');
                tc.className = 'pc-hit-time';
                tc.textContent = fmtTime(it.start);
                // Слово в списке — КНОПКА: выбрать эту заглушку и показать её
                // на дорожке (увеличить и подвести). До 22.09.2026 это был
                // мёртвый текст, и по нему кликали (rage-клик, Ангелина 21.09).
                var label = document.createElement('button');
                label.type = 'button';
                label.className = 'pc-hit-word';
                label.textContent = it.label;
                label.title = i18n.locate_on_wave || '';
                label.onclick = function () {
                    selectItem(it.id, { locate: true, seek: true });
                    if (window.__ae) __ae('feature_use', { tool: slug, feature: 'hit_locate', kind: it.kind === 'manual' ? 'manual' : it.hitKind });
                };
                if (it.edited) {
                    var ed = document.createElement('span');
                    ed.className = 'pc-hit-edited';
                    ed.title = i18n.sel_edited || '';
                    ed.textContent = '✎';
                    label.appendChild(ed);
                }
                var del = document.createElement('button');
                del.className = 'pc-hit-del';
                del.type = 'button';
                del.setAttribute('aria-label', i18n.sel_delete || i18n.click_unmute || 'unmute');
                del.innerHTML = '&times;';
                del.onclick = function () { deleteItem(it.id, 'list'); };
                div.appendChild(play); div.appendChild(ear); div.appendChild(tc); div.appendChild(label); div.appendChild(del);
                list.appendChild(div);
                listRowFor[it.id] = div;
            });
            var empty = $('pc-hits-empty');
            if (empty) empty.style.display = items.length ? 'none' : '';
            followList(true);
        }

        /* The list follows the playhead (Ангелина 23.09: «список не показывает
         * автоматически, на каком моменте видео я сейчас нахожусь»). The row of
         * the censor under — or next after — the playhead is marked, and the
         * LIST scrolls itself (its own scrollTop; the page never moves). */
        var listRowFor = {}, listCurrentId = null;
        function itemIndexAt(t) {
            // first item whose end is after t (the one playing, or the next one)
            var lo = 0, hi = items.length;
            while (lo < hi) { var mid = (lo + hi) >> 1; if (items[mid].end < t) lo = mid + 1; else hi = mid; }
            return lo < items.length ? lo : -1;
        }
        // index of the word under t (or the nearest one before it)
        function wordIndexAt(t) {
            var lo = 0, hi = words.length - 1, ans = 0;
            while (lo <= hi) {
                var mid = (lo + hi) >> 1;
                if (words[mid].start <= t) { ans = mid; lo = mid + 1; } else hi = mid - 1;
            }
            return ans;
        }
        function followList(force) {
            if (!mediaEl || !items.length) return;
            var k = itemIndexAt(mediaEl.currentTime);
            var id = k >= 0 ? items[k].id : null;
            if (id === listCurrentId && !force) return;
            if (listCurrentId && listRowFor[listCurrentId]) listRowFor[listCurrentId].classList.remove('current');
            listCurrentId = id;
            var row = id ? listRowFor[id] : null;
            if (!row) return;
            row.classList.add('current');
            keepRowVisible(row);
        }
        function keepRowVisible(row) {
            var list = $('pc-hits');
            if (!list || list.scrollHeight <= list.clientHeight) return;
            var lr = list.getBoundingClientRect(), rr = row.getBoundingClientRect();
            var top = rr.top - lr.top + list.scrollTop, bottom = top + rr.height;
            if (top < list.scrollTop) list.scrollTop = Math.max(0, top - 4);
            else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight + 4;
        }

        function renderStats() {
            var idxs = activeHitIndices();
            var active = {};
            idxs.forEach(function (i) { active[i] = true; });
            var n = $('pc-stat-found'); if (n) n.textContent = String(idxs.length + manualRegions.length);
            var fz = hits.filter(function (h) { return h.kind === 'fuzzy' && active[h.index]; }).length;
            var f = $('pc-stat-fuzzy'); if (f) f.textContent = String(fz);
            // shown only when there is something to check: a standing «0» read as a broken feature
            var fc = $('pc-stat-fuzzy-chip'); if (fc) fc.hidden = fz === 0;
            var censoredS = regions.reduce(function (a, r) { return a + (r.end - r.start); }, 0);
            var c = $('pc-stat-censored'); if (c) c.textContent = censoredS.toFixed(1) + i18n.sec_suffix;
        }

        function toggleWord(i) {
            var isHit = hits.some(function (h) { return h.index === i; });
            var cur = overrides.hasOwnProperty(i) ? overrides[i] : isHit;
            pushHistory();
            setWordActive(i, !cur);
            if (words[i]) {
                if (!cur && !isHit) lexSignal('gap', 'word', words[i].text);
                else if (cur && isHit) lexSignal('fp', 'word', words[i].text);
            }
            refreshAll(); // manual marks must enable export even with 0 auto-hits
        }

        function addManualRegion(start, end, opts) {
            // both ends inside the file: a censor past its end got start > end
            start = Math.max(0, Math.min(duration, start));
            end = Math.max(0, Math.min(duration, end));
            if (end - start < 0.05 || !duration) return null;
            var snap = pcmAudio ? pcmAudio.snapZero : function (t) { return t; };
            pushHistory();
            var r = { id: manualSeq++, start: snap(Math.max(0, start)), end: snap(Math.min(duration, end)), pv: PREVIEW_VERSION };
            manualRegions.push(r);
            if (window.__ae) __ae('feature_use', { tool: slug, feature: 'manual_region', n: manualRegions.length });
            if (opts && opts.select) selectedId = 'm' + r.id;
            refreshAll();
            return 'm' + r.id;
        }

        /* ---------------- selected censor ----------------
         * One censor is «in hand»: its edges can be dragged on the wave, the
         * bar under the wave shows it with nudge / listen / reset / remove, and
         * ‹ › walk to the neighbours — the whole review happens next to the
         * picture, without scrolling down to the list and back. */
        function selectItem(id, opts) {
            opts = opts || {};
            selectedId = id && itemById(id) ? id : null;
            var it = selectedId ? itemById(selectedId) : null;
            if (it && opts.locate) locateOnWave(it.start, it.end);
            if (it && opts.seek) seekTo(Math.max(0, it.start - 0.3), { keepPaused: true });
            renderOverlay();
            renderInspector();
            Object.keys(listRowFor).forEach(function (k) { listRowFor[k].classList.toggle('selected', k === selectedId); });
            if (it && listRowFor[it.id]) keepRowVisible(listRowFor[it.id]);
        }
        function stepSelection(dir) {
            if (!items.length) return;
            var cur = selectedId ? itemById(selectedId) : null, k;
            if (cur) k = items.indexOf(cur) + dir;
            else {
                var t = mediaEl ? mediaEl.currentTime : 0;
                k = itemIndexAt(t);
                if (k < 0) k = items.length - 1;
                else if (dir < 0) k = Math.max(0, items[k].start > t ? k - 1 : k);
            }
            k = Math.max(0, Math.min(items.length - 1, k));
            selectItem(items[k].id, { locate: true, seek: true });
            if (window.__ae) __ae('feature_use', { tool: slug, feature: 'region_step', dir: dir });
        }
        function renderInspector() {
            var bar = $('pc-sel');
            if (!bar) return;
            bar.hidden = !items.length;
            if (!items.length) return;
            var it = selectedId ? itemById(selectedId) : null;
            bar.classList.toggle('has-sel', !!it);
            var word = $('pc-sel-word'), time = $('pc-sel-time'), badge = $('pc-sel-badge'), reset = $('pc-sel-reset');
            if (word) word.textContent = it ? it.label : (i18n.sel_empty || '');
            if (time) time.textContent = it ? fmtTimecode(it.start) + ' – ' + fmtTimecode(it.end) + ' · ' + Math.round((it.end - it.start) * 1000) + ' ms' : '';
            if (badge) badge.hidden = !(it && it.edited);
            if (reset) reset.hidden = !(it && it.kind === 'auto' && it.edited);
            bar.querySelectorAll('[data-needs-sel]').forEach(function (b) { b.disabled = !it; });
            var pos = $('pc-sel-pos');
            if (pos) pos.textContent = it ? (items.indexOf(it) + 1) + ' / ' + items.length : String(items.length);
        }

        /* ---------------- waveform ---------------- */
        function buildPeaks() {
            if (!pcm || !pcm.length) { masterPeaks = null; return; }
            var count = Math.ceil(pcm.length / PEAK_BUCKET);
            var mins = new Float32Array(count), maxs = new Float32Array(count);
            for (var i = 0; i < count; i++) {
                var lo = 32767, hi = -32768;
                var s = i * PEAK_BUCKET, e = Math.min(s + PEAK_BUCKET, pcm.length);
                for (var j = s; j < e; j++) {
                    var v = pcm[j];
                    if (v < lo) lo = v;
                    if (v > hi) hi = v;
                }
                mins[i] = lo / 32768; maxs[i] = hi / 32768;
            }
            masterPeaks = { mins: mins, maxs: maxs, count: count };
            peaksCache = null;
            ovPeaks = null;          // the overview is of THIS file, at any width
            // the same PCM, as the edge walks see it (noise floor, zero crossings)
            pcmAudio = PR.audio(pcm);
            invalidateAuto();
        }
        // Пики считаются ДЛЯ ВИДИМОГО ОКНА [f0..f1] (доли файла), а не для
        // всей ленты: в увеличении лента шире окна в десятки раз, и полотно
        // на всю её длину не влезло бы ни в текстуру, ни в память.
        function computePeaks(width, f0, f1) {
            var pk = peaksOf(width, f0, f1);
            peaksCache = { mins: pk.mins, maxs: pk.maxs, width: width, f0: f0, f1: f1 };
        }
        function peaksOf(width, f0, f1) {
            var mp = masterPeaks;
            var mins = new Float32Array(width), maxs = new Float32Array(width);
            var from = Math.max(0, Math.min(mp.count, Math.floor(f0 * mp.count)));
            var to = Math.max(from + 1, Math.min(mp.count, Math.ceil(f1 * mp.count)));
            var step = (to - from) / width;
            for (var i = 0; i < width; i++) {
                var lo = 1, hi = -1;
                var s = from + Math.floor(i * step), e = from + Math.min(Math.ceil((i + 1) * step), to - from);
                if (e <= s) e = s + 1;
                for (var j = s; j < e && j < mp.count; j++) {
                    if (mp.mins[j] < lo) lo = mp.mins[j];
                    if (mp.maxs[j] > hi) hi = mp.maxs[j];
                }
                // окно короче одного ведра пиков — растягиваем ведро, а не
                // рисуем пустоту (иначе на большом зуме дорожка исчезает)
                if (hi < lo) { var k = Math.min(mp.count - 1, s); lo = mp.mins[k]; hi = mp.maxs[k]; }
                mins[i] = lo; maxs[i] = hi;
            }
            return { mins: mins, maxs: maxs };
        }
        function sizeCanvas(cv, w, h, dpr) {
            if (cv.width !== Math.round(w * dpr) || cv.height !== Math.round(h * dpr)) {
                cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr);
            }
            var ctx = cv.getContext('2d');
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            return ctx;
        }
        function renderWave() {
            var wrap = $('pc-wave-wrap'), inner = $('pc-wave-inner'), cv = $('pc-wave');
            if (!wrap || !inner || !cv || !masterPeaks) return;
            var baseW = wrap.clientWidth, h = wrap.clientHeight;
            if (baseW < 2 || h < 2) return;
            // waveW — ЛЕНТА целиком; канва рисует только видимое окно и едет
            // за прокруткой. На 10 минутах при зуме 140x лента 126 000 px, а
            // полотно всё те же ~900: сколько ни увеличивай, картинка резкая
            // и памяти стоит одинаково.
            waveW = Math.max(baseW, Math.round(baseW * zoom));
            waveH = h;
            inner.style.width = zoom > 1 ? waveW + 'px' : '100%';
            var w = Math.min(baseW, waveW);
            var viewX = Math.max(0, Math.min(Math.round(wrap.scrollLeft), waveW - w));
            var dpr = Math.min(2, window.devicePixelRatio || 1);
            var ctx = sizeCanvas(cv, w, h, dpr);
            cv.style.width = w + 'px';
            cv.style.height = h + 'px';
            cv.style.transform = 'translateX(' + viewX + 'px)';
            ctx.clearRect(0, 0, w, h);
            var f0 = viewX / waveW, f1 = (viewX + w) / waveW;
            if (!peaksCache || peaksCache.width !== w || peaksCache.f0 !== f0 || peaksCache.f1 !== f1) computePeaks(w, f0, f1);
            ctx.fillStyle = 'rgba(56,189,248,.10)';
            ctx.fillRect(0, h / 2 - 0.5, w, 1);
            ctx.fillStyle = 'rgba(56,189,248,.62)';
            for (var i = 0; i < w; i++) {
                var y1 = (1 - peaksCache.maxs[i]) / 2 * h;
                var y2 = (1 - peaksCache.mins[i]) / 2 * h;
                ctx.fillRect(i, y1, 1, Math.max(1, y2 - y1));
            }
            renderOverlay();
        }
        function renderOverlay() {
            var wrap = $('pc-wave-wrap'), cv = $('pc-wave-overlay');
            if (!wrap || !cv || !duration) return;
            var baseW = wrap.clientWidth, h = waveH || wrap.clientHeight;
            var total = waveW || baseW;
            var w = Math.min(baseW, total);
            if (w < 2 || h < 2) return;
            var viewX = Math.max(0, Math.min(Math.round(wrap.scrollLeft), total - w));
            var dpr = Math.min(2, window.devicePixelRatio || 1);
            var ctx = sizeCanvas(cv, w, h, dpr);
            cv.style.width = w + 'px';
            cv.style.height = h + 'px';
            cv.style.transform = 'translateX(' + viewX + 'px)';
            ctx.clearRect(0, 0, w, h);
            function xOf(t) { return t / duration * total - viewX; }
            /* Each censor is drawn ON ITS OWN, not the joined export regions:
             * two censors that touch are still two things the user can grab.
             * Red — found by the detector, indigo — drawn by the user, an amber
             * rim — detector's censor whose bounds the user changed. */
            var sel = null;
            items.forEach(function (it) {
                var x = xOf(it.start), rw = Math.max(2, (it.end - it.start) / duration * total);
                if (x + rw < -2 || x > w + 2) return;
                if (it.id === selectedId) { sel = it; return; }
                var manual = it.kind === 'manual';
                ctx.fillStyle = manual ? 'rgba(99,102,241,.30)' : 'rgba(239,68,68,.26)';
                ctx.fillRect(x, 0, rw, h);
                ctx.fillStyle = it.edited ? 'rgba(245,158,11,.95)' : (manual ? 'rgba(129,140,248,.9)' : 'rgba(239,68,68,.85)');
                ctx.fillRect(x, 0, rw, 3);
                ctx.fillRect(x, h - 3, rw, 3);
                // a hairline at the start keeps neighbours that touch apart
                if (rw > 6) { ctx.fillStyle = 'rgba(0,0,0,.35)'; ctx.fillRect(x, 0, 1, h); }
            });
            if (sel) {
                var sx = xOf(sel.start), sw = Math.max(2, (sel.end - sel.start) / duration * total);
                ctx.fillStyle = sel.kind === 'manual' ? 'rgba(99,102,241,.42)' : 'rgba(239,68,68,.40)';
                ctx.fillRect(sx, 0, sw, h);
                ctx.strokeStyle = 'rgba(255,255,255,.92)';
                ctx.lineWidth = 2;
                ctx.strokeRect(sx + 1, 1, Math.max(0, sw - 2), h - 2);
                // edge grips — what can be dragged
                ctx.fillStyle = '#fff';
                [sx, sx + sw].forEach(function (gx) {
                    ctx.fillRect(Math.round(gx) - 2, h / 2 - 14, 4, 28);
                });
            }
            // Шкала времени — только в увеличении: без неё непонятно, КУДА
            // ты заехал, а весь смысл зума в том, чтобы попасть в слово.
            drawWords(ctx, w, viewX, total);    // itself decides whether there is room (≥40 px/s)
            if (markIn !== null) {
                var mx = xOf(markIn);
                if (mx >= -2 && mx <= w + 2) {
                    ctx.fillStyle = 'rgba(56,189,248,.95)';
                    ctx.fillRect(Math.round(mx) - 1, 0, 2, h);
                    ctx.fillRect(Math.round(mx) - 1, 0, 8, 2);
                }
            }
            if (zoom > 1) drawRuler(ctx, w, h, viewX, total);
            renderOverview();
        }
        /* Words written on the wave once there is room for them — the edge of
         * a censor is then placed against the WORD, not against a guess of
         * where it is (Premiere shows the transcript on the timeline for the
         * same reason). Hits in red; a word is skipped rather than overlapped. */
        function drawWords(ctx, w, viewX, total) {
            if (!words.length) return;
            var pxPerS = total / duration;
            if (pxPerS < 40) return;
            var t0 = viewX / pxPerS, t1 = (viewX + w) / pxPerS;
            var hit = {};
            activeHitIndices().forEach(function (i) { hit[i] = true; });
            ctx.font = '600 11px system-ui, -apple-system, "Segoe UI", sans-serif';
            ctx.textBaseline = 'top';
            ctx.textAlign = 'left';
            var lastRight = -1e9;
            for (var i = Math.max(0, wordIndexAt(t0) - 1); i < words.length; i++) {
                var wd = words[i];
                if (wd.start > t1) break;
                var x = wd.start * pxPerS - viewX;
                var tw = ctx.measureText(wd.text).width;
                if (x < lastRight + 4) continue;
                ctx.fillStyle = 'rgba(0,0,0,.55)';
                ctx.fillRect(x - 2, 5, tw + 4, 14);
                ctx.fillStyle = hit[i] ? '#fca5a5' : 'rgba(255,255,255,.82)';
                ctx.fillText(wd.text, x, 6);
                lastRight = x + tw;
            }
        }
        // Подписи идут по «круглым» долям секунды, подобранным под масштаб:
        // шаг выбирается так, чтобы метки стояли не чаще, чем раз в ~90 px.
        var RULER_STEPS = [0.1, 0.25, 0.5, 1, 2, 5, 10, 15, 30, 60, 120, 300, 600];
        function drawRuler(ctx, w, h, viewX, total) {
            var pxPerSec = total / duration;
            var step = RULER_STEPS[RULER_STEPS.length - 1];
            for (var i = 0; i < RULER_STEPS.length; i++) {
                if (RULER_STEPS[i] * pxPerSec >= 90) { step = RULER_STEPS[i]; break; }
            }
            var t0 = viewX / pxPerSec, t1 = (viewX + w) / pxPerSec;
            ctx.fillStyle = 'rgba(0,0,0,.30)';
            ctx.fillRect(0, h - 15, w, 15);
            ctx.font = '10px "SF Mono", Monaco, monospace';
            ctx.textBaseline = 'bottom';
            ctx.textAlign = 'left';
            for (var t = Math.ceil(t0 / step) * step; t <= t1; t += step) {
                var x = Math.round(t * pxPerSec - viewX) + 0.5;
                ctx.fillStyle = 'rgba(255,255,255,.35)';
                ctx.fillRect(x, h - 15, 1, 15);
                ctx.fillStyle = 'rgba(255,255,255,.70)';
                ctx.fillText(fmtTick(t, step), x + 3, h - 3);
            }
        }
        function fmtTick(t, step) {
            var m = Math.floor(t / 60), sec = t - m * 60;
            if (step < 1) return m + ':' + (sec < 10 ? '0' : '') + sec.toFixed(step < 0.25 ? 2 : 1);
            return m + ':' + (Math.round(sec) < 10 ? '0' : '') + Math.round(sec);
        }
        /* ---------------- увеличение дорожки ---------------- */
        // Сколько увеличивать имеет смысл: в максимуме в окне остаётся
        // MIN_VISIBLE_S секунд. Короткий файл дальше своего предела не
        // увеличивается — там и так каждое слово шириной в палец.
        function maxZoom() {
            if (!duration) return 1;
            return Math.max(1, Math.min(ZOOM_HARD_MAX, Math.round(duration / MIN_VISIBLE_S)));
        }
        function clampZoom(z) { return Math.max(1, Math.min(maxZoom(), z)); }
        // anchorT — время, которое должно остаться на месте (под курсором,
        // под головкой). Без якоря увеличение уносит в случайное место.
        function setZoom(z, anchorT, via) {
            var wrap = $('pc-wave-wrap');
            if (!wrap || !duration) return;
            var next = clampZoom(z);
            if (next === zoom) { syncZoomUI(); return; }
            var baseW = wrap.clientWidth;
            var w = Math.min(baseW, waveW || baseW);
            var anchor = (typeof anchorT === 'number' && isFinite(anchorT)) ? anchorT
                : (mediaEl ? mediaEl.currentTime : 0);
            var anchorPx = waveW ? (anchor / duration * waveW - wrap.scrollLeft) : w / 2;
            if (anchorPx < 0 || anchorPx > w) anchorPx = w / 2;
            zoom = next;
            wrap.classList.toggle('zoomed', zoom > 1);
            waveW = Math.max(baseW, Math.round(baseW * zoom));
            wrap.scrollLeft = Math.max(0, anchor / duration * waveW - anchorPx);
            renderWave();
            updatePlayhead();
            syncZoomUI();
            if (window.__ae && !zoomReported) {
                zoomReported = true;
                __ae('feature_use', { tool: slug, feature: 'wave_zoom', z: zoom, via: via || 'btn', durS: Math.round(duration) });
            }
        }
        // Новый файл — новая длительность, а значит и другой потолок зума:
        // остаться в 80x на тридцатисекундной записи значит открыть её в
        // пустом окне.
        function resetZoom() {
            zoom = 1; zoomReported = false;
            var wrap = $('pc-wave-wrap'), inner = $('pc-wave-inner');
            if (wrap) { wrap.classList.remove('zoomed'); wrap.scrollLeft = 0; }
            if (inner) inner.style.width = '100%';
            syncZoomUI();
        }
        function syncZoomUI() {
            var lvl = $('pc-zoom-level'), zin = $('pc-zoom-in'), zout = $('pc-zoom-out'), fit = $('pc-zoom-fit');
            if (lvl) lvl.textContent = (zoom < 10 ? Math.round(zoom * 10) / 10 : Math.round(zoom)) + '×';
            if (zin) zin.disabled = !duration || zoom >= maxZoom();
            if (zout) zout.disabled = zoom <= 1;
            if (fit) fit.hidden = zoom <= 1;
            renderOverview();
            var nav = $('pc-wave-nav');
            if (nav) {
                var coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
                nav.hidden = !(zoom > 1 && coarse);
                if (!nav.hidden) updateWaveNav();
            }
        }
        function updateWaveNav() {
            var wrap = $('pc-wave-wrap'), pos = $('pc-wave-pos');
            if (!wrap || !pos) return;
            var max = wrap.scrollWidth - wrap.clientWidth;
            pos.textContent = (max > 0 ? Math.round(wrap.scrollLeft / max * 100) : 0) + '%';
        }
        /* ---------------- обзор всей записи под дорожкой ----------------
         * В увеличении дорожка — окно в несколько секунд, а прокрутка шла
         * системной полосой самой дорожки. Нажатие на эту полосу считалось
         * щелчком по волне: перематывало на время под курсором, и окно
         * возвращалось к нему; протяжка рисовала ручную заглушку («если я
         * перемещусь сюда, меня возвращает обратно» — Ангелина, 08.10; в её
         * сессии десятки `manual_region`, снятых тут же Ctrl+Z).
         *
         * Теперь под дорожкой обзор, как полоса масштаба в Premiere: вся запись
         * с заглушками и головкой, рамка — видимое окно. Тащить рамку — листать,
         * тянуть её края — менять масштаб, щёлкнуть мимо рамки — подвести окно
         * туда. Головку обзор не двигает никогда: смотреть и слушать — разные
         * действия. Системная полоса скрыта. */
        var followHold = false;          // the person moved the window away while playing
        var ovPeaks = null;              // whole-file peaks at the overview's width
        function holdFollow() { if (mediaEl && !mediaEl.paused) followHold = true; }
        function renderOverview() {
            var box = $('pc-overview'), cv = $('pc-overview-cv'), win = $('pc-ov-win'), wrap = $('pc-wave-wrap');
            if (!box || !cv || !win || !wrap) return;
            var show = zoom > 1 && !!masterPeaks && duration > 0;
            if (box.hidden === show) box.hidden = !show;
            if (!show) return;
            var W = box.clientWidth, H = box.clientHeight;
            if (W < 4 || H < 4) return;
            var dpr = Math.min(2, window.devicePixelRatio || 1);
            var ctx = sizeCanvas(cv, W, H, dpr);
            cv.style.width = W + 'px'; cv.style.height = H + 'px';
            if (!ovPeaks || ovPeaks.width !== W) { var pk = peaksOf(W, 0, 1); ovPeaks = { width: W, mins: pk.mins, maxs: pk.maxs }; }
            ctx.clearRect(0, 0, W, H);
            ctx.fillStyle = 'rgba(56,189,248,.45)';
            for (var i = 0; i < W; i++) {
                var y1 = (1 - ovPeaks.maxs[i]) / 2 * H, y2 = (1 - ovPeaks.mins[i]) / 2 * H;
                ctx.fillRect(i, y1, 1, Math.max(1, y2 - y1));
            }
            // every censor, at least a pixel — where the work is in the file
            items.forEach(function (it) {
                var x = it.start / duration * W, rw = Math.max(1, (it.end - it.start) / duration * W);
                ctx.fillStyle = it.kind === 'manual' ? 'rgba(129,140,248,.9)' : (it.edited ? 'rgba(245,158,11,.9)' : 'rgba(239,68,68,.85)');
                ctx.fillRect(x, 0, rw, H);
            });
            var total = waveW || wrap.clientWidth;
            var left = wrap.scrollLeft / total * W, width = Math.max(14, wrap.clientWidth / total * W);
            win.style.left = Math.max(0, Math.min(W - width, left)) + 'px';
            win.style.width = width + 'px';
            win.setAttribute('aria-valuenow', String(Math.round(wrap.scrollLeft / Math.max(1, total - wrap.clientWidth) * 100)));
            moveOverviewPlayhead();
        }
        // the overview's playhead moves every frame: written on a new device
        // pixel only (the shared primitive), with a fallback for a page whose
        // core is older than this file
        function pxWrite(e, p, v, u) { if (window.__perPixel) return window.__perPixel(e, p, v, u); e.style[p] = v + (u || '%'); }
        function moveOverviewPlayhead() {
            var box = $('pc-overview'), ph = $('pc-ov-ph');
            if (!box || !ph || box.hidden || !duration || !mediaEl) return;
            var pct = Math.max(0, Math.min(100, mediaEl.currentTime / duration * 100));
            pxWrite(ph, 'left', pct);
        }
        function bindOverview() {
            var box = $('pc-overview'), win = $('pc-ov-win'), wrap = $('pc-wave-wrap');
            if (!box || !win || !wrap) return;
            var g = null;
            function W() { return box.clientWidth || 1; }
            function total() { return waveW || wrap.clientWidth; }
            function xOf(ev) { var r = box.getBoundingClientRect(); return ev.clientX - r.left; }
            function scrollTo(px) { wrap.scrollLeft = Math.max(0, Math.min(total() - wrap.clientWidth, px)); renderWave(); updateWaveNav(); }
            box.addEventListener('pointerdown', function (ev) {
                if (ev.button !== undefined && ev.button !== 0) return;
                var x = xOf(ev), handle = ev.target.closest && ev.target.closest('.pc-ov-h');
                var winL = parseFloat(win.style.left) || 0, winW = parseFloat(win.style.width) || 14;
                try { box.setPointerCapture(ev.pointerId); } catch (e) { /* noop */ }
                holdFollow();
                if (handle) {
                    // the far edge stays where it is, this one sets the span
                    var a = winL / W() * duration, b = (winL + winW) / W() * duration;
                    g = { mode: 'edge', edge: handle.getAttribute('data-h'), a: a, b: b };
                } else if (x >= winL && x <= winL + winW) {
                    g = { mode: 'pan', x0: x, s0: wrap.scrollLeft };
                } else {
                    // jump the WINDOW (never the playhead) and keep dragging it
                    scrollTo((x / W()) * total() - wrap.clientWidth / 2);
                    g = { mode: 'pan', x0: x, s0: wrap.scrollLeft };
                    if (window.__ae) __ae('feature_use', { tool: slug, feature: 'overview', act: 'jump' });
                }
                ev.preventDefault();
            });
            box.addEventListener('pointermove', function (ev) {
                if (!g) return;
                var x = xOf(ev);
                if (g.mode === 'pan') {
                    scrollTo(g.s0 + (x - g.x0) * total() / W());
                } else {
                    var t = Math.max(0, Math.min(duration, x / W() * duration));
                    var a = g.edge === 'start' ? Math.min(t, g.b - MIN_VISIBLE_S) : g.a;
                    var b = g.edge === 'end' ? Math.max(t, g.a + MIN_VISIBLE_S) : g.b;
                    var span = Math.max(MIN_VISIBLE_S, b - a);
                    setZoom(duration / span, null, 'overview');
                    scrollTo(a / duration * total());
                }
            });
            function end() {
                if (g && g.mode === 'edge' && window.__ae) __ae('feature_use', { tool: slug, feature: 'overview', act: 'zoom', z: Math.round(zoom) });
                g = null;
            }
            box.addEventListener('pointerup', end);
            box.addEventListener('pointercancel', end);
            box.addEventListener('wheel', function (ev) {
                ev.preventDefault();
                holdFollow();
                var d = Math.abs(ev.deltaX) > Math.abs(ev.deltaY) ? ev.deltaX : ev.deltaY;
                scrollTo(wrap.scrollLeft + d * total() / W() * 0.5);
            }, { passive: false });
            win.addEventListener('keydown', function (ev) {
                var step = wrap.clientWidth * (ev.shiftKey ? 1 : 0.25), k = ev.key;
                if (k === 'ArrowLeft' || k === 'ArrowRight' || k === 'Home' || k === 'End' || k === 'PageUp' || k === 'PageDown') {
                    ev.preventDefault(); ev.stopPropagation();
                    holdFollow();
                    if (k === 'ArrowLeft') scrollTo(wrap.scrollLeft - step);
                    else if (k === 'ArrowRight') scrollTo(wrap.scrollLeft + step);
                    else if (k === 'PageUp') scrollTo(wrap.scrollLeft - wrap.clientWidth);
                    else if (k === 'PageDown') scrollTo(wrap.scrollLeft + wrap.clientWidth);
                    else if (k === 'Home') scrollTo(0);
                    else scrollTo(total());
                }
            });
        }
        // Показать момент времени: подвести окно так, чтобы он был по центру.
        // Горизонтальная прокрутка ВНУТРИ дорожки — страницу не двигает.
        function revealTime(t) {
            var wrap = $('pc-wave-wrap');
            if (!wrap || !duration || zoom <= 1) return;
            var w = wrap.clientWidth;
            wrap.scrollLeft = Math.max(0, Math.min(waveW - w, t / duration * waveW - w / 2));
            renderWave();
            updatePlayhead();
            updateWaveNav();
        }
        // «Показать на дорожке» из списка найденного: увеличить так, чтобы
        // слово занимало заметную долю окна, и подвести к нему. Ровно то,
        // чего не хватало, когда слово было уже полпикселя.
        function locateOnWave(start, end) {
            var span = Math.max(0.6, end - start);
            setZoom(clampZoom(duration / (span * 12)), (start + end) / 2, 'locate');
            revealTime((start + end) / 2);
            var wrap = $('pc-wave-wrap');
            if (wrap) { try { wrap.focus({ preventScroll: true }); } catch (e) { /* noop */ } }
        }
        function updatePlayhead() {
            var ph = $('pc-playhead');
            if (!ph || !duration || !mediaEl) return;
            var total = waveW || 1;
            var x = mediaEl.currentTime / duration * total;
            ph.style.transform = 'translateX(' + x + 'px)';
            var tm = $('pc-time');
            if (tm) tm.textContent = fmtTime(mediaEl.currentTime) + ' / ' + fmtTime(duration);
            // в увеличении окно следует за головкой, иначе воспроизведение
            // уезжает за край и дорожка стоит мёртвой — но НЕ когда человек сам
            // увёл окно в сторону: «перемещусь сюда, а меня возвращает обратно»
            // (Ангелина, 08.10). Следование возвращается, когда головка снова в
            // окне или человек сам перемотал.
            if (zoom > 1 && !mediaEl.paused) {
                var wrap = $('pc-wave-wrap');
                if (wrap) {
                    var w = wrap.clientWidth, left = wrap.scrollLeft;
                    var inView = x >= left && x <= left + w;
                    if (followHold && inView && x > left + w * 0.1 && x < left + w * 0.9) followHold = false;
                    if (!followHold && (x < left + w * 0.1 || x > left + w * 0.9)) {
                        wrap.scrollLeft = Math.max(0, Math.min(total - w, x - w / 2));
                        renderWave();
                        updateWaveNav();
                    }
                }
            }
            moveOverviewPlayhead();
            followList(false);
            followTranscript();
        }
        var nowWordIdx = -1;
        function followTranscript() {
            if (!mediaEl || !words.length) return;
            var t = mediaEl.currentTime, k = wordIndexAt(t);
            if (words[k] && t > words[k].end + 0.6) k = -1;     // in a pause: nothing is «now»
            if (k === nowWordIdx) return;
            var box = $('pc-transcript');
            if (!box) return;
            var prev = nowWordIdx >= 0 ? box.querySelector('.pc-word[data-i="' + nowWordIdx + '"]') : null;
            if (prev) prev.classList.remove('now');
            nowWordIdx = k;
            if (k < 0) return;
            var span = box.querySelector('.pc-word[data-i="' + k + '"]');
            if (!span) return;
            span.classList.add('now');
            if (mediaEl.paused || box.matches(':hover')) return;   // the reader is reading
            var br = box.getBoundingClientRect(), sr = span.getBoundingClientRect();
            if (sr.top < br.top || sr.bottom > br.bottom) box.scrollTop += (sr.top - br.top) - box.clientHeight / 3;
        }

        /* ---- waveform pointer interaction ----
         *   on an edge of a censor      drag = move that edge (any censor —
         *                               the detector's too, since 24.09.2026)
         *   inside a censor             click = select it (and seek there),
         *                               drag = move it whole
         *   on empty wave               click = seek, drag = draw a new censor
         * Hit-testing is geometry, not DOM handles: a dense file has a
         * thousand censors, and they all have edges. */
        function bindWaveform() {
            var wrap = $('pc-wave-wrap');
            if (!wrap) return;
            var sel = $('pc-drag-sel');
            var drag = null;

            // Координата считается ВНУТРИ ЛЕНТЫ: в увеличении окно
            // прокручено, и без scrollLeft каждый клик попадал бы в начало
            // видимого куска, а не туда, куда целится человек.
            function evX(ev) {
                var rect = wrap.getBoundingClientRect();
                var x = ev.clientX - rect.left + wrap.scrollLeft;
                return Math.max(0, Math.min(waveW || rect.width, x));
            }
            function xToT(x) { return duration ? x / (waveW || wrap.clientWidth) * duration : 0; }
            function tToX(t) { return duration ? t / duration * (waveW || wrap.clientWidth) : 0; }
            /* Snapping, as on a Premiere timeline: a dragged edge within 8 px of
             * the playhead or of a word boundary lands exactly on it — the
             * playhead is where the ear says the word starts, a word boundary
             * is where the recogniser heard it. Alt switches snapping off. */
            var SNAP_PX = 8;
            function snapTime(t, ev, dragId) {
                if (ev && ev.altKey) return t;
                var pxPerS = (waveW || wrap.clientWidth) / duration, tol = SNAP_PX / pxPerS;
                var best = t, bestD = tol;
                function consider(c) { var d = Math.abs(c - t); if (d < bestD) { bestD = d; best = c; } }
                if (mediaEl) consider(mediaEl.currentTime);
                var k = wordIndexAt(t);
                for (var j = Math.max(0, k - 2); j <= Math.min(words.length - 1, k + 2); j++) {
                    consider(words[j].start); consider(words[j].end);
                }
                return best;
            }
            function edgeTol(ev) { return (ev && ev.pointerType === 'touch') || (window.matchMedia && window.matchMedia('(pointer: coarse)').matches) ? 14 : 7; }

            /* What is under x: {item, edge:'start'|'end'|null}. An edge wins
             * when it is within reach AND the censor is wide enough to have
             * two separate edges on screen; the selected censor is preferred,
             * so a grabbed edge does not jump to a neighbour that touches it. */
            function hitTest(x, tol) {
                var best = null, bestD = Infinity, inside = null;
                for (var i = 0; i < items.length; i++) {
                    var it = items[i], x0 = tToX(it.start), x1 = tToX(it.end);
                    if (x1 < x - tol - 1) continue;
                    if (x0 > x + tol + 1) break;
                    var wide = (x1 - x0) >= 2 * tol;
                    var pref = it.id === selectedId ? -0.5 : 0;
                    if (wide || it.id === selectedId) {
                        var d0 = Math.abs(x - x0) + pref, d1 = Math.abs(x - x1) + pref;
                        if (d0 <= tol && d0 < bestD) { best = { item: it, edge: 'start' }; bestD = d0; }
                        if (d1 <= tol && d1 < bestD) { best = { item: it, edge: 'end' }; bestD = d1; }
                    }
                    if (x >= x0 && x <= x1 && (!inside || (x1 - x0) < (tToX(inside.end) - tToX(inside.start)))) inside = it;
                }
                if (best) return best;
                return inside ? { item: inside, edge: null } : null;
            }

            wrap.addEventListener('pointerdown', function (ev) {
                if (!duration || phase === 'export') return;
                if (ev.button !== undefined && ev.button !== 0) return;
                // the strip under the content box is the element's own
                // scrollbar (hidden where the overview replaces it, live in
                // browsers that still draw one): scrolling, never «seek here»
                // or «draw a censor here»
                var wr = wrap.getBoundingClientRect();
                if (ev.clientY > wr.top + wrap.clientTop + wrap.clientHeight) return;
                try { wrap.setPointerCapture(ev.pointerId); } catch (e) { /* noop */ }
                var x = evX(ev), h = hitTest(x, edgeTol(ev));
                if (h && h.edge) {
                    if (h.item.id !== selectedId) selectItem(h.item.id);
                    drag = { mode: 'resize', id: h.item.id, edge: h.edge, before: snapState(), changed: false };
                } else if (h) {
                    // Only a censor you can SEE is movable by dragging: at low zoom
                    // one is a pixel wide, and a drag that happens to start on it
                    // means «draw here», not «move that».
                    var movable = (tToX(h.item.end) - tToX(h.item.start)) >= 12;
                    drag = { mode: 'item', id: h.item.id, movable: movable, x0: x, t0: xToT(x), s0: h.item.start, e0: h.item.end, before: snapState(), moved: false };
                } else {
                    drag = { mode: 'draw', x0: x, t0: xToT(x), moved: false };
                }
                ev.preventDefault();
            });
            wrap.addEventListener('pointermove', function (ev) {
                var x = evX(ev), t = xToT(x);
                if (!drag) {
                    // hover: tell the hand what it can do here
                    if (!duration || ev.pointerType === 'touch') return;
                    var h = hitTest(x, edgeTol(ev));
                    wrap.style.cursor = !h ? '' : h.edge ? 'ew-resize'
                        : ((tToX(h.item.end) - tToX(h.item.start)) >= 12 ? 'grab' : 'pointer');
                    return;
                }
                if (drag.mode === 'resize') {
                    var it = itemById(drag.id);
                    if (!it) { drag = null; return; }
                    var s = it.start, e = it.end;
                    t = snapTime(t, ev, drag.id);
                    if (drag.edge === 'start') s = Math.min(t, e - MIN_ITEM_S); else e = Math.max(t, s + MIN_ITEM_S);
                    if (setItemBounds(it, s, e)) { drag.changed = true; refreshRegionsOnly(); }
                    return;
                }
                if (drag.mode === 'item' && !drag.movable && Math.abs(x - drag.x0) > 4) {
                    drag = { mode: 'draw', x0: drag.x0, t0: drag.t0, moved: true };
                }
                if (drag.mode === 'item') {
                    if (!drag.moved && Math.abs(x - drag.x0) <= 4) return;
                    drag.moved = true;
                    wrap.style.cursor = 'grabbing';
                    var it2 = itemById(drag.id);
                    if (!it2) { drag = null; return; }
                    var len = drag.e0 - drag.s0;
                    var ns = Math.max(0, Math.min(duration - len, drag.s0 + (t - drag.t0)));
                    // snap whichever edge lands near a snap point
                    var sS = snapTime(ns, ev, drag.id), sE = snapTime(ns + len, ev, drag.id);
                    if (sS !== ns) ns = sS; else if (sE !== ns + len) ns = sE - len;
                    ns = Math.max(0, Math.min(duration - len, ns));
                    if (setItemBounds(it2, ns, ns + len)) refreshRegionsOnly();
                    return;
                }
                if (Math.abs(x - drag.x0) > 4) drag.moved = true;
                if (drag.moved && sel) {
                    var a = Math.min(drag.x0, x), b = Math.max(drag.x0, x);
                    sel.hidden = false;
                    sel.style.left = a + 'px';
                    sel.style.width = (b - a) + 'px';
                }
            });
            function snapItem(id) {
                var it = itemById(id);
                if (!it || !pcmAudio) return;
                setItemBounds(it, pcmAudio.snapZero(it.start), pcmAudio.snapZero(it.end));
            }
            function endDrag(ev) {
                if (!drag) return;
                var d = drag; drag = null;
                if (sel) sel.hidden = true;
                wrap.style.cursor = '';
                if (d.mode === 'resize') {
                    if (d.changed) {
                        snapItem(d.id);
                        pushHistory(d.before);
                        refreshAll();
                        reportEdit('resize', itemById(d.id), 'wave');
                    }
                    return;
                }
                if (d.mode === 'item') {
                    if (d.moved) {
                        snapItem(d.id);
                        pushHistory(d.before);
                        refreshAll();
                        reportEdit('move', itemById(d.id), 'wave');
                    } else {
                        selectItem(d.id);
                        seekTo(d.t0, { keepPaused: true });
                    }
                    return;
                }
                var t1 = xToT(evX(ev));
                if (d.moved && Math.abs(t1 - d.t0) >= 0.05) {
                    addManualRegion(Math.min(d.t0, t1), Math.max(d.t0, t1), { select: true });
                } else {
                    if (selectedId) selectItem(null);
                    seekTo(d.t0, { keepPaused: true });
                }
            }
            wrap.addEventListener('pointerup', endDrag);
            wrap.addEventListener('pointercancel', function () {
                // an interrupted gesture must not leave a half-applied edit
                if (drag && (drag.mode === 'resize' || drag.mode === 'item') && drag.before && (drag.changed || drag.moved)) restoreState(drag.before);
                drag = null; if (sel) sel.hidden = true; wrap.style.cursor = '';
            });
            wrap.addEventListener('pointerleave', function () { if (!drag) wrap.style.cursor = ''; });

            // Колесо: с Ctrl/Cmd — увеличение вокруг курсора (как в любом
            // редакторе), без него в увеличении — прокрутка ленты.
            wrap.addEventListener('wheel', function (ev) {
                if (!duration) return;
                if (ev.ctrlKey || ev.metaKey) {
                    ev.preventDefault();
                    setZoom(ev.deltaY < 0 ? zoom * ZOOM_STEP : zoom / ZOOM_STEP, xToT(evX(ev)), 'wheel');
                } else if (zoom > 1 && !ev.shiftKey) {
                    ev.preventDefault();
                    holdFollow();
                    wrap.scrollLeft += (Math.abs(ev.deltaX) > Math.abs(ev.deltaY) ? ev.deltaX : ev.deltaY);
                    scheduleViewRender();
                }
            }, { passive: false });
            var viewRaf = 0;
            function scheduleViewRender() {
                if (viewRaf) return;
                viewRaf = requestAnimationFrame(function () {
                    viewRaf = 0;
                    renderWave();
                    updateWaveNav();
                });
            }
            wrap.addEventListener('scroll', scheduleViewRender);

            var zin = $('pc-zoom-in'), zout = $('pc-zoom-out'), zfit = $('pc-zoom-fit');
            if (zin) zin.onclick = function () { setZoom(zoom * ZOOM_STEP, null, 'btn'); };
            if (zout) zout.onclick = function () { setZoom(zoom / ZOOM_STEP, null, 'btn'); };
            if (zfit) zfit.onclick = function () { setZoom(1, null, 'fit'); };
            var navL = $('pc-wave-left'), navR = $('pc-wave-right');
            if (navL) navL.onclick = function () { holdFollow(); wrap.scrollLeft -= wrap.clientWidth * 0.6; scheduleViewRender(); };
            if (navR) navR.onclick = function () { holdFollow(); wrap.scrollLeft += wrap.clientWidth * 0.6; scheduleViewRender(); };
            bindOverview();

            if (window.ResizeObserver && !resizeObs) {
                resizeObs = new ResizeObserver(function () { renderWave(); updatePlayhead(); syncZoomUI(); });
                resizeObs.observe(wrap);
            }
        }

        /* Page-wide editor keys (Ангелина 23.09: «пробел всегда как пауза и
         * плей… я привыкла работать в Premiere Pro»; 124 Space and three
         * Ctrl+Z presses in one session, the Z presses doing nothing).
         * Active only while there is a result AND the player is on screen —
         * a reader down in the FAQ keeps Space for scrolling. Text fields keep
         * their own Space, Delete and native undo. */
        function isTypingTarget(el) {
            if (!el || el === document.body) return false;
            if (el.isContentEditable) return true;
            var tag = el.tagName;
            if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
            if (tag === 'INPUT') return !/^(checkbox|radio|range|button|submit|reset|file|color)$/i.test(el.type || 'text');
            return false;
        }
        function playerOnScreen() {
            var card = document.querySelector('.pc-player-card');
            if (!card || card.offsetParent === null) return false;
            var r = card.getBoundingClientRect();
            return r.bottom > 40 && r.top < (window.innerHeight || document.documentElement.clientHeight) - 40;
        }
        var FRAME_S = 0.04;                     // «one frame» for ← → (25 fps)
        var markIn = null;                      // I pressed without a selection
        var keyStats = {};                      // key → count, reported with the export
        var RATES = [1, 1.5, 2];
        function setRate(r) {
            if (!mediaEl) return;
            mediaEl.playbackRate = r;
            var b = $('pc-rate');
            if (b) { b.textContent = (r === 1 ? '1' : String(r)) + '×'; b.classList.toggle('fast', r !== 1); }
        }
        function cycleRate() {
            if (!mediaEl) return;
            var k = RATES.indexOf(mediaEl.playbackRate);
            setRate(RATES[(k + 1) % RATES.length]);
            if (window.__ae) __ae('feature_use', { tool: slug, feature: 'play_rate', r: mediaEl.playbackRate });
        }
        /* I / O. With a censor selected they move ITS start / end to the
         * playhead — play, stop where the word begins, press I. Without a
         * selection, I marks the in-point and O draws the censor from it. */
        function markInOut(which, t) {
            var sel = selectedId ? itemById(selectedId) : null;
            if (sel) {
                var s = sel.start, e = sel.end;
                if (which === 'in') s = t; else e = t;
                if (e - s < MIN_ITEM_S) { if (window.__toast) __toast(i18n.io_invalid || '', 'warning'); return; }
                pushHistory();
                if (setItemBounds(sel, s, e)) { refreshAll(); reportEdit('resize', sel, 'key_io'); }
                return;
            }
            if (which === 'in') { markIn = t; renderOverlay(); return; }
            if (markIn !== null && t - markIn >= 0.05) {
                addManualRegion(markIn, t, { select: true });
                markIn = null;
            } else if (markIn !== null && markIn - t >= 0.05) {
                addManualRegion(t, markIn, { select: true });
                markIn = null;
            }
        }
        function moveItem(id, d) {
            var it = itemById(id);
            if (!it) return;
            var len = it.end - it.start, ns = Math.max(0, Math.min(duration - len, it.start + d));
            var now = Date.now();
            if (!(lastNudge && lastNudge.id === id && now - lastNudge.at < 1500)) { pushHistory(); reportEdit('nudge', it, 'key'); }
            if (setItemBounds(it, ns, ns + len)) refreshAll();
            lastNudge = { id: id, at: now };
            syncUndoUI();
        }
        /* Play the censor WITH the censor, a second before to a moment after,
         * then stop — the check after every edit (Premiere's «play around»). */
        var PLAY_PRE = 1.0, PLAY_POST = 0.7, stopAt = null;
        function playAround(it) {
            if (!mediaEl || !it) return;
            seekTo(Math.max(0, it.start - PLAY_PRE), { keepPaused: true });
            stopAt = it.end + PLAY_POST;
            if (mediaEl.paused) mediaEl.play().catch(function () { /* gesture rules */ });
            if (window.__ae) __ae('feature_use', { tool: slug, feature: 'play_around' });
        }
        function bindEditorKeys() {
            document.addEventListener('keydown', function (ev) {
                if (!mediaEl || !duration || !items || phase !== 'ready') return;
                if (ev.defaultPrevented || isTypingTarget(ev.target)) return;
                var mod = ev.ctrlKey || ev.metaKey;
                // Undo / redo work wherever the focus is on the page (not in a
                // field): the wave, a list button, the body after a click.
                if (mod && !ev.altKey && (ev.code === 'KeyZ' || ev.code === 'KeyY')) {
                    ev.preventDefault();
                    if (ev.code === 'KeyY' || ev.shiftKey) redo(); else undo();
                    return;
                }
                // Ctrl+S — save the project, as in every editor (and not the
                // browser's «save page», which keeps nothing of the work)
                if (mod && !ev.altKey && !ev.shiftKey && ev.code === 'KeyS') {
                    ev.preventDefault();
                    saveProject(false, 'key');
                    return;
                }
                if (mod || ev.altKey) return;
                var tag = ev.target && ev.target.tagName;
                if (ev.code === 'Space' || ev.key === ' ') {
                    // the <video> handles its own Space; a toggle here as well
                    // would pause and resume in one keystroke
                    if (tag === 'VIDEO' || tag === 'AUDIO') return;
                    if (!playerOnScreen()) return;
                    ev.preventDefault();          // no page scroll, no re-click of a focused button
                    togglePlay();
                    return;
                }
                if (!playerOnScreen()) return;
                var inCard = !!(ev.target && ev.target.closest && ev.target.closest('.pc-player-card'));
                var code = ev.code, t = mediaEl.currentTime, sel = selectedId ? itemById(selectedId) : null;
                var handled = true;
                if (ev.key === 'Delete' || ev.key === 'Backspace') {
                    if (sel) deleteItem(sel.id, 'key'); else handled = false;
                } else if (ev.key === 'Escape') {
                    if (sel) selectItem(null); else if (markIn !== null) { markIn = null; renderOverlay(); } else handled = false;
                } else if (code === 'BracketLeft' || (code === 'ArrowUp' && inCard)) {
                    stepSelection(-1);
                } else if (code === 'BracketRight' || (code === 'ArrowDown' && inCard)) {
                    stepSelection(1);
                } else if (code === 'KeyK' && ev.shiftKey) {
                    if (sel) playAround(sel); else handled = false;
                } else if (code === 'KeyK') {
                    // K — stop, and back to normal speed (as J/K/L in Premiere)
                    if (!mediaEl.paused) mediaEl.pause();
                    setRate(1);
                } else if (code === 'KeyL') {
                    // L — play; again while playing — faster (1 → 1.5 → 2)
                    if (mediaEl.paused) { setRate(1); togglePlay(); }
                    else setRate(mediaEl.playbackRate < 1.5 ? 1.5 : 2);
                } else if (code === 'KeyJ') {
                    // J — back 3 s at normal speed (a browser cannot play backwards)
                    setRate(1);
                    seekTo(t - 3, { keepPaused: false });
                } else if (code === 'KeyI' || code === 'KeyB') {
                    markInOut('in', t);
                } else if (code === 'KeyO' || code === 'KeyN') {
                    markInOut('out', t);
                } else if ((code === 'Comma' || code === 'Period') && sel) {
                    // , . — move the selected censor 10 ms (Shift: 100 ms), as in /audio-censor
                    var d = (ev.shiftKey ? 0.1 : 0.01) * (code === 'Comma' ? -1 : 1);
                    moveItem(sel.id, d);
                } else if ((code === 'ArrowLeft' || code === 'ArrowRight') && inCard && ev.target.tagName !== 'VIDEO') {
                    // ← → — one frame (Shift: one second), as in an editor
                    var step = ev.shiftKey ? 1 : FRAME_S;
                    seekTo(t + (code === 'ArrowLeft' ? -step : step), { keepPaused: mediaEl.paused });
                } else if (ev.key === '+' || ev.key === '=') {
                    setZoom(zoom * ZOOM_STEP, null, 'key');
                } else if (ev.key === '-' || ev.key === '_') {
                    setZoom(zoom / ZOOM_STEP, null, 'key');
                } else if ((window.__keyLetter ? window.__keyLetter(ev) : ev.key) === '0' && inCard) {
                    setZoom(1, null, 'key');
                } else {
                    handled = false;
                }
                if (handled) {
                    ev.preventDefault();
                    if (window.__ae) keyStats[code] = (keyStats[code] || 0) + 1;
                }
            });
            // A focused button fires its click on Space KEYUP — cancelled above
            // on keydown, but some engines still activate: swallow it here too.
            document.addEventListener('keyup', function (ev) {
                if ((ev.code === 'Space' || ev.key === ' ') && mediaEl && phase === 'ready' && !isTypingTarget(ev.target) &&
                    ev.target && ev.target.tagName === 'BUTTON' && playerOnScreen()) ev.preventDefault();
            });
        }

        /* ---------------- preview (live censor on media element) ---------------- */
        function setupPreview() {
            var holder = $('pc-video-holder');
            if (mediaEl) { try { mediaEl.pause(); } catch (e) { /* noop */ } try { mediaEl.remove(); } catch (e) { /* noop */ } }
            cancelAnimationFrame(rafId);
            if (srcNode) { try { srcNode.disconnect(); } catch (e) { /* noop */ } srcNode = null; }
            stopCustomSrc();
            if (holder) holder.innerHTML = '';
            if (mediaUrl) URL.revokeObjectURL(mediaUrl);
            mediaUrl = URL.createObjectURL(file);
            mediaEl = document.createElement(isVideoFile ? 'video' : 'audio');
            mediaEl.preload = 'metadata';
            mediaEl.src = mediaUrl;
            if (isVideoFile) {
                mediaEl.controls = true;
                mediaEl.className = 'pc-media';
                mediaEl.playsInline = true;
                if (holder) holder.appendChild(mediaEl);
            } else {
                // audio rides invisibly behind the custom waveform transport
                mediaEl.style.display = 'none';
                document.body.appendChild(mediaEl);
            }

            var el = mediaEl, routed = false;
            warmGate();
            // a new element is a new timeline: whatever the gate knew is void
            unlockSync('file');
            postGate({ type: 'map', m0: null });
            postGate({ type: 'under-clear' }); underSent = {};
            pushGateParams();      // the new file's rate and start define the frames
            el.addEventListener('play', function () {
                try {
                    if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
                    ensureGate();
                    // createMediaElementSource is once-per-ELEMENT: every new
                    // element gets its own source into the shared graph
                    if (!routed) {
                        srcNode = audioCtx.createMediaElementSource(el);
                        routed = true;
                        rewirePreview();
                    }
                    if (gateState !== 'ready' && settings.mode === 'custom') ensureCustomSrc();
                    if (audioCtx.state === 'suspended') audioCtx.resume();
                } catch (e) { /* preview falls back to uncensored playback */ }
                unlockSync('play');
                followHold = false;
                setPlayIcon(true);
                tick();
            });
            // the element moves again (after ▶, a stall, a seek): the gate gets
            // its timeline from the EVENT — a hidden tab has no animation frame
            // to hand it one, and plays on
            el.addEventListener('playing', function () { startGateClock(); });
            el.addEventListener('pause', function () { stopGateClock('pause'); cancelAnimationFrame(rafId); muteBeep(); stopVoiceSrc(); setPlayIcon(false); updatePlayhead(); });
            el.addEventListener('ended', function () { stopGateClock('ended'); cancelAnimationFrame(rafId); muteBeep(); stopVoiceSrc(); setPlayIcon(false); updatePlayhead(); applyVideoCue(0, false); });
            el.addEventListener('emptied', function () { stopGateClock('emptied'); });
            // перемотка внутри той же заглушки: источник «не голоса» играл бы со
            // старой позиции — перезапустит следующий кадр tick()
            // (a seek while PLAYING keeps a timeline — at the target, so a censor
            // there is in place before the first sample arrives; paused, there is none)
            el.addEventListener('seeking', function () {
                stopVoiceSrc();
                if (el.paused) stopGateClock('seek'); else { unlockSync('seek'); postProvisionalMap(); }
            });
            // a stall stops the element while the graph runs on: the measured
            // mapping no longer describes what comes next. (The timeline itself
            // stays — on the element's clock, refreshed every frame by tick():
            // «waiting» is not «no sound», the buffered tail still plays out,
            // and a gate without a timeline there let a censored word through.)
            el.addEventListener('waiting', function () { unlockSync('stall'); });
            el.addEventListener('ratechange', function () { unlockSync('rate'); });
            el.addEventListener('loadedmetadata', updatePlayhead);
            el.addEventListener('seeked', function () {
                startGateClock();
                updatePlayhead();
                // reflect the cue when scrubbing into / out of a region paused
                var previewOn = $('pc-preview-toggle') ? $('pc-preview-toggle').checked : true;
                applyVideoCue(el.currentTime, previewOn && inRegion(el.currentTime));
            });
            setPlayIcon(false);
        }

        /* ---------------- the preview graph ----------------
         * element → MediaElementSource → gate (AudioWorklet) → speakers.
         *
         * The gate is preview-gate-worklet.js: it silences, ducks or replaces
         * each SAMPLE by the same shape the exported file gets, at the moment
         * OF THE FILE that sample is. That moment is measured (preview-sync.js
         * finds a tap of the gate's input in the tool's PCM — the timeline
         * the words, the waveform and the export share), so the preview is
         * the file whatever the browser's decoder, seek or output device do.
         *
         * Until 09.10.2026 a rAF tick decided «censored?» from
         * `currentTime + outputLatency + 29 ms` and ramped a GainNode. The
         * device latency has no business in that sum: the word and its silence
         * leave the graph together and reach the ear together. On a desktop
         * card it put the silence ~56 ms ahead of the word, on a Bluetooth or
         * USB headset ~110 ms (chat 640: Ангелина tuned 297 censors by that
         * preview over three hours, and the exactly-cut file let the start of
         * every word through). The GainNode path stays for a browser without
         * AudioWorklet, without that sum.
         *
         * Until the module is loaded (fetched ahead by warmGate() when the file
         * is ready, so normally before the first ▶) the source rides that
         * GainNode path — a few milliseconds off, never silent — and moves to
         * the gate the moment it exists. */
        var gateNode = null, gateState = 'none';   // none|loading|ready|failed
        var underSent = {};
        var sync = { locked: false, map: null, gen: 0, tapGen: -1, tapAt: 0, unlockFrame: 0, fails: 0,
            reported: false, lagS: [], why: 'none', legacyAsked: false, playS: 0, hist: [] };
        function ctxFrameNow() { return audioCtx ? Math.round(audioCtx.currentTime * audioCtx.sampleRate) : 0; }
        function postGate(msg, transfer) {
            if (gateNode) { try { gateNode.port.postMessage(msg, transfer || []); } catch (e) { /* noop */ } }
        }
        function ensureGate() {
            if (gateState !== 'none' || !audioCtx) return;
            // __pcNoGate: stands force the GainNode fallback to reproduce the old preview
            var ok = !window.__pcNoGate && !!(window.AudioWorkletNode && audioCtx.audioWorklet && cfg.gateWorkletUrl && window.ProfanityPreviewSync);
            if (!ok) { gateState = 'failed'; reportGate('unsupported'); return; }
            gateState = 'loading';
            // the shared sound shape first: the gate reads ProfanitySound
            var mods = [cfg.soundModuleUrl, cfg.gateWorkletUrl].filter(Boolean);
            // the shared loader retries a transient network blip and counts the
            // attempts (worklet_load_attempt); bare addModule where it is absent
            function addMod(u) {
                return window.AudioWorkletLoader ? window.AudioWorkletLoader.load(audioCtx, [u], { slug: slug })
                    : audioCtx.audioWorklet.addModule(u);
            }
            mods.reduce(function (p, u) {
                return p.then(function () { return addMod(u); });
            }, Promise.resolve()).then(function () {
                gateNode = new AudioWorkletNode(audioCtx, 'pc-preview-gate', {
                    numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [2]
                });
                gateNode.port.onmessage = onGateMessage;
                gateNode.connect(audioCtx.destination);
                gateState = 'ready';
                pushGateAll();
                rewirePreview();
            }).catch(function (e) {
                gateState = 'failed';
                reportGate('load_failed', String((e && e.message) || e).slice(0, 80));
                rewirePreview();
            });
        }
        function reportGate(what, msg) {
            if (window.__ae) __ae('feature_use', { tool: slug, feature: 'preview_gate', state: what, msg: msg || '' });
        }
        /* The source goes to the gate when it is there, and through the
           GainNode path while it loads or when it cannot be. Moving to the
           gate silences the GainNode path's own sounds (beep, loop). */
        function rewirePreview() {
            if (!srcNode || !audioCtx) return;
            try { srcNode.disconnect(); } catch (e) { /* noop */ }
            if (gateState === 'ready') {
                srcNode.connect(gateNode);
                muteBeep(); stopCustomSrc(); stopVoiceSrc();
                return;
            }
            ensureLegacyGraph();
            srcNode.connect(gainNode);
            if (settings.mode === 'custom' && mediaEl && !mediaEl.paused) ensureCustomSrc();
        }
        /* The modules are small; fetching them when the file is ready puts
           them in the HTTP cache, so the first ▶ finds the gate at once. */
        var gateWarmed = false;
        function warmGate() {
            if (gateWarmed || !cfg.gateWorkletUrl || !window.fetch) return;
            gateWarmed = true;
            [cfg.soundModuleUrl, cfg.gateWorkletUrl].filter(Boolean).forEach(function (u) {
                try { fetch(u, { credentials: 'same-origin' }).catch(function () { /* addModule will say */ }); } catch (e) { /* noop */ }
            });
        }
        function ensureLegacyGraph() {
            if (gainNode || !audioCtx) return;
            gainNode = audioCtx.createGain();
            gainNode.connect(audioCtx.destination);
            beepGain = audioCtx.createGain();
            beepGain.gain.value = 0;
            beepGain.connect(audioCtx.destination);
            beepOsc = audioCtx.createOscillator();
            beepOsc.frequency.value = settings.beepFreq;
            beepOsc.connect(beepGain);
            beepOsc.start();
        }
        function previewOn() { return $('pc-preview-toggle') ? $('pc-preview-toggle').checked : true; }
        function pushGateRegions() {
            if (!gateNode) return;
            postGate({ type: 'regions', list: regions.map(function (r) { return [r.start, r.end, r.peak || 0]; }) });
            // the «не голос» windows belong to the censors they were cut for
            if (Object.keys(underSent).length) { postGate({ type: 'under-clear' }); underSent = {}; }
        }
        function pushGateParams() {
            // frames: the export's `asetnsamples=128` at the source rate, counted
            // from its first sample's pts. That is 0 for an audio file (an MP3's
            // trimmed priming included — measured), for the underlay path and
            // the zero-copy video lane (both re-time to first_pts=0); a video
            // rendered the classic way counts from its sound's own start.
            var underlaid = settings.mode === 'custom' || voiceOnlyActive();
            var classicVideo = isVideoFile && !underlaid && (!!buildVideoFx() || !zeroCopyCapable());
            postGate({ type: 'params', mode: settings.mode, floor: settings.mode === 'duck' ? 0.88 : 1,
                fade: GATE_FADE, freq: settings.beepFreq, on: previewOn(),
                frameS: srcRate > 0 ? 128 / srcRate : 0, frameOrigin: classicVideo ? (srcStartS || 0) : 0 });
        }
        function pushGateCustom() {
            if (!gateNode) return;
            if (!customBuffer) { postGate({ type: 'custom', ch: null }); return; }
            var ch = [];
            for (var c = 0; c < customBuffer.numberOfChannels; c++) ch.push(customBuffer.getChannelData(c).slice());
            postGate({ type: 'custom', ch: ch, sr: customBuffer.sampleRate, vol: volGain(), loop: !!settings.customLoop },
                ch.map(function (a) { return a.buffer; }));
        }
        function pushGateAudition() {
            postGate(auditionRegion
                ? { type: 'audition', s: auditionRegion.start - AUDITION_PAD, e: auditionRegion.end + AUDITION_PAD }
                : { type: 'audition', s: null });
        }
        function pushGateAll() { pushGateParams(); pushGateRegions(); pushGateCustom(); pushGateAudition(); }

        /* ---- where the preview is, measured ---- */
        var TAP_S = 0.17, VERIFY_MS = 1500, HUNT_MS = 120, TAP_TIMEOUT_MS = 1200;
        function mapAt(f) {
            var m = sync.map;
            if (!m || !audioCtx) return null;
            return m.m0 + (f - m.f0) / audioCtx.sampleRate * m.rate;
        }
        function unlockSync(why) {
            sync.locked = false; sync.gen++; sync.tapGen = -1; sync.why = why;
            sync.unlockFrame = ctxFrameNow();
        }
        /* Until a tap is found, the element's own clock — good to a frame or
           two, and the first measurement replaces it within ~0.2 s. */
        function postProvisionalMap() {
            if (!audioCtx || !mediaEl) return;
            var rate = mediaEl.playbackRate || 1;
            sync.map = { f0: ctxFrameNow(), m0: mediaEl.currentTime, rate: rate, provisional: true };
            postGate({ type: 'map', f0: sync.map.f0, m0: sync.map.m0, rate: rate });
        }
        /* The gate's timeline is a COUNT: file time = m0 + frames × rate. It is
         * true only while the element is moving. Nothing used to withdraw it
         * when the element stopped, so the gate went on counting through the
         * pause — and when its count reached the next censor it laid the beep
         * or the replacement sound into the silence of a stopped player
         * («заглушка в рандомный момент включается во время паузы, пока я
         * мышкой по экрану вожу» — Ангелина, chat 640, 12.10.2026; measured:
         * paused at 2.21 s with a censor at 4.0 s, the sound came 1.66 s into
         * the pause). A stopped element has no timeline: pause, the end, a
         * seek made while paused and an emptied source all take it away, and
         * it comes back when the element is moving again. */
        function stopGateClock(why) {
            sync.map = null;
            unlockSync(why);
            postGate({ type: 'map', m0: null });
        }
        function startGateClock() {
            // moving = not paused, not at the end, not in the middle of a seek
            if (!mediaEl || mediaEl.paused || mediaEl.ended || mediaEl.seeking) return;
            if (!sync.locked) postProvisionalMap();
        }
        function maybeTap() {
            if (!gateNode || !pcm || !audioCtx) return;
            var now = performance.now();
            var due = sync.locked ? VERIFY_MS : HUNT_MS;
            if (sync.tapGen === sync.gen && now - sync.tapAt < TAP_TIMEOUT_MS) return;   // one in flight
            if (now - sync.tapAt < due) return;
            sync.tapGen = sync.gen; sync.tapAt = now;
            postGate({ type: 'tap', frames: Math.round(TAP_S * audioCtx.sampleRate) });
        }
        function onGateMessage(ev) {
            var d = ev.data || {};
            if (d.type !== 'tap') return;
            var gen = sync.tapGen;
            sync.tapGen = -1;
            // taken before a seek / stall / rate change: describes another moment
            if (gen !== sync.gen || d.f0 < sync.unlockFrame) return;
            if (!pcm || !mediaEl || mediaEl.paused || !window.ProfanityPreviewSync) return;
            var rate = mediaEl.playbackRate || 1;
            var expect = mapAt(d.f0);
            if (expect === null) return;
            var res = ProfanityPreviewSync.measure(d.data, d.sr, pcm, expect, rate);
            if (!res) { sync.fails++; maybeReportNoSync(); return; }
            var errS = res.startS - expect, wasLocked = sync.locked;
            sync.hist.push({ f0: d.f0, err: Math.round(errS * 100000) / 100, ncc: Math.round(res.ncc * 1000) / 1000, locked: wasLocked ? 1 : 0 });
            if (sync.hist.length > 40) sync.hist.shift();
            if (!wasLocked && sync.map && sync.map.provisional && rate === 1) {
                // how far the element's clock is from the graph — the old
                // preview's error on this device is computed from it
                sync.lagS.push(errS);
                if (sync.lagS.length > 9) sync.lagS.shift();
            }
            if (!wasLocked || Math.abs(errS) > 0.002) {
                sync.map = { f0: d.f0, m0: res.startS, rate: rate, provisional: false };
                postGate({ type: 'map', f0: d.f0, m0: res.startS, rate: rate });
            }
            sync.locked = true;
            if (!wasLocked) onFirstLock(errS, res);
        }
        function syncLagS() {
            if (!sync.lagS.length) return 0;
            var a = sync.lagS.slice().sort(function (x, y) { return x - y; });
            return a[a.length >> 1];
        }
        function onFirstLock(errS, res) {
            if (!sync.reported && window.__ae) {
                sync.reported = true;
                var lat = audioCtx ? (audioCtx.outputLatency || audioCtx.baseLatency || 0) : 0;
                var legacy = ProfanityPreviewSync.legacyShift(lat, syncLagS());
                __ae('feature_use', {
                    tool: slug, feature: 'preview_sync', err_ms: Math.round(errS * 1000),
                    out_lat_ms: Math.round(lat * 1000), legacy_bias_ms: Math.round(legacy.bias * 1000),
                    ncc: Math.round(res.ncc * 100) / 100, rate: (mediaEl && mediaEl.playbackRate) || 1,
                    sr: audioCtx ? audioCtx.sampleRate : 0, fails: sync.fails, why: sync.why
                });
            }
            maybeOfferLegacyShift();
        }
        /* Censors placed BY EAR against the old preview were placed where IT
         * was heard — (bias) seconds late in the file. Now that the preview is
         * measured, the bias of that old one ON THIS DEVICE is known too
         * (ProfanityPreviewSync.legacyShift: its lookahead minus the element's
         * measured lag), and the person can have their work where they heard
         * it. Only hand edits without the PREVIEW_VERSION stamp move; the
         * detector's own bounds were never heard-and-placed. One undo step. */
        var legacyNote = null;
        function legacyItems() {
            var out = [];
            Object.keys(edits).forEach(function (k) {
                var e = edits[k];
                if (e && !e.pv && itemById('w' + k)) out.push({ kind: 'auto', key: k });
            });
            manualRegions.forEach(function (r) { if (!r.pv) out.push({ kind: 'manual', id: r.id }); });
            return out;
        }
        function maybeOfferLegacyShift() {
            if (sync.legacyAsked || !sync.lagS.length || !audioCtx || legacyNote) return;
            var list = legacyItems();
            if (!list.length) return;
            var lat = audioCtx.outputLatency || audioCtx.baseLatency || 0;
            var shift = ProfanityPreviewSync.legacyShift(lat, syncLagS());
            sync.legacyAsked = true;
            if (shift.bias < 0.015) return;      // under a sixtieth of a second: nothing anyone heard
            var ms = Math.round(shift.bias * 1000);
            var host = $('pc-results');
            if (!host) return;
            var b = document.createElement('div');
            b.id = 'pc-legacy-note';
            b.className = 'pc-restored pc-legacy';
            b.setAttribute('role', 'status');
            var txt = document.createElement('span');
            txt.textContent = (i18n.legacy_shift_text || 'Your :n edits were made while the preview on this device muted :ms ms ahead of the sound, so in the file they land later than you heard them. The preview now matches the file.')
                .replace(':n', String(list.length)).replace(':ms', String(ms));
            var go = document.createElement('button');
            go.type = 'button'; go.className = 'pc-mini-btn pc-legacy-go';
            go.textContent = (i18n.legacy_shift_apply || 'Move my edits :ms ms earlier').replace(':ms', String(ms));
            var keep = document.createElement('button');
            keep.type = 'button'; keep.className = 'pc-mini-btn';
            keep.textContent = i18n.legacy_shift_keep || 'Keep as they are';
            function done(action, n) {
                b.remove(); legacyNote = null;
                if (window.__ae) __ae('feature_use', { tool: slug, feature: 'legacy_shift', action: action, n: n, bias_ms: ms,
                    out_lat_ms: Math.round(lat * 1000), lag_ms: Math.round(syncLagS() * 1000) });
            }
            go.addEventListener('click', function () {
                var n = applyLegacyShift(shift);
                done('apply', n);
                window.__toast((i18n.legacy_shift_done || 'Moved :n edits. Undo: Ctrl+Z.').replace(':n', String(n)), 'success');
            });
            keep.addEventListener('click', function () {
                // their call: these stand as they are and are not asked about again
                legacyItems().forEach(function (x) {
                    if (x.kind === 'auto') edits[x.key].pv = PREVIEW_VERSION;
                    else manualRegions.forEach(function (r) { if (r.id === x.id) r.pv = PREVIEW_VERSION; });
                });
                scheduleSessionSave();
                done('keep', list.length);
            });
            b.appendChild(txt); b.appendChild(go); b.appendChild(keep);
            host.insertBefore(b, host.firstChild);
            legacyNote = b;
            if (window.__ae) __ae('feature_use', { tool: slug, feature: 'legacy_shift', action: 'offered', n: list.length, bias_ms: ms });
        }
        function applyLegacyShift(shift) {
            var list = legacyItems();
            if (!list.length) return 0;
            pushHistory();
            function moved(s, e) {
                var ns = Math.max(0, Math.min(duration, s + shift.start));
                var ne = Math.max(0, Math.min(duration, e + shift.end));
                if (ne - ns < MIN_ITEM_S) ne = Math.min(duration, ns + MIN_ITEM_S);
                return [ns, ne];
            }
            list.forEach(function (x) {
                if (x.kind === 'auto') {
                    var e = edits[x.key], m = moved(e.start, e.end);
                    edits[x.key] = { start: m[0], end: m[1], pv: PREVIEW_VERSION };
                } else {
                    manualRegions.forEach(function (r) {
                        if (r.id !== x.id) return;
                        var m2 = moved(r.start, r.end);
                        r.start = m2[0]; r.end = m2[1]; r.pv = PREVIEW_VERSION;
                    });
                }
            });
            refreshAll();
            return list.length;
        }

        var noSyncReported = false;
        function maybeReportNoSync() {
            if (noSyncReported || sync.locked || sync.fails < 40) return;
            noSyncReported = true;
            if (window.__ae) __ae('feature_use', { tool: slug, feature: 'preview_sync_none', fails: sync.fails });
        }
        /* «не голос» windows for the censors just ahead, handed to the gate once
           each (it mixes them under g exactly as the export's underlay does). */
        function provideUnder(t) {
            if (!gateNode || !voiceOnlyActive() || !voiceLane || !voiceLane.previewAt) return;
            for (var i = 0; i < regions.length; i++) {
                var r = regions[i];
                if (r.end + GATE_FADE < t) continue;
                if (r.start - GATE_FADE > t + 2) break;
                var p = null;
                try { p = voiceLane.previewAt(r.start); } catch (e) { p = null; }
                if (!p || underSent[p.id]) continue;
                underSent[p.id] = true;
                var ch = [];
                for (var c = 0; c < p.buffer.numberOfChannels; c++) ch.push(p.buffer.getChannelData(c).slice());
                postGate({ type: 'under', id: p.id, t0: p.t0, sr: p.buffer.sampleRate, ch: ch }, ch.map(function (a) { return a.buffer; }));
            }
        }

        function setPlayIcon(playing) {
            var p = $('pc-play-icon'), pa = $('pc-pause-icon'), btn = $('pc-play');
            if (p) p.style.display = playing ? 'none' : '';
            if (pa) pa.style.display = playing ? '' : 'none';
            if (btn) btn.setAttribute('aria-label', playing ? (i18n.pause || 'Pause') : (i18n.play || 'Play'));
        }
        function togglePlay() {
            if (!mediaEl) return;
            if (mediaEl.paused) mediaEl.play().catch(function () { /* user gesture rules */ });
            else mediaEl.pause();
        }

        // custom replacement sound rides a loop=true buffer source gated by
        // beepGain — same envelope as the oscillator, so region enter/exit
        // logic in tick() needs no special-casing
        function ensureCustomSrc() {
            if (!audioCtx || !customBuffer || customSrc) return;
            try {
                customSrc = audioCtx.createBufferSource();
                customSrc.buffer = customBuffer;
                customSrc.loop = true;
                customSrc.connect(beepGain);
                customSrc.start();
            } catch (e) { customSrc = null; }
        }
        function stopCustomSrc() {
            if (customSrc) { try { customSrc.stop(); customSrc.disconnect(); } catch (e) { /* noop */ } customSrc = null; }
        }

        function muteBeep() { if (beepGain && audioCtx) beepGain.gain.setTargetAtTime(0, audioCtx.currentTime, 0.005); }

        function regionAt(t) {
            var lo = 0, hi = regions.length - 1;
            while (lo <= hi) {
                var mid = (lo + hi) >> 1;
                if (t < regions[mid].start) hi = mid - 1;
                else if (t > regions[mid].end) lo = mid + 1;
                else return regions[mid];
            }
            return null;
        }
        function inRegion(t) { return !!regionAt(t); }

        // Live preview of the video censor-cue (Oleg 14.06): the export effect
        // was invisible until download — "зачем настройки, если ничего не
        // меняют". This mirrors the FFmpeg effect with cheap CSS on the
        // <video> element, toggled in sync with the same censored regions, so
        // the user SEES darken/shake/float/border while scrubbing. Pure
        // preview — the real pixel-accurate effect is still the FFmpeg pass.
        // File-adaptive UI (Oleg 15.06): reshape the in-tool controls to the
        // ACTUAL uploaded file, not the page slug. Both /profanity-remover and
        // /profanity-remover-video render both control blocks; this shows the
        // video-cue block for a video file and the audio-export (format/bitrate)
        // block for an audio file — so the experience is correct whichever page
        // you landed on, and dropping a video on the audio page still unlocks
        // the visual cue. Called after isVideoFile is classified; reset on New
        // file. (The two pages stay separate URLs purely for their distinct
        // SEO intent — invisible to this runtime toggle.)
        function applyMediaMode() {
            // No file (idle / after "New file") → hide BOTH; only reveal the
            // medium-specific block once a file is actually classified.
            var hasFile = !!file;
            var vfx = $('pc-videofx-block');
            var aud = $('pc-audio-export-block');
            if (vfx) vfx.hidden = !(hasFile && isVideoFile);
            if (aud) aud.hidden = !(hasFile && !isVideoFile);
            var wavBtn = $('pc-export-wav');
            if (wavBtn) wavBtn.hidden = !(hasFile && isVideoFile);
            updateMediaTabs();
        }

        // Media-tab active highlight (Oleg 15.06): follows the PAGE default
        // before upload, the ACTUAL FILE after. Visual only — never changes
        // the URL. cfg.isVideoTool is the page's own medium (slug).
        function updateMediaTabs() {
            var tabs = $('pc-mediatabs');
            if (!tabs) return;
            var activeMedia = file ? (isVideoFile ? 'video' : 'audio')
                                   : (cfg.isVideoTool ? 'video' : 'audio');
            var list = tabs.querySelectorAll('[data-media]');
            for (var i = 0; i < list.length; i++) {
                var on = list[i].getAttribute('data-media') === activeMedia;
                list[i].classList.toggle('active', on);
                list[i].setAttribute('aria-selected', on ? 'true' : 'false');
            }
        }

        var FX_CLASSES = ['pc-fx-darken', 'pc-fx-shake', 'pc-fx-float', 'pc-fx-border'];
        function applyVideoCue(t, censored) {
            if (!isVideoFile) return;
            var holder = $('pc-video-holder');
            if (!holder) return;
            var fx = settings.videoFx;
            var on = censored && fx && fx !== 'none';
            for (var i = 0; i < FX_CLASSES.length; i++) {
                var want = on && FX_CLASSES[i] === 'pc-fx-' + fx;
                holder.classList.toggle(FX_CLASSES[i], want);
            }
        }

        var AUDITION_PAD = 0.12;        // lead-in/out so the word is fully heard

        /* The GainNode fallback only (no AudioWorklet): how far ahead of
         * `currentTime` its once-a-frame decision must look so the ramp lands
         * on the word — half a frame plus the 6 ms ramp reaching −20 dB.
         *
         * ⚠️ NOT the output latency. Until 09.10.2026 this returned
         * outputLatency + 29 ms, on the reasoning that «the samples heard now
         * left the graph outputLatency ago». True, and irrelevant: the gain
         * acts on the samples in the graph, and those and their silence reach
         * the ear together. The 07.08 stand added outputLatency to an
         * in-graph measurement and so «found» a lag that was not there; the
         * correction made the preview mute early by the device latency
         * (desktop ~56 ms, headsets ~110 ms — chat 640). */
        function previewLookahead() {
            // test override (seconds) — the stand reproduces the old sum with it
            if (typeof window.__pcPreviewLookaheadS === 'number') return window.__pcPreviewLookaheadS;
            var rate = (mediaEl && mediaEl.playbackRate) || 1;
            return 0.014 * rate;
        }

        function tick() {
            if (!mediaEl || mediaEl.paused) return;
            var t = mediaEl.currentTime;
            // auditioning a single hit: play it uncensored, then stop just past
            // its end so it's a one-tap "hear the original" — independent of the
            // global preview toggle.
            if (stopAt !== null && t > stopAt) {
                stopAt = null;
                mediaEl.pause();
                return;
            }
            if (auditionRegion) {
                if (t > auditionRegion.end + AUDITION_PAD) {
                    auditionRegion = null;
                    pushGateAudition();
                    mediaEl.pause();   // pause handler cancels the raf + resets icons
                    return;
                }
            }
            var previewIsOn = previewOn();
            if (gateState === 'ready') {
                // the gate censors the samples themselves; this frame only keeps
                // its timeline measured and its «не голос» windows supplied
                startGateClock();
                maybeTap();
                provideUnder(t);
                var inAud = auditionRegion && t >= auditionRegion.start - AUDITION_PAD && t <= auditionRegion.end + AUDITION_PAD;
                applyVideoCue(t, previewIsOn && inRegion(t) && !inAud);
                updatePlayhead();
                rafId = requestAnimationFrame(tick);
                return;
            }
            // audio decisions run on the FUTURE position (see previewLookahead);
            // on-screen cues stay on the real one, so the picture matches what
            // the eye sees rather than what the speaker is about to play.
            var tAudio = t + previewLookahead();
            var inAudition = auditionRegion &&
                tAudio >= auditionRegion.start - AUDITION_PAD && tAudio <= auditionRegion.end + AUDITION_PAD;
            var censored = previewIsOn && inRegion(tAudio) && !inAudition;
            var inAuditionNow = auditionRegion &&
                t >= auditionRegion.start - AUDITION_PAD && t <= auditionRegion.end + AUDITION_PAD;
            applyVideoCue(t, previewIsOn && inRegion(t) && !inAuditionNow);
            if (gainNode && audioCtx) {
                var now = audioCtx.currentTime;
                var mode = settings.mode;
                var target = censored ? (mode === 'duck' ? 0.12 : 0) : 1;
                gainNode.gain.setTargetAtTime(target, now, 0.006);
                if (beepGain) {
                    var beepTarget = 0;
                    if (censored && mode === 'beep' && beepOsc) {
                        beepOsc.frequency.value = settings.beepFreq;
                        var rNow = regionAt(tAudio);
                        beepTarget = rNow ? rNow.peak : typicalBeepPeak();
                    } else if (censored && mode === 'custom' && customSrc) {
                        beepTarget = volGain();
                    }
                    beepGain.gain.setTargetAtTime(beepTarget, now, 0.006);
                }
                voicePreview(censored, tAudio, now);
            }
            updatePlayhead();
            rafId = requestAnimationFrame(tick);
        }

        /* «Только голос» в предпросмотре — то же, что ляжет в файл: под
         * заглушкой играет «не голос» из лейна, с громкостью, дополняющей
         * глушение оригинала (оба узла идут к цели с одной постоянной времени,
         * поэтому их сумма остаётся единицей — кроссфейд, а не провал).
         * Заглушка, для которой разделение ещё не готово, звучит как обычная. */
        var voiceSrc = null, voiceSrcId = '', voiceGain = null;
        function stopVoiceSrc() {
            if (voiceSrc) { try { voiceSrc.stop(); voiceSrc.disconnect(); } catch (e) { /* noop */ } }
            voiceSrc = null; voiceSrcId = '';
            if (voiceGain && audioCtx) voiceGain.gain.setTargetAtTime(0, audioCtx.currentTime, 0.006);
        }
        function voicePreview(censored, tAudio, now) {
            var p = null;
            try { p = (censored && voiceOnlyActive()) ? voiceLane.previewAt(tAudio) : null; } catch (e) { p = null; }
            if (!voiceGain) {
                if (!p) return;
                voiceGain = audioCtx.createGain();
                voiceGain.gain.value = 0;
                voiceGain.connect(audioCtx.destination);
            }
            if (p && p.id !== voiceSrcId) {
                if (voiceSrc) { try { voiceSrc.stop(); voiceSrc.disconnect(); } catch (e) { /* noop */ } }
                voiceSrc = audioCtx.createBufferSource();
                voiceSrc.buffer = p.buffer;
                voiceSrc.playbackRate.value = (mediaEl && mediaEl.playbackRate) || 1;
                voiceSrc.connect(voiceGain);
                voiceSrc.start(0, Math.max(0, Math.min(p.buffer.duration, tAudio - p.t0)));
                voiceSrcId = p.id;
            }
            voiceGain.gain.setTargetAtTime(p ? (settings.mode === 'duck' ? 0.88 : 1) : 0, now, 0.006);
        }

        function seekTo(t, opts) {
            if (!mediaEl) return;
            if (!(opts && opts.keepAudition)) { auditionRegion = null; pushGateAudition(); } // manual jump cancels an audition
            stopAt = null;
            followHold = false;          // a seek is the person choosing where to look
            mediaEl.currentTime = Math.max(0, Math.min(duration, t));
            // в увеличении перемотка обязана ПРИВЕСТИ окно к месту: иначе
            // «прослушать с этого места» играет за краем видимого куска и
            // дорожка показывает не то, что звучит
            if (zoom > 1) {
                var wrap = $('pc-wave-wrap');
                if (wrap && waveW) {
                    var x = mediaEl.currentTime / duration * waveW;
                    if (x < wrap.scrollLeft + 20 || x > wrap.scrollLeft + wrap.clientWidth - 20) revealTime(mediaEl.currentTime);
                }
            }
            updatePlayhead();
            if (!(opts && opts.keepPaused) && mediaEl.paused) {
                mediaEl.play().catch(function () { /* user gesture rules */ });
            }
        }

        // One-tap "hear the original of this hit": jump just before it, play it
        // with the censor bypassed, auto-stop right after. Lets the user verify
        // a detection (correct word? false positive to remove?) without touching
        // the global preview toggle. (Oleg 20.06)
        function auditionHit(start, end) {
            if (!mediaEl || !duration) return;
            seekTo(Math.max(0, start - AUDITION_PAD), { keepPaused: true, keepAudition: true });
            auditionRegion = { start: start, end: end };
            pushGateAudition();
            if (mediaEl.paused) mediaEl.play().catch(function () { /* user gesture rules */ });
            if (window.__ae) __ae('feature_use', { tool: slug, feature: 'audition_original' });
        }

        /* ---------------- custom censor sound ---------------- */
        async function loadCustomSound(f) {
            if (!f) return;
            if (f.size > CUSTOM_MAX_MB * 1048576) {
                window.__toast((i18n.custom_too_big || 'Too big').replace(':mb', String(CUSTOM_MAX_MB)), 'error');
                return;
            }
            try {
                var buf = await f.arrayBuffer();
                var ac = new (window.AudioContext || window.webkitAudioContext)();
                var dec = await ac.decodeAudioData(buf);
                ac.close();
                if (dec.duration > CUSTOM_MAX_S) {
                    // keep the first CUSTOM_MAX_S seconds — export trims the same way
                    var len = Math.floor(CUSTOM_MAX_S * dec.sampleRate);
                    var trimmed = new AudioBuffer({ length: len, numberOfChannels: dec.numberOfChannels, sampleRate: dec.sampleRate });
                    for (var ch = 0; ch < dec.numberOfChannels; ch++) {
                        trimmed.copyToChannel(dec.getChannelData(ch).subarray(0, len), ch);
                    }
                    dec = trimmed;
                }
                customFile = f;
                customBuffer = dec;
                customDur = dec.duration;
                customResampled = null;
                var nameEl = $('pc-custom-name');
                if (nameEl) nameEl.textContent = f.name + ' · ' + fmtShortDur(customDur, i18n.sec_suffix);
                stopCustomSrc();
                if (gateState !== 'ready' && mediaEl && !mediaEl.paused && settings.mode === 'custom') ensureCustomSrc();
                pushGateCustom();
                if (window.__ae) __ae('feature_use', { tool: slug, feature: 'custom_sound', durMs: Math.round(customDur * 1000) });
            } catch (e) {
                window.__toast(i18n.custom_decode_err || 'Cannot decode', 'error');
            }
        }

        /* ---------------- censor-sound preview button ---------------- */
        var soundPreviewCtx = null;
        function previewCensorSound() {
            try {
                if (!soundPreviewCtx) soundPreviewCtx = new (window.AudioContext || window.webkitAudioContext)();
                var ctx = soundPreviewCtx;
                if (ctx.state === 'suspended') ctx.resume();
                var g = ctx.createGain();
                g.connect(ctx.destination);
                var now = ctx.currentTime, dur = 0.8;
                if (settings.mode === 'beep') {
                    var osc = ctx.createOscillator();
                    osc.frequency.value = settings.beepFreq;
                    osc.connect(g);
                    g.gain.setValueAtTime(0, now);
                    var pk = typicalBeepPeak();
                    g.gain.linearRampToValueAtTime(pk, now + 0.02);
                    g.gain.setValueAtTime(pk, now + dur - 0.04);
                    g.gain.linearRampToValueAtTime(0, now + dur);
                    osc.start(now); osc.stop(now + dur);
                } else if (settings.mode === 'custom' && customBuffer) {
                    // as one censor of a typical word plays it: once (or
                    // repeated, if asked) over 0.8 s — not looped regardless,
                    // which made a single leaf rustle sound like three
                    var src = ctx.createBufferSource();
                    src.buffer = customBuffer;
                    src.loop = !!settings.customLoop;
                    src.connect(g);
                    var len = settings.customLoop ? dur : Math.min(dur, customBuffer.duration);
                    g.gain.setValueAtTime(0, now);
                    g.gain.linearRampToValueAtTime(volGain(), now + SND.REPLACEMENT_FADE_IN);
                    g.gain.setValueAtTime(volGain(), Math.max(now + SND.REPLACEMENT_FADE_IN, now + len - SND.REPLACEMENT_FADE_OUT));
                    g.gain.linearRampToValueAtTime(0, now + len);
                    src.start(now); src.stop(now + len);
                } else if (settings.mode === 'custom') {
                    window.__toast(i18n.custom_missing || 'Pick a sound first', 'warning');
                }
                // silence/duck: nothing to audition
            } catch (e) { /* noop */ }
        }

        /* ---------------- export ---------------- */
        /* Every mode renders through ONE graph (buildGateGraph): the original
         * gated by the trapezoid `volume`, and — for the replacement sound and
         * «только голос» — an underlay mixed in. Until 09.10.2026 the
         * replacement sound had its own per-region atrim/concat graph,
         * superlinear in the censor count, and so a ceiling of 150 censors
         * that joined everything closer than ~3 s (chat 640: 414 → 141,
         * «Заглушено» 110 → 444 s) and looped the sound to fill each one. */
        function buildFilterGraph(layout, withUnderlay) {
            return buildGateGraph(layout, withUnderlay);
        }

        /* The replacement sound as an underlay: the user's sound laid at the
         * START of every censor in ProfanitySound's envelope (once, or
         * repeated when asked), zeros everywhere else — built like the
         * «только голос» underlay (ProfanityVoiceUnderlay.buildWav: one zero
         * block by reference), so a long file costs memory only where the
         * sound plays. The preview's gate plays the same shape (customAt). */
        async function customUnderlay() {
            var U = window.ProfanityVoiceUnderlay;
            if (!U || !customBuffer) throw new Error('CUSTOM_UNDERLAY_UNAVAILABLE');
            var ch = channels === 1 ? 1 : 2, sr = srcRate;
            if (!customResampled || customResampled.sr !== sr || customResampled.ch !== ch) {
                var len = Math.max(1, Math.ceil(customBuffer.duration * sr));
                var oc = new OfflineAudioContext(ch, len, sr);
                var bs = oc.createBufferSource();
                bs.buffer = customBuffer;
                bs.connect(oc.destination);
                bs.start(0);
                var rendered = await oc.startRendering();
                var data = [];
                for (var c = 0; c < ch; c++) data.push(rendered.getChannelData(c));
                customResampled = { sr: sr, ch: ch, data: data, len: len };
            }
            var cr = customResampled, soundS = cr.len / sr, vol = volGain(), loop = !!settings.customLoop;
            var total = Math.ceil((duration + 2) * sr), patches = [];
            for (var k = 0; k < regions.length; k++) {
                var r = regions[k], regionS = r.end - r.start;
                var stop = loop ? regionS : Math.min(regionS, soundS);
                var i0 = Math.round(r.start * sr);
                var nextI0 = k + 1 < regions.length ? Math.round(regions[k + 1].start * sr) : total;
                var n = Math.min(Math.ceil(stop * sr), nextI0 - i0, total - i0);
                if (n <= 0) continue;
                var pd = new Int16Array(n * ch);
                for (var j = 0; j < n; j++) {
                    var d = j / sr;
                    var env = SND.replacementEnvelope(d, regionS, soundS, loop);
                    if (env <= 0) continue;
                    var idx = Math.floor(SND.replacementOffset(d, soundS, loop) * sr);
                    if (idx < 0 || idx >= cr.len) continue;
                    for (var c2 = 0; c2 < ch; c2++) {
                        var x = cr.data[c2][idx] * vol * env;
                        x = x > 1 ? 1 : x < -1 ? -1 : x;
                        pd[j * ch + c2] = Math.round(x * 32767);
                    }
                }
                patches.push({ i0: i0, data: pd });
            }
            return U.buildWav(sr, ch, total, patches);
        }

        // beep / silence / duck: a 3-chain timeline-gate graph, flat in region
        // count. The source plays through ONE volume filter whose expression
        // ramps to the censored level over GATE_FADE s at every region edge
        // (trapezoid per region; asetnsamples=128 gives the ramp ~2.9 ms
        // resolution — measured boundary step ≈ old graph's faded cut). The
        // beep is a full-length sine hard-gated by between() terms and mixed
        // in with amix (normalize=0: kept speech must stay at unity).
        // Measured 2026-08-06 vs the atrim/concat graph on a 300 s file:
        // 500 regions 281 s -> 300 regions 11.4 s, 1672 regions 39.6 s, and
        // the export is FASTER than the old graph even at 1 region.
        var GATE_FADE = 0.03;
        /* The beep's level in the file: the SAME per-region peak the preview
         * plays, converted for FFmpeg's 1/8-amplitude sine. One level for all
         * regions → one gated term; otherwise a sum of gated levels (regions
         * never overlap after layout, so at most one term is non-zero at t). */
        function beepGainExpr() {
            var g = regions.map(function (r) { return SND.ffmpegSineGain(r.peak).toFixed(4); });
            var same = g.every(function (x) { return x === g[0]; });
            if (same) {
                return 'if(' + regions.map(function (r) {
                    return 'between(t,' + r.start.toFixed(4) + ',' + r.end.toFixed(4) + ')';
                }).join('+') + ',' + (g[0] || '0') + ',0)';
            }
            return regions.map(function (r, i) {
                return g[i] + '*between(t,' + r.start.toFixed(4) + ',' + r.end.toFixed(4) + ')';
            }).join('+');
        }
        /* withUnderlay — «глушить только голос»: второй вход ([1:a]) — подложка
         * из vocal-only-export.js / voice-underlay.js, точное дополнение гейта
         * (g × «не голос», где g — то же выражение на тех же кадрах по 128).
         * `amix` складывает входы ПО НОМЕРУ СЭМПЛА, а не по времени (замер
         * 24.09, FFmpeg 7.0: у файла, чей звук начинается на 0.5 с позже,
         * заплатка без выравнивания съезжала на 0.5 с), поэтому оригинал
         * выравнивается тем же `aresample=async=1:first_pts=0`, что и звук для
         * распознавания: сэмпл i подложки = сэмпл i оригинала = мгновение, в
         * котором распознавание нашло слово. Гейт от этого не сдвигается: он
         * считает от меток времени, а они те же. */
        function buildGateGraph(layout, withUnderlay) {
            var gate = regions.map(function (r) {
                return 'between(t,' + r.start.toFixed(4) + ',' + r.end.toFixed(4) + ')';
            }).join('+');
            var ramp = regions.map(function (r) {
                return 'min(max((t-' + Math.max(0, r.start - GATE_FADE).toFixed(4) + ')/' + GATE_FADE +
                    ',0),1)*min(max((' + (r.end + GATE_FADE).toFixed(4) + '-t)/' + GATE_FADE + ',0),1)';
            }).join('+');
            // duck keeps the speech at 0.12 inside regions; beep/silence go to 0
            var floor = settings.mode === 'duck' ? '0.88' : '1';
            var keepVol = "'1-" + floor + "*min(" + ramp + ",1)'";
            var fmt = 'aformat=sample_rates=' + srcRate + ':channel_layouts=' + layout;
            var align = withUnderlay ? 'aresample=async=1:first_pts=0,' : '';
            var base = '[0:a]' + align + fmt + ',asetnsamples=128,volume=volume=' + keepVol + ':eval=frame';
            var under = withUnderlay ? '[1:a]' + fmt + '[under];' : '';
            var inputs = withUnderlay ? '[base][under]' : '[base]';
            var n = withUnderlay ? 2 : 1;
            if (settings.mode !== 'beep') {
                if (!withUnderlay) return base + '[outa]';
                return base + '[base];' + under + inputs + 'amix=inputs=' + n + ':duration=first:normalize=0[outa]';
            }
            // The tone is mono. `aformat` to stereo would rematrix it at the
            // centre-mix level (0.707, −3 dB per channel) — measured 24.09 on
            // the exported file: −29.6 dBFS against −26.6 in the preview, whose
            // Web Audio upmix COPIES a mono node into both channels. `pan`
            // copies too, so the file and the preview carry the same peak.
            var toLayout = layout === 'stereo' ? 'pan=stereo|c0=c0|c1=c0,' : '';
            return base + '[base];' + under +
                'sine=frequency=' + settings.beepFreq + ':sample_rate=' + srcRate + ',' + toLayout + fmt +
                ',volume=volume=\'' + beepGainExpr() + '\':eval=frame[beep];' +
                inputs + '[beep]amix=inputs=' + (n + 1) + ':duration=first:normalize=0[outa]';
        }

        // Video censor-cue (Oleg 14.06): a visual marker on the picture
        // synced to the censored audio regions — the differentiator no other
        // censor has. Returns { chain, label } to splice into filter_complex,
        // or null when not applicable (audio file / fx=none / no regions).
        // ALWAYS forces a video re-encode (we're filtering [0:v]); the caller
        // swaps -c:v copy for libx264 when this is active.
        function buildVideoFx() {
            if (!isVideoFile || settings.videoFx === 'none' || !regions.length) return null;
            var FXSET = { darken: 1, shake: 1, float: 1, border: 1 };
            if (!FXSET[settings.videoFx]) return null;
            // enable / amplitude-gate expression: 1 inside any censored region,
            // 0 elsewhere. Sum of between() is safe — regions never overlap
            // after merge, so at most one term is 1 at any t.
            var terms = regions.map(function (r) {
                return 'between(t,' + r.start.toFixed(3) + ',' + r.end.toFixed(3) + ')';
            });
            var gate = terms.join('+');
            var fx = settings.videoFx;
            if (fx === 'darken') {
                // brightness dip; eq honours the enable timeline.
                return { chain: '[0:v]eq=brightness=-0.30:enable=\'' + gate + '\'[outv]', label: '[outv]' };
            }
            if (fx === 'border') {
                // thick pulsing red border only during censored spans.
                return { chain: '[0:v]drawbox=x=0:y=0:w=iw:h=ih:color=red@0.85:t=max(8\\,ih*0.025):enable=\'' + gate + '\'[outv]', label: '[outv]' };
            }
            if (fx === 'float' || fx === 'shake') {
                // Both ride a constant inset crop (even dims + wobble margin).
                // float = smooth low-freq sine drift ("плавание") for mat used
                //   as filler / semi-romantic — Oleg 14.06.
                // shake = sharp square-wave jolts ("как бармен трясёт шейкер",
                //   2-4 crisp left-rights per short censored word). sign(sin)
                //   snaps instantly between -A and +A → crisp, not smooth; at
                //   ~7 Hz a 0.3-0.6 s word gets 2-4 jolts, matching his ask.
                //   Mostly horizontal ("влево-вправо") with a tiny vertical
                //   jitter so it doesn't look like a 1-D slide.
                var M = 12;
                var xExpr, yExpr;
                if (fx === 'float') {
                    var Af = 10;
                    xExpr = M + '+' + Af + '*sin(2*PI*2.2*t)*(' + gate + ')';
                    yExpr = M + '+' + Af + '*sin(2*PI*2.7*t+1.3)*(' + gate + ')';
                } else {
                    // ffmpeg's expr eval has no sign(); (2*gt(sin,0)-1) is the
                    // square wave: +1 while sin>0, -1 otherwise → instant crisp
                    // left/right snap (the "shaker" feel), not a smooth slide.
                    var As = 8, Ay = 3;
                    xExpr = M + '+' + As + '*(2*gt(sin(2*PI*7*t),0)-1)*(' + gate + ')';
                    yExpr = M + '+' + Ay + '*(2*gt(sin(2*PI*9*t+1),0)-1)*(' + gate + ')';
                }
                return {
                    chain: '[0:v]crop=w=iw-' + (2 * M) + ':h=ih-' + (2 * M) +
                        ':x=\'' + xExpr + '\':y=\'' + yExpr + '\'[outv]',
                    label: '[outv]'
                };
            }
            return null;
        }

        /* ── Zero-copy video export (29.09.2026) ─────────────────────────────
         * The tool changes ONLY the soundtrack: FFmpeg renders the censored
         * soundtrack alone (the same graph, AAC) and SoundtrackSplice puts it
         * into the ORIGINAL mp4/mov — the picture stays file.slice() references.
         * The classic `-c:v copy` export built its whole output in MEMFS (the
         * 1600 MiB wall): Premium chats 657/659, 3.0 and 2.5 GB phone videos
         * refused at «максимум 1024 МБ». The alignment, the lead and the `.mp4`
         * name are the primitive's (public/js/soundtrack-splice.js); anything the
         * splice refuses goes down the classic export where its output fits. */
        function zeroCopyCapable() {
            return !!(isVideoFile && window.SoundtrackSplice && SoundtrackSplice.capable(file));
        }
        function soundtrackGraph(graph) {
            return SoundtrackSplice.alignGraph(graph, { rate: srcRate, startSec: srcStartS });
        }

        /* Everything the zero-copy lane does not take — another container, or
         * the on-frame marker (a filtered picture is a new picture) — goes the
         * classic way and builds its WHOLE output in MEMFS: the 1600 MiB wall.
         * Priced before any work, with the lever that actually helps; '' = it
         * fits. The zone accepts video far past the wall now, so without this
         * the refusal would come after recognition, the separator and the
         * encode (the «поздняя стена» class). */
        /* The classic export's ceiling is the shared one (SoundtrackSplice.
           classicCeiling): the MEMFS wall, but never past what the page's 'video'
           kind promised before the zero-copy lane — a 1.5 GB copy on a 4 GB phone
           lives three times over (MEMFS, readFile, Blob). */
        function copyWallBytes() { return window.SoundtrackSplice ? SoundtrackSplice.classicCeiling() : Math.min(+window.__WASM_OUTPUT_MAX || 1600 * 1048576, 1073741824); }
        function fmtSize(b) {
            var gb = b / 1073741824, L = window.__LOCALE || 'en';
            if (gb < 1) return Math.round(b / 1048576) + ' ' + (i18n.unit_mb || 'MB');
            var n;
            try { n = gb.toLocaleString(L, { maximumFractionDigits: 1 }); } catch (e) { n = gb.toFixed(1); }
            return n + ' ' + (i18n.unit_gb || 'GB');
        }
        function copyWallRefusal(vfx, src) {
            if (!isVideoFile || !file) return '';
            if (!vfx && zeroCopyCapable()) return '';
            var wall = copyWallBytes(), outBytes = file.size;
            if (vfx && window.FFmpegMemoryGuard && FFmpegMemoryGuard.checkVideoEncode && mediaEl && mediaEl.videoWidth > 0) {
                var g = FFmpegMemoryGuard.checkVideoEncode({ durationS: duration, width: mediaEl.videoWidth, height: mediaEl.videoHeight, fps: 30, crf: 22, inputBytes: file.size });
                if (!g.safe) outBytes = Infinity;
                else if (g.outMB) outBytes = g.outMB * 1048576;
            }
            if (outBytes <= wall) return '';
            if (window.__ae) __ae('feature_use', { tool: slug, feature: 'copy_wall', src: src || 'export', vfx: vfx ? 1 : 0, ext: fileExt, sizeMB: Math.round(file.size / 1048576) });
            if (vfx && (zeroCopyCapable() || file.size <= wall)) {
                return (i18n.big_vfx_off || 'The marker rebuilds the whole picture, and the browser cannot do that for a video this size (:size). Choose “:none” under “:label”: the video is assembled without re-encoding.')
                    .replace(':size', fmtSize(file.size))
                    .replace(':label', i18n.videofx_label || 'Marker').replace(':none', i18n.videofx_none || 'None');
            }
            return (i18n.big_copy_wav || 'A :ext video this size (:size) cannot be assembled back in the browser — the limit for this format is ~:cap. The clean soundtrack can be saved with «:wav»; MP4 and MOV are assembled without this limit.')
                .replace(':ext', (fileExt || '').toUpperCase()).replace(':size', fmtSize(file.size))
                .replace(':cap', fmtSize(wall)).replace(':wav', i18n.export_wav || 'WAV');
        }

        /* opts.audioOnly — a video's censored soundtrack as lossless WAV, for
         * editors who finish the cut elsewhere and only need the clean audio. */
        async function exportFile(opts) {
            var audioOnly = !!(opts && opts.audioOnly === true);
            if (!regions.length) { window.__toast(i18n.nothing_to_censor, 'warning'); return; }
            if (settings.mode === 'custom' && !customFile) {
                window.__toast(i18n.custom_missing || 'Pick a sound first', 'warning');
                return;
            }
            /* Soft daily cap. Gated on the RUNTIME slug: this core serves both
               profanity-remover (audio, uncapped → cap() is Infinity, never blocks)
               and profanity-remover-video (capped). Placed below the nothing-to-censor
               and missing-custom-sound bails so a refused export burns nothing, and
               above the button lock so a wall-hit can't leave it disabled.
               commit() once FFmpeg returns the blob — the render is the work; the
               download that follows, and the separate report download, are free.
               abort() in the finally covers the catch, including a Cancel (pc-cancel
               calls FFmpegProcessor.cancel(), which rejects the export). */
            /* Tariff ceiling on the DOWNLOAD, not on the upload (see
               maybeConfirmTooLong): by now the user has seen exactly what the
               tool found in THEIR file, so the offer is about finishing a job
               that visibly works — not about buying a promise. Placed above the
               daily-cap begin() so a refused export burns no quota. */
            if (tierWallDurS > 0) {
                var premS = premiumCeilingS(), tierS = tierCeilingS();
                if (window.__ae) __ae('feature_use', {
                    tool: slug, feature: 'tier_duration_wall', src: 'export',
                    dur_s: Math.round(tierWallDurS), tier_s: tierS, premium_s: premS,
                    hits: regions.length
                });
                var wch = await pcConfirm({
                    icon: '⏱️',
                    title: i18n.tierlim_title || 'This file is longer than your limit',
                    message: (i18n.tierlim_export_body || 'We found :n fragments in these :d. Downloading a file this long needs Premium — it raises the limit to :p and runs recognition about 2.6x faster.')
                        .replace(':n', String(regions.length))
                        .replace(':d', fmtCoarse(tierWallDurS))
                        .replace(':c', fmtCoarse(tierS))
                        .replace(':p', premS > 0 ? fmtCoarse(premS) : fmtCoarse(tierS)),
                    choices: [
                        { id: 'pro', label: i18n.tierlim_cta || 'See Premium', primary: true },
                        { id: 'split', label: i18n.toolong_split || 'Split into parts' },
                        { id: 'cancel', label: i18n.tierlim_dismiss || 'Not now' }
                    ]
                });
                if (window.__ae) __ae('feature_use', {
                    tool: slug, feature: 'tier_duration_wall', src: 'export_choice', action: wch
                });
                if (wch === 'pro') { location.href = window.__PRO_URL || ('/' + (window.__LOCALE || 'en') + '/pricing'); return; }
                if (wch === 'split') { location.href = '/' + (window.__LOCALE || 'en') + (isVideoFile ? '/video-trim' : '/chapter-splitter'); return; }
                return;
            }

            // A video export the browser cannot assemble is refused HERE — before
            // the separator, the render and the daily quota (copyWallRefusal).
            if (isVideoFile && !audioOnly) {
                var wallMsg = copyWallRefusal(buildVideoFx(), 'export');
                if (wallMsg) { window.__toast(wallMsg, 'error'); return; }
            }

            // Keyed by the SOURCE file. Keyless, begin() reads the page's whole
            // uploader selection, and the video page has a second input — the
            // subtitles — so adding subtitles made the same video a «new» file and
            // billed it again (audit 2026-09-25).
            var myRun = window.__toolLimit
                ? window.__toolLimit.begin(slug, (file && window.__toolLimit.fileKey) ? window.__toolLimit.fileKey(file) : undefined)
                : null;
            if (window.__toolLimit && !myRun) return;   // wall hit — note already shown

            var btn = $('pc-export-btn');
            var btnLabel = btn ? btn.textContent : '';
            var wavBtnBusy = $('pc-export-wav');
            if (wavBtnBusy) wavBtnBusy.disabled = true;     // one export at a time
            if (btn) {
                btn.disabled = true;
                btn.setAttribute('aria-busy', 'true');
                btn.textContent = i18n.exporting || i18n.stage_export || 'Processing…';
            }
            setPhase('export');
            var underlay = null, voiceMs = 0;
            // pc-cancel (and a new file) bump jobSeq — THAT is the user's cancel of
            // this export, whatever the error that ends it says
            var exportJob = jobSeq;
            try {
                var layout = channels === 1 ? 'mono' : 'stereo';
                // «Только голос»: сначала подложка — лейн доделывает окна, которых
                // ещё нет (обычно их нет вовсе: разделение шло, пока человек
                // правил заглушки). Отказ разделения — не тупик и не молчание:
                // человек решает, скачать ли с обычной заглушкой.
                if (voiceOnlyActive()) {
                    var tv = Date.now();
                    setStage(i18n.stage_voice || i18n.stage_export, 0);
                    try {
                        underlay = await voiceLane.underlay({
                            regions: regions, floor: settings.mode === 'duck' ? 0.88 : 1,
                            totalSamples: Math.ceil((duration + 2) * srcRate),
                            onProgress: function (f, line) { setStage(line || i18n.stage_voice || '', Math.round(f * 100)); }
                        });
                    } catch (ve) {
                        var vmsg = (ve && ve.message) || String(ve || '');
                        // «cancel|abort» in the text was also a separator that DIED
                        // («Aborted()», OOM): the export failed with a red toast instead
                        // of offering the usual censor below.
                        if (exportJob !== jobSeq || (window.__isUserCancel && window.__isUserCancel(ve))) throw ve;
                        var vch = await pcConfirm({
                            icon: '🎧',
                            title: i18n.voice_failed_title || 'Could not separate the voice',
                            message: i18n.voice_failed_body || 'Download with the usual censor — it mutes everything under the word?',
                            choices: [
                                { id: 'plain', label: i18n.voice_failed_plain || 'Download with the usual censor', primary: true },
                                { id: 'cancel', label: i18n.voice_failed_cancel || 'Not now' }
                            ]
                        });
                        if (window.__ae) __ae('feature_use', { tool: slug, feature: 'voice_only', action: 'export_fallback', choice: vch, error: vmsg.slice(0, 80) });
                        if (vch !== 'plain') return;
                        underlay = null;
                    }
                    voiceMs = Date.now() - tv;
                }
                // the replacement sound rides the same graph as an underlay
                if (settings.mode === 'custom') underlay = await customUnderlay();
                var graph = buildFilterGraph(layout, !!underlay);
                var base = file.name.replace(/\.[^.]+$/, '');
                var args, outName, dlExt = '';
                // zero-copy lane (see zeroCopyCapable): FFmpeg renders the
                // soundtrack, replaceAudio puts it into the original file;
                // classicArgs() is the copy export it falls back to
                var zeroCopy = false, classicArgs = null, remuxInfo = null;
                if (isVideoFile && audioOnly) {
                    outName = 'out.wav';
                    args = ['-filter_complex', graph, '-map', '[outa]', '-c:a', 'pcm_s16le'];
                } else if (isVideoFile) {
                    // #1123 (Нина: 447 MB AVI → 26-min WASM re-encode → MP4 back):
                    // this tool edits AUDIO ONLY, so the export keeps the USER'S
                    // container. Same container in = same container out makes
                    // -c:v copy valid by construction (the stream already lived
                    // there): bit-exact video, ~37× faster than the old forced
                    // avi-class→mp4 transcode. Map = container → censored-audio
                    // encoder [codec, bitrate?]; every pair proven in the real
                    // WASM core + ffprobe + Media Foundation playback
                    // (tests/playwright/profanity-video-container.mjs).
                    // m4v deliberately absent → mp4 fallback: .m4v would pick the
                    // untested ipod muxer, and m4v players read mp4 natively anyway.
                    var AC = {
                        mp4: ['aac'], mov: ['aac'], '3gp': ['aac'],
                        mkv: ['aac'], ts: ['aac'], flv: ['aac'],
                        avi: ['libmp3lame'], wmv: ['wmav2'],
                        mpg: ['mp2'], mpeg: ['mp2'], vob: ['ac3'],
                        ogv: ['libvorbis', '160k'], webm: ['libopus', '128k']
                    };
                    var vext = AC[fileExt] ? fileExt : 'mp4';
                    // Censor-cue visual effect (Oleg): when active it filters
                    // [0:v] → [outv], which ALWAYS needs a re-encode (can't copy
                    // a filtered stream). Otherwise keep the cheap copy path.
                    var vfx = buildVideoFx();
                    // Ogg holds no video encoder this build has (H.264-in-ogg
                    // refuses to mux) → nearest open-codec container, webm/VP8,
                    // effect-on only; effect-off ogv stays ogv via copy.
                    if (vfx && vext === 'ogv') vext = 'webm';
                    dlExt = vext;
                    var acEnc = AC[vext] || AC.mp4;
                    classicArgs = function () {
                        var g = vfx ? graph + ';' + vfx.chain : graph;
                        var vmap = vfx ? vfx.label : '0:v:0?';
                        // 0:v:0? — audio-only files inside video containers
                        // (podcast .mp4/.m4b, misnamed audio) must not abort on
                        // a missing video stream; they just export audio-only
                        var a = ['-filter_complex', g, '-map', vmap, '-map', '[outa]'];
                        if (!vfx) {
                            a = a.concat(['-c:v', 'copy']);
                        } else if (vext === 'webm') {
                            // VP8, not libx264: x264-in-webm dies at write_header (the
                            // pre-fix code did exactly that — webm+effect never worked).
                            // VP8 rate trap: -b:v 0 silently means 200 kbps ABR; the
                            // 12M ceiling never binds ≤1080p and frees -crf to govern.
                            a = a.concat(['-c:v', 'libvpx', '-crf', '22', '-b:v', '12M', '-pix_fmt', 'yuv420p']);
                        } else if (vext === 'mpg' || vext === 'mpeg' || vext === 'vob') {
                            // MPEG-PS: H.264-in-PS muxes but Media Foundation refuses
                            // it — native mpeg2video is the standards-correct stream.
                            a = a.concat(['-c:v', 'mpeg2video', '-q:v', '4']);
                        } else {
                            a = a.concat(['-c:v', 'libx264', '-preset', 'veryfast', '-crf', '22', '-pix_fmt', 'yuv420p']);
                        }
                        // no -movflags here: the processor auto-adds +faststart for
                        // mp4-family outputs; avi/asf/mpeg/ogg muxers don't know it
                        return a.concat(['-c:a', acEnc[0], '-b:a', acEnc[1] || '192k', '-shortest']);
                    };
                    zeroCopy = !vfx && zeroCopyCapable();
                    if (zeroCopy) {
                        // AAC at 192k — the same soundtrack the copy export muxes.
                        // ⚠️ `.mp4`, not `.m4a`: the processor clamps an AUDIO
                        // output's bitrate to the plan's quality ceiling by its
                        // extension (_clampAudioKbpsArgs), and a video's soundtrack
                        // is a passenger. Measured on the stand as .m4a: 114 kb/s
                        // against the copy export's 171, −22.7 dB from the source
                        // against −34.0 (docs/why/ffmpeg-wasm.md, audio_enc.mp4).
                        outName = SoundtrackSplice.OUT_NAME;
                        args = ['-filter_complex', soundtrackGraph(graph), '-map', '[outa]', '-c:a', 'aac', '-b:a', '192k'];
                    } else {
                        // priced at the top of exportFile (copyWallRefusal)
                        outName = 'out.' + vext;
                        args = classicArgs();
                    }
                } else {
                    var fmt = ($('pc-format') && $('pc-format').value) || 'mp3';
                    outName = 'out.' + fmt;
                    var br = (fmt === 'wav' || fmt === 'flac') ? 0 : settings.bitrate; // lossless: no -b:a
                    args = ['-filter_complex', graph, '-map', '[outa]'].concat(FFmpegProcessor.getCodecArgs(fmt, br));
                }
                setStage(i18n.stage_export, 5);
                prog.stage = 'export';
                prog.jobStart = performance.now();
                startTicker();
                var onProg = function (e) {
                    var p = e && e.detail; // detail IS the 0..1 number
                    if (typeof p === 'number') setStage(i18n.stage_export, Math.round(p * 100));
                };
                window.addEventListener('ffmpeg-progress', onProg);
                var result;
                var underlayFile = underlay ? new File([underlay], 'pc-underlay.wav', { type: 'audio/wav' }) : null;
                var customUsed = settings.mode === 'custom' && !!underlay;
                var render = async function (a, name) {
                    if (underlayFile) {
                        // Многофайловая дверь не добавляет +faststart, как
                        // однофайловая (_streamOutputContract): без него moov
                        // большого mp4 ложится в хвост, и наш же Tier-0 зонд
                        // считает файл битым. Заявляем сами, как process().
                        var outExt = name.split('.').pop().toLowerCase();
                        if (/^(mp4|m4a|m4b|m4r|m4p|mov|3gp|3gpp)$/.test(outExt) && a.indexOf('-movflags') === -1) {
                            a = a.concat(['-movflags', '+faststart']);
                        }
                        return FFmpegProcessor.processMultiStreaming([file, underlayFile], a, name);
                    }
                    return FFmpegProcessor.process(file, a, name);
                };
                try {
                    // Движок отпущен после извлечения (release() в processFile,
                    // экономит сотни МБ на время ASR) — перед экспортом поднять
                    // заново. Идемпотентно: живой инстанс = мгновенный no-op.
                    var ffOk = await FFmpegProcessor.init(function (p) {
                        setStage(i18n.stage_export, Math.round(p * 4));
                    });
                    if (!ffOk) throw new Error('ffmpeg init failed');
                    var tRender = Date.now();
                    result = await render(args, outName);
                    if (zeroCopy) {
                        // the rendered soundtrack goes into the ORIGINAL file; the
                        // picture's bytes are never read (see zeroCopyCapable)
                        var renderMs = Date.now() - tRender, tRemux = Date.now();
                        try {
                            var sp = await SoundtrackSplice.splice(file, result.blob,
                                { mime: file.type || (dlExt === 'mov' ? 'video/quicktime' : 'video/mp4') });
                            remuxInfo = { lane: 'zero-copy', render_ms: renderMs, remux_ms: Date.now() - tRemux, parts: sp.info.parts,
                                qt: sp.info.qt, edits: sp.info.edits, flat: sp.info.flat, lead_ms: Math.round((srcStartS || 0) * 1000),
                                co64: sp.info.co64 || 0 };
                            result = { blob: sp.blob };
                        } catch (re) {
                            var reason = (re && re.remuxReason) || 'error';
                            if (window.__ae) __ae('feature_use', { tool: slug, feature: 'zero_copy_refused', reason: reason, ext: fileExt, sizeMB: Math.round(file.size / 1048576), msg: String((re && re.message) || re).slice(0, 80) });
                            // a browser error while reading the file (a revoked
                            // handle) is not a refusal of the shape — surface it
                            if (!re || !re.remuxReason) throw re;
                            // the classic copy export — where its output fits the heap
                            if (file.size > copyWallBytes()) {
                                var refusal = new Error((i18n.big_remux_refused || 'This video file has a structure the browser cannot rebuild at this size (:reason). The clean soundtrack can be saved with «:wav».')
                                    .replace(':reason', reason).replace(':wav', i18n.export_wav || 'WAV'));
                                refusal.userFacing = true;
                                throw refusal;
                            }
                            remuxInfo = { lane: 'ffmpeg', reason: reason, render_ms: renderMs };
                            outName = 'out.' + dlExt;
                            setStage(i18n.stage_export, 5);
                            result = await render(classicArgs(), outName);
                        }
                    }
                } finally {
                    window.removeEventListener('ffmpeg-progress', onProg);
                }
                setStage('', -1);
                if (myRun) myRun.commit();   // censored file rendered → bill one run (not the save)
                var dlName = base + (audioOnly ? '_clean_audio.' : '_clean.') + (dlExt || outName.split('.').pop());
                window.__downloadFile(result.blob, dlName);
                if (window.__aeToolComplete) {
                    __aeToolComplete({
                        regions: regions.length, manual: manualRegions.length, mode: settings.mode,
                        // how much of the result is the user's hand: coverage
                        // they chose, detector censors they re-bounded or
                        // switched off, undos — the numbers that were
                        // missing when Ангелина's 193 redrawn censors looked
                        // like ordinary «manual» usage
                        coverage: settings.coverage, items: items.length,
                        edited: Object.keys(edits).filter(function (k) { var it = itemById('w' + k); return !!it; }).length,
                        switched_off: Object.keys(overrides).filter(function (k) { return overrides[k] === false; }).length,
                        undos: undoCount, nudges: editStats.nudge, moves: editStats.move, resizes: editStats.resize,
                        keys: Object.keys(keyStats).length ? JSON.stringify(keyStats).slice(0, 200) : '',
                        auto_level: settings.mode === 'beep' ? (settings.beepAuto ? 1 : 0) : '',
                        video: isVideoFile ? 1 : 0, durationS: Math.round(duration), out: dlExt || outName.split('.').pop(),
                        // vcopy: same-container -c:v copy took the fast path (#1123)
                        vcopy: (isVideoFile && !vfx && !audioOnly) ? 1 : 0, src_ext: fileExt, audio_only: audioOnly ? 1 : 0,
                        // zero-copy lane: which way the picture went (zero-copy |
                        // ffmpeg after a refusal), the refusal, and the two halves
                        remux: remuxInfo ? remuxInfo.lane : '', remux_reason: remuxInfo && remuxInfo.reason ? remuxInfo.reason : '',
                        render_ms: remuxInfo ? remuxInfo.render_ms : '', remux_ms: remuxInfo && remuxInfo.remux_ms != null ? remuxInfo.remux_ms : '',
                        remux_parts: remuxInfo && remuxInfo.parts ? remuxInfo.parts : '', remux_qt: remuxInfo && remuxInfo.qt ? 1 : '',
                        // chunk tables widened to co64: the output crossed 4 GiB (chat 657)
                        remux_co64: remuxInfo && remuxInfo.co64 ? remuxInfo.co64 : '',
                        lead_ms: remuxInfo && remuxInfo.lead_ms ? remuxInfo.lead_ms : '', sizeMB: Math.round(file.size / 1048576),
                        // «только голос»: чем разделили и сколько заняло ожидание
                        // разделения В экспорте (подготовка до нажатия сюда не входит)
                        voice_only: (underlay && !customUsed) ? 1 : 0,
                        vo_engine: (underlay && !customUsed) && voiceLane ? voiceLane.engine() : '',
                        vo_ms: (underlay && !customUsed) ? voiceMs : '',
                        // the replacement sound: once per censor or repeated
                        custom_loop: customUsed ? (settings.customLoop ? 1 : 0) : '',
                        // the preview this file was approved by: measured or not
                        psync: gateState === 'ready' ? (sync.reported ? 'locked' : 'gate') : gateState
                    });
                }
            } catch (err) {
                setStage('', -1);
                // The user's cancel ends quietly: the separator's «Cancelled» came out
                // as a red «processing failed», and every cancel was beaconed as a
                // wasm-error.
                if (exportJob !== jobSeq || (window.__isUserCancel && window.__isUserCancel(err))) return;
                // our own refusal already speaks to the person (zero-copy lane)
                if (err && err.userFacing) { window.__toast(err.message, 'error'); return; }
                window.__toast(FFmpegProcessor.getErrorMessage(err, file ? file.size : 0), 'error');
                if (FFmpegProcessor.reportError) FFmpegProcessor.reportError(slug, err, file ? file.size : 0, 1);
            } finally {
                if (myRun) myRun.abort();   // idempotent after commit; cancel / failure → never bills
                setPhase('ready');
                if (btn) {
                    btn.disabled = false;
                    btn.removeAttribute('aria-busy');
                    if (btnLabel) btn.textContent = btnLabel;
                }
                updateExportEnabled();
            }
        }

        /* ---------------- report ---------------- */
        function buildReport(kind) {
            // Timecodes of what is ACTUALLY muted (the censor's bounds, edits
            // included) — the report is imported into editors (Premiere
            // markers, platform forms), where the ASR word time is not what
            // the file contains.
            var byIndex = {};
            hits.forEach(function (h) { byIndex[h.index] = h; });
            var rows = items.map(function (it) {
                if (it.kind === 'manual') return { start: it.start, end: it.end, word: '[' + (i18n.censored_label || '***') + ']', kind: 'manual-region', rule: 'manual' };
                var h = byIndex[it.wi];
                return { start: it.start, end: it.end, word: it.label, kind: h ? h.kind : 'manual', rule: h ? h.ruleId : 'manual' };
            });
            if (kind === 'csv') {
                var csv = 'start;end;word;kind;rule\n' + rows.map(function (r) {
                    return fmtTimecode(r.start) + ';' + fmtTimecode(r.end) + ';"' + r.word.replace(/"/g, '""') + '";' + r.kind + ';' + r.rule;
                }).join('\n');
                return new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
            }
            if (kind === 'json') {
                return new Blob([JSON.stringify({ file: file.name, duration: duration, hits: rows }, null, 1)], { type: 'application/json' });
            }
            if (kind === 'srt') {
                var srt = regions.map(function (r, i) {
                    function st(s) {
                        var total = Math.max(0, Math.round(s * 1000));   // whole ms first: never ",1000"
                        var h2 = Math.floor(total / 3600000), m = Math.floor(total % 3600000 / 60000), sec = Math.floor(total % 60000 / 1000), ms = total % 1000;
                        function p(x, l) { return ('000' + x).slice(-l); }
                        return p(h2, 2) + ':' + p(m, 2) + ':' + p(sec, 2) + ',' + p(ms, 3);
                    }
                    return (i + 1) + '\n' + st(r.start) + ' --> ' + st(r.end) + '\n[' + (i18n.censored_label || '***') + ']\n';
                }).join('\n');
                return new Blob([srt], { type: 'text/plain;charset=utf-8' });
            }
            var txt = (i18n.report_header || 'Profanity report') + ' — ' + file.name + '\n' +
                rows.map(function (r) { return fmtTimecode(r.start) + ' – ' + fmtTimecode(r.end) + '  ' + r.word + (r.kind === 'fuzzy' ? '  (?)' : ''); }).join('\n') + '\n';
            return new Blob(['\uFEFF' + txt], { type: 'text/plain;charset=utf-8' });
        }

        function downloadReport(kind) {
            var base = file.name.replace(/\.[^.]+$/, '');
            var ext = kind === 'csv' ? 'csv' : kind === 'json' ? 'json' : kind === 'srt' ? 'srt' : 'txt';
            window.__downloadFile(buildReport(kind), base + '_report.' + ext);
            if (window.__ae) __ae('feature_use', { tool: slug, feature: 'report_' + ext });
        }

        /* ---------------- main flow ---------------- */
        /* ONE toast per failure. Both catch blocks used to call __handleError
           (which shows the RAW technical string: «profanity-remover:process:
           asr stalled — no worker liveness for 145s») and THEN add the
           localized line — so a Russian user got an English diagnostic on top
           of the explanation, and Pino stored the same failure twice (prod
           20 d: 56 raw + 78 localized rows for one stall class).

           For errors WE minted (pcCode) the localized line is the useful one,
           so the raw text goes to telemetry only — same `toast_error` shape
           __toast would have produced, so existing dashboards keep matching —
           and the visible toast is localized and telemetry-silent.
           Unclassified errors keep going through __handleError untouched: it
           owns the INIT_ERROR ad-block dialog, the cancel filter and the typed
           FILE_LOST / EMPTY_FILE rejects, and its raw toast is then the only
           one shown. */
        function reportAndToast(err, ctxName) {
            var code = err && err.pcCode;
            if (!code) { window.__handleError(err, ctxName); return; }
            var raw = ctxName + ': ' + ((err && err.message) || String(err));
            try {
                if (window.__aeToolError) window.__aeToolError({
                    error_type: 'toast_error', msg: raw.substring(0, 500), ctx: ctxName,
                    pc_code: code,
                    br: (window.__brOf ? window.__brOf(navigator.userAgent) : 'other'),
                    mob: /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? 1 : 0,
                    dm: navigator.deviceMemory || 0
                });
            } catch (e) { /* noop */ }
            try { console.error('[Timbrica]', raw); } catch (e) { /* noop */ }
            window.__toast(errorToastFor(err), 'error', { silent: true });
        }

        /* Провал, который лечится повтором, обязан ОСТАТЬСЯ на экране.
           Жалоба 17.08 (Android/Firefox) звучала как «вгружаю файл, она не
           принимает его»: модель не скачалась, тул вернулся в 'idle', тост
           через несколько секунд исчез — а выбранный файл остался висеть.
           С экрана это неотличимо от «файл отвергли молча». Тост сообщает,
           но не ДАЁТ ДЕЙСТВИЯ, а действие здесь ровно одно и оно осмысленно:
           повторить, потому что скачанное уже лежит в кэше. */
        function showRetryNotice(err) {
            var host = $('pc-workspace');
            if (!host) return;
            var old = $('pc-retry-notice'); if (old) old.remove();
            var box = document.createElement('div');
            box.id = 'pc-retry-notice';
            box.setAttribute('role', 'alert');
            box.style.cssText = 'margin:0 0 12px;padding:12px 14px;border:1px solid var(--danger,#ef4444);' +
                'border-radius:12px;background:var(--glass,rgba(255,255,255,.04));font-size:13px;line-height:1.5;' +
                'color:var(--text-primary,#fff);display:flex;gap:12px;align-items:center;flex-wrap:wrap';
            var txt = document.createElement('span');
            txt.style.cssText = 'flex:1 1 240px';
            txt.textContent = '⚠️ ' + errorToastFor(err);
            var btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'lg-btn';
            btn.style.cssText = 'padding:8px 16px;font-size:13px;white-space:nowrap';
            btn.textContent = i18n.err_retry_action || 'Try again';
            btn.addEventListener('click', function () {
                var f = file;
                box.remove();
                if (f) processFile(f);
            });
            box.appendChild(txt);
            box.appendChild(btn);
            host.parentNode.insertBefore(box, host);
        }

        function clearRetryNotice() {
            var b = $('pc-retry-notice'); if (b) b.remove();
        }

        /* Повтор осмыслен только там, где мешала СРЕДА (сеть, зависшая
           загрузка), а не сам файл: предлагать «попробовать снова» после
           «файл повреждён» — издевательство. */
        function isRetryable(err) {
            var code = err && err.pcCode;
            if (code === 'dl_failed' || code === 'stalled') return true;
            var m = String((err && err.message) || '').toLowerCase();
            return /network|failed to fetch|load failed|truncated|stalled/.test(m);
        }

        function errorToastFor(err) {
            var code = err && err.pcCode;
            if (code === 'dl_failed') return i18n.err_model_download || i18n.process_error;
            if (code === 'oom') return i18n.err_oom || i18n.process_error;
            if (code === 'session_failed' || code === 'inference_failed') return i18n.err_asr || i18n.process_error;
            if (code === 'stalled') return i18n.err_stalled || i18n.err_oom || i18n.process_error;
            return (i18n.process_error || 'Error') + ': ' + (err && err.message || err);
        }

        /* ---------------- the work survives the tab ----------------
         * A 37-minute video took Ангелина six minutes of recognition and 2.6
         * hours of edits (23.09). One closed tab, one crash, one «Другой файл»
         * pressed by mistake — and all of it was gone, and the next attempt
         * started with the model and the recognition again. So the transcript
         * and every decision are saved (TranscriptCheckpointStore, IndexedDB,
         * the store /auto-captions and /audio-to-text already use), keyed by
         * the file; choosing the SAME file brings both back without
         * recognising anything. `origin` keeps the neighbours' banners off it. */
        var SESSION_ORIGIN = 'profanity-censor';
        var SESSION_MAX_AGE_MS = 30 * 86400000;
        var sessionKey = '', sessionSaveTimer = 0, sessionStartedAt = 0;
        function sessionStore() {
            var S = window.TranscriptCheckpointStore;
            return (S && S.isSupported && S.isSupported()) ? S : null;
        }
        function sessionKeyFor(f) {
            var S = sessionStore();
            return S && f ? S.fingerprint(f, 'pc|' + settings.lang) : '';
        }
        function scheduleSessionSave() {
            if (!sessionKey || phase !== 'ready' || !words.length) return;
            clearTimeout(sessionSaveTimer);
            sessionSaveTimer = setTimeout(function () { sessionSaveTimer = 0; saveSessionNow(); }, 1200);
        }
        /* A save still waiting out its 1.2 s debounce is written NOW — before
           «Другой файл» or a new pick clears the state it would have saved
           (until 09.10 the last edits of a session were lost that way). */
        function flushSessionSave() {
            if (!sessionSaveTimer) return;
            clearTimeout(sessionSaveTimer);
            sessionSaveTimer = 0;
            saveSessionNow();
        }
        function saveSessionNow() {
            var S = sessionStore();
            if (!S || !sessionKey || !words.length) return;
            S.save(sessionKey, {
                origin: SESSION_ORIGIN, status: 'done', slug: slug, keepMs: SESSION_MAX_AGE_MS,
                fileName: file ? file.name : '', fileSize: file ? file.size : 0,
                lang: activeLang, engine: asr ? asr.engine : '', duration: duration,
                words: words, wordCount: words.length,
                o: overrides, m: manualRegions, e: edits, s: manualSeq,
                decisions: Object.keys(overrides).length + manualRegions.length + Object.keys(edits).length,
                startedAt: sessionStartedAt || Date.now()
            }).catch(function () { /* quota / private mode: the tool works without it */ });
        }
        function usableSession(saved) {
            return !!(saved && saved.origin === SESSION_ORIGIN && saved.status === 'done' && saved.words && saved.words.length);
        }
        /* The key is name|size|lastModified — how the browser describes the pick,
         * not what is in it. Renamed, moved to another folder, downloaded again,
         * copied back from a backup: the same bytes, a different key, and hours
         * of edits unreachable although the page said «choose the same file».
         * Byte length is the identity that survives all of those (a 769 MB video
         * matching another one to the byte does not happen), and it is not the
         * last word: the restore below still demands the decoded length to agree
         * within a second before a single edit is applied. */
        async function sameFileSession(f) {
            var S = sessionStore();
            if (!S || !f || !(f.size > 0)) return null;
            var tail = '|pc|' + settings.lang;
            var list = await S.listPending();
            var mine = (list || []).filter(function (x) {
                return x.origin === SESSION_ORIGIN && x.fileSize === f.size && x.key !== sessionKey
                    && String(x.key).slice(-tail.length) === tail;
            }).sort(function (a, b) { return (b.updatedAt || 0) - (a.updatedAt || 0); });
            if (!mine.length) return null;
            var saved = await S.get(mine[0].key);
            return usableSession(saved) ? saved : null;
        }
        function restoreSession(saved) {
            var movedFrom = saved.key && saved.key !== sessionKey ? saved.key : '';
            words = saved.words.slice();
            renderTranscript(words);
            overrides = saved.o || {}; manualRegions = saved.m || []; edits = saved.e || {}; manualSeq = saved.s || 1;
            if (saved.lang) activeLang = saved.lang;
            sessionStartedAt = saved.startedAt || Date.now();
            setPhase('ready');
            setStage('', -1);
            $('pc-results').style.display = 'block';
            syncWide();
            var noSpeech = $('pc-no-speech');
            if (noSpeech) noSpeech.style.display = words.length ? 'none' : '';
            renderWave();
            updatePlayhead();
            syncZoomUI();
            rescan();
            showRestoredNote(saved);
            // the work now belongs to this pick: next time the exact key finds it
            if (movedFrom) {
                saveSessionNow();
                var S = sessionStore(); if (S) S.discard(movedFrom).catch(function () {});
            }
            if (window.__ae) __ae('feature_use', {
                tool: slug, feature: 'session_restored', words: words.length, match: movedFrom ? 'size' : 'exact',
                decisions: saved.decisions || 0, age_h: Math.round((Date.now() - (saved.updatedAt || Date.now())) / 3600000)
            });
            lexSeenSignal();
        }
        function showRestoredNote(saved) {
            var old = $('pc-restored-note'); if (old) old.remove();
            var host = $('pc-results');
            if (!host) return;
            var n = saved.decisions || 0;
            var b = document.createElement('div');
            b.id = 'pc-restored-note';
            b.className = 'pc-restored';
            b.setAttribute('role', 'status');
            var txt = document.createElement('span');
            txt.textContent = (n ? (i18n.restored_edits || 'Restored: the transcript and your :n edits.').replace(':n', n)
                                 : (i18n.restored_plain || 'Restored: the transcript of this file — recognition was not repeated.'));
            var again = document.createElement('button');
            again.type = 'button';
            again.className = 'pc-mini-btn';
            again.textContent = i18n.restored_redo || 'Recognise again';
            again.addEventListener('click', function () {
                var S = sessionStore(), f = file;
                if (S && sessionKey) S.discard(sessionKey).catch(function () {});
                if (window.__ae) __ae('feature_use', { tool: slug, feature: 'session_redo' });
                if (f) processFile(f, { fresh: true });
            });
            var close = document.createElement('button');
            close.type = 'button';
            close.className = 'pc-restored-x';
            close.setAttribute('aria-label', '×');
            close.textContent = '×';
            close.addEventListener('click', function () { b.remove(); });
            b.appendChild(txt); b.appendChild(again); b.appendChild(close);
            host.insertBefore(b, host.firstChild);
        }
        /* Before a file is chosen: unfinished work is named, so the person who
         * closed the tab yesterday knows that picking the same file brings it
         * back (the file itself cannot be restored — the browser never gives
         * it to us again). */
        function pendingSessionNote() {
            var S = sessionStore();
            if (!S) return;
            S.purgeStale(SESSION_MAX_AGE_MS).catch(function () {});
            var lostP = S.takeLost ? S.takeLost(SESSION_ORIGIN).catch(function () { return []; }) : Promise.resolve([]);
            Promise.all([S.listPending(), lostP]).then(function (both) {
                var list = both[0], lost = (both[1] || []).filter(function (x) { return x.decisions > 0; })
                    .sort(function (a, b) { return (b.updatedAt || 0) - (a.updatedAt || 0); });
                var mine = (list || []).filter(function (x) { return x.origin === SESSION_ORIGIN && x.decisions > 0; })
                    .sort(function (a, b) { return (b.updatedAt || 0) - (a.updatedAt || 0); });
                /* The browser removed saved work (disk nearly full — see the
                   store): the page says so instead of greeting the person with
                   an empty upload zone after «saved automatically». Only when
                   nothing newer is waiting — a newer save is what they want. */
                var gone = lost.length && (!mine.length || (lost[0].updatedAt || 0) > (mine[0].updatedAt || 0)) ? lost[0] : null;
                // waiting work is the projects list now (renderProjectsList) — every
                // project, not only the latest; here only what the browser took away
                if (!gone || file) return;
                var zone = $('upload-zone');
                if (!zone || !zone.parentNode || $('pc-pending-note')) return;
                var m = gone || mine[0];
                var b = document.createElement('div');
                b.id = 'pc-pending-note';
                b.className = 'pc-restored';
                b.setAttribute('role', 'status');
                var txt = document.createElement('span');
                txt.textContent = (gone
                    ? (i18n.pending_lost || 'Your saved edits for “:name” (:n) were removed by the browser to free disk space.')
                    : (i18n.pending_work || '«:name»: :n edits are saved. Choose the same file to continue.'))
                    .replace(':name', m.fileName || '').replace(':n', m.decisions);
                if (gone && window.__ae) __ae('feature_use', {
                    tool: slug, feature: 'session_lost_shown', decisions: gone.decisions,
                    age_h: Math.round((Date.now() - (gone.updatedAt || Date.now())) / 3600000)
                });
                var close = document.createElement('button');
                close.type = 'button';
                close.className = 'pc-restored-x';
                close.setAttribute('aria-label', '×');
                close.textContent = '×';
                close.addEventListener('click', function () { b.remove(); });
                b.appendChild(txt); b.appendChild(close);
                zone.parentNode.insertBefore(b, zone);
                if (!gone && window.__ae) __ae('feature_use', { tool: slug, feature: 'session_pending_shown', decisions: m.decisions });
            }).catch(function () {});
        }

        /* ---------------- the project as a file ----------------
         * Chat 640: «как будто надо делать проекты, чтобы сохранялись». The
         * browser's own store keeps every project 30 days (and can lose them
         * to a full disk); a .tmbcensor file keeps it on the person's device
         * for as long as they like, on another computer too — with the
         * recording inside when asked, so ONE file opens the whole work.
         * Format and checks: censor-project.js. Nothing goes to our server. */
        var PRJ = null, pendingProject = null, preferredSessionKey = '';
        var CUSTOM_EMBED_MAX = 3 * 1048576;     // a replacement sound rides in the project up to this size
        function prj() { return PRJ || (PRJ = window.ProfanityCensorProject || null); }
        function settingsForProject() {
            return {
                mode: settings.mode, beepFreq: settings.beepFreq, beepVol: settings.beepVol, beepAuto: settings.beepAuto,
                coverage: settings.coverage, tier2: settings.tier2, tier3: settings.tier3,
                blacklist: settings.blacklist, whitelist: settings.whitelist,
                customLoop: !!settings.customLoop, videoFx: settings.videoFx, voiceOnly: settings.voiceOnly
            };
        }
        function bytesToB64(u8) {
            var bin = '';
            for (var i = 0; i < u8.length; i += 0x8000) bin += String.fromCharCode.apply(null, u8.subarray(i, i + 0x8000));
            return btoa(bin);
        }
        function b64ToBytes(b64) {
            var bin = atob(b64), u8 = new Uint8Array(bin.length);
            for (var i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
            return u8;
        }
        async function currentProject(withMedia) {
            var custom = null;
            if (customFile && customFile.size <= CUSTOM_EMBED_MAX) {
                try {
                    custom = { name: customFile.name, type: customFile.type || '',
                        b64: bytesToB64(new Uint8Array(await customFile.arrayBuffer())) };
                } catch (e) { custom = null; }
            }
            return prj().makeProject({
                app: slug, fileName: file ? file.name : '', fileSize: file ? file.size : 0,
                fileType: file ? (file.type || '') : '', fileModified: file ? (file.lastModified || 0) : 0,
                duration: duration, lang: activeLang, engine: asr ? asr.engine : '',
                words: words, o: overrides, m: manualRegions, e: edits, s: manualSeq,
                settings: settingsForProject(), custom: custom, media: !!withMedia
            });
        }
        async function saveProject(withMedia, via) {
            if (!prj() || !file || !words.length || phase !== 'ready') return;
            var data = await currentProject(withMedia);
            var blob = prj().encode(data, withMedia ? file : null);
            var pd = window.__projectDoor;
            var label = withMedia ? (i18n.project_name_media || 'project with the recording') : (i18n.project_name || 'project');
            var base = prj().baseName(file.name, label);
            var name = pd && pd.suggestName ? pd.suggestName(base, prj().EXT) : base + '.' + prj().EXT;
            var res = { via: 'download' };
            try {
                res = pd && pd.saveFile
                    ? await pd.saveFile(blob, { key: slug + (withMedia ? ':media' : ''), suggestedName: name, slug: slug,
                        types: [{ description: 'Timbrica', accept: { 'application/octet-stream': ['.' + prj().EXT] } }] })
                    : (window.__downloadFile(blob, name), { via: 'download' });
            } catch (e) {
                if (window.__handleError) window.__handleError(e, 'project_save', { say: i18n.project_save_failed || 'Could not save the project.' });
                return;
            }
            if (res.via === 'cancelled') return;
            window.__toast((i18n.project_saved || 'Project saved: :name').replace(':name', res.name || name), 'success');
            if (window.__ae) __ae('feature_use', { tool: slug, feature: 'project_save', media: withMedia ? 1 : 0, via: via || 'btn',
                how: res.via, kb: Math.round(blob.size / 1024), decisions: Object.keys(overrides).length + manualRegions.length + Object.keys(edits).length });
        }
        /* The project's settings become the page's — and the controls show them. */
        function applyProjectSettings(ps) {
            if (!ps) return;
            var keys = ['mode', 'beepFreq', 'beepVol', 'beepAuto', 'coverage', 'tier2', 'tier3', 'blacklist', 'whitelist', 'customLoop', 'videoFx', 'voiceOnly'];
            keys.forEach(function (k) {
                if (ps[k] === undefined || ps[k] === null) return;
                settings[k] = ps[k];
                var v = ps[k];
                saveSetting(k, typeof v === 'boolean' ? (v ? '1' : '0') : String(v));
            });
            syncControlsFromSettings();
        }
        function syncControlsFromSettings() {
            var mw = $('pc-mode');
            if (mw) mw.querySelectorAll('[data-mode]').forEach(function (b) { b.classList.toggle('active', b.getAttribute('data-mode') === settings.mode); });
            var fx = $('pc-videofx');
            if (fx) fx.querySelectorAll('[data-fx]').forEach(function (b) { b.classList.toggle('active', b.getAttribute('data-fx') === settings.videoFx); });
            [['pc-freq', 'beepFreq', 'pc-freq-out', ' Hz'], ['pc-vol', 'beepVol', 'pc-vol-out', '%'], ['pc-coverage', 'coverage', 'pc-coverage-out', '%']].forEach(function (r) {
                var el = $(r[0]), out = $(r[2]);
                if (el) el.value = String(settings[r[1]]);
                if (out) out.textContent = settings[r[1]] + r[3];
            });
            [['pc-beep-auto', 'beepAuto'], ['pc-tier2', 'tier2'], ['pc-tier3', 'tier3'], ['pc-custom-loop', 'customLoop'], ['pc-voice-only', 'voiceOnly']].forEach(function (r) {
                var el = $(r[0]); if (el) el.checked = !!settings[r[1]];
            });
            var bl = $('pc-blacklist'), wl = $('pc-whitelist');
            if (bl) bl.value = settings.blacklist || '';
            if (wl) wl.value = settings.whitelist || '';
            updateSoundRows();
            syncVoiceOnlyUi();
            invalidateAuto();
            pushGateParams();
            pushGateCustom();
        }
        async function applyProjectExtras(project) {
            applyProjectSettings(project.settings);
            if (project.custom && project.custom.b64 && !customFile) {
                try {
                    var cf = new File([b64ToBytes(project.custom.b64)], project.custom.name || 'sound', { type: project.custom.type || '' });
                    await loadCustomSound(cf);
                } catch (e) { /* the sound is a convenience; the work is restored without it */ }
            }
        }
        function projectWaitingNote(p) {
            var old = $('pc-project-wait'); if (old) old.remove();
            var zone = $('upload-zone');
            if (!zone || !zone.parentNode) return;
            var b = document.createElement('div');
            b.id = 'pc-project-wait';
            b.className = 'pc-restored';
            b.setAttribute('role', 'status');
            var txt = document.createElement('span');
            var n = Object.keys(p.o || {}).length + (p.m || []).length + Object.keys(p.e || {}).length;
            txt.textContent = (i18n.project_pick_media || 'Project «:name» (:n edits) is open. Now choose the recording itself — «:name», :dur.')
                .replace(/:name/g, p.file.name || '').replace(':n', String(n)).replace(':dur', fmtTime(p.file.duration || 0));
            var pick = document.createElement('button');
            pick.type = 'button'; pick.className = 'pc-mini-btn';
            pick.textContent = i18n.project_pick_btn || 'Choose the recording';
            pick.addEventListener('click', function () {
                var inp = zone.querySelector('input[type="file"]');
                if (inp) inp.click();
            });
            var close = document.createElement('button');
            close.type = 'button'; close.className = 'pc-restored-x'; close.setAttribute('aria-label', '×'); close.textContent = '×';
            close.addEventListener('click', function () { pendingProject = null; b.remove(); });
            b.appendChild(txt); b.appendChild(pick); b.appendChild(close);
            zone.parentNode.insertBefore(b, zone);
        }
        async function openProjectFile(f) {
            if (!prj()) return;
            var got;
            try { got = await prj().decode(f); } catch (e) {
                var code = (e && e.code) || 'DAMAGED';
                window.__toast(code === 'NOT_A_PROJECT' ? (i18n.project_not_project || 'This is not a project file.')
                    : code === 'TOO_NEW' ? (i18n.project_too_new || 'This project was saved by a newer version — reload the page.')
                    : (i18n.project_damaged || 'The project file is damaged.'), 'error');
                if (window.__ae) __ae('feature_use', { tool: slug, feature: 'project_open', ok: 0, code: code, kb: Math.round(f.size / 1024) });
                return;
            }
            if (window.__ae) __ae('feature_use', { tool: slug, feature: 'project_open', ok: 1, media: got.media ? 1 : 0, kb: Math.round(f.size / 1024) });
            var p = got.data;
            if (got.media) {
                var mf = new File([got.media], p.file.name || 'recording', { type: p.file.type || '', lastModified: p.file.lastModified || Date.now() });
                processFile(mf, { project: p });
                return;
            }
            pendingProject = p;
            projectWaitingNote(p);
        }
        /* Does the picked recording belong to the waiting project? The same
           length in bytes is the identity that survives a rename or a copy;
           a different one is asked about, not refused (a re-export of the same
           take differs by a byte) — and the restore still demands the decoded
           length to agree within a second. */
        async function projectForPick(f) {
            var p = pendingProject;
            if (!p) return null;
            if (p.file && p.file.size && f.size !== p.file.size) {
                var ch = await pcConfirm({
                    icon: '📁',
                    title: i18n.project_other_title || 'Another recording?',
                    message: (i18n.project_other_body || 'The project was made on «:pname» (:psize). You chose «:name» (:size). Apply the project to it?')
                        .replace(':pname', p.file.name || '').replace(':psize', fmtSize(p.file.size || 0))
                        .replace(':name', f.name).replace(':size', fmtSize(f.size)),
                    choices: [
                        { id: 'apply', label: i18n.project_other_apply || 'Apply', primary: true },
                        { id: 'no', label: i18n.project_other_no || 'Open without the project' }
                    ]
                });
                if (ch !== 'apply') { pendingProject = null; var w = $('pc-project-wait'); if (w) w.remove(); return null; }
            }
            return p;
        }

        /* Every project this browser keeps — not only the latest («я уже не
           восстановлю прошлый, сохраняется только самый последний», chat 640:
           they were all kept, the page named one). */
        function renderProjectsList() {
            var S = sessionStore(), box = $('pc-projects');
            if (!S || !box) return;
            S.listPending().then(function (list) {
                var mine = (list || []).filter(function (x) { return x.origin === SESSION_ORIGIN && (x.decisions > 0 || x.wordCount > 0); })
                    .sort(function (a, b) { return (b.updatedAt || 0) - (a.updatedAt || 0); }).slice(0, 8);
                box.innerHTML = '';
                if (!mine.length || file) { box.hidden = true; return; }
                var head = document.createElement('div');
                head.className = 'pc-projects-head';
                head.textContent = i18n.projects_title || 'Your projects in this browser';
                box.appendChild(head);
                var ul = document.createElement('ul');
                ul.className = 'pc-projects-list';
                mine.forEach(function (m) {
                    var li = document.createElement('li');
                    li.className = 'pc-project';
                    var info = document.createElement('div');
                    info.className = 'pc-project-info';
                    var nm = document.createElement('div');
                    nm.className = 'pc-project-name';
                    nm.textContent = m.fileName || '—';
                    var meta = document.createElement('div');
                    meta.className = 'pc-project-meta';
                    var when = '';
                    try { when = new Date(m.updatedAt || Date.now()).toLocaleString(window.__intlLocale ? window.__intlLocale() : undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch (e) { when = ''; }
                    meta.textContent = [m.duration ? fmtTime(m.duration) : '', (i18n.projects_edits || ':n edits').replace(':n', String(m.decisions || 0)), when].filter(Boolean).join(' · ');
                    info.appendChild(nm); info.appendChild(meta);
                    var go = document.createElement('button');
                    go.type = 'button'; go.className = 'pc-mini-btn pc-project-go';
                    go.textContent = i18n.projects_continue || 'Continue';
                    go.title = (i18n.projects_continue_hint || 'Choose «:name» — the work comes back as you left it').replace(':name', m.fileName || '');
                    go.addEventListener('click', function () {
                        preferredSessionKey = m.key;
                        var inp = $('upload-zone') && $('upload-zone').querySelector('input[type="file"]');
                        if (inp) inp.click();
                        if (window.__ae) __ae('feature_use', { tool: slug, feature: 'projects_list', act: 'continue' });
                    });
                    var save = document.createElement('button');
                    save.type = 'button'; save.className = 'pc-mini-btn';
                    save.textContent = i18n.projects_to_file || 'Save as file';
                    save.addEventListener('click', function () { exportStoredProject(m.key); });
                    var del = document.createElement('button');
                    del.type = 'button'; del.className = 'pc-restored-x';
                    del.setAttribute('aria-label', i18n.projects_delete || 'Delete');
                    del.title = i18n.projects_delete || 'Delete';
                    del.textContent = '×';
                    del.addEventListener('click', function () {
                        pcConfirm({
                            icon: '🗑️', title: i18n.projects_delete_title || 'Delete this project?',
                            message: (i18n.projects_delete_body || '«:name»: :n edits will be removed from this browser. The recording itself stays where it is.').replace(':name', m.fileName || '').replace(':n', String(m.decisions || 0)),
                            choices: [{ id: 'del', label: i18n.projects_delete || 'Delete', primary: true }, { id: 'cancel', label: i18n.projects_cancel || 'Cancel' }]
                        }).then(function (ch) {
                            if (ch !== 'del') return;
                            S.discard(m.key).catch(function () {}).then(renderProjectsList);
                            if (window.__ae) __ae('feature_use', { tool: slug, feature: 'projects_list', act: 'delete' });
                        });
                    });
                    li.appendChild(info); li.appendChild(go); li.appendChild(save); li.appendChild(del);
                    ul.appendChild(li);
                });
                box.appendChild(ul);
                var foot = document.createElement('div');
                foot.className = 'pc-hint';
                foot.textContent = i18n.projects_hint || 'Kept in this browser for 30 days. To continue, choose the same recording; «Save as file» keeps a project for good and opens it on another computer.';
                box.appendChild(foot);
                box.hidden = false;
                if (window.__ae) __ae('feature_use', { tool: slug, feature: 'projects_list', act: 'shown', n: mine.length });
            }).catch(function () { box.hidden = true; });
        }
        async function exportStoredProject(key) {
            var S = sessionStore();
            if (!S || !prj()) return;
            var sv = null;
            try { sv = await S.get(key); } catch (e) { sv = null; }
            if (!usableSession(sv)) { window.__toast(i18n.project_damaged || 'The project file is damaged.', 'error'); return; }
            var data = prj().makeProject({
                app: slug, fileName: sv.fileName, fileSize: sv.fileSize, duration: sv.duration, lang: sv.lang, engine: sv.engine,
                words: sv.words, o: sv.o, m: sv.m, e: sv.e, s: sv.s, settings: settingsForProject(), media: false
            });
            var blob = prj().encode(data, null), pd = window.__projectDoor;
            var base = prj().baseName(sv.fileName, i18n.project_name || 'project');
            var name = pd && pd.suggestName ? pd.suggestName(base, prj().EXT) : base + '.' + prj().EXT;
            var res = pd && pd.saveFile ? await pd.saveFile(blob, { key: 'stored:' + key, suggestedName: name, slug: slug,
                types: [{ description: 'Timbrica', accept: { 'application/octet-stream': ['.' + prj().EXT] } }] }) : (window.__downloadFile(blob, name), { via: 'download' });
            if (res.via !== 'cancelled') window.__toast((i18n.project_saved || 'Project saved: :name').replace(':name', res.name || name), 'success');
            if (window.__ae) __ae('feature_use', { tool: slug, feature: 'projects_list', act: 'to_file', how: res.via });
        }

        async function processFile(f, opts) {
            opts = opts || {};
            flushSessionSave();
            var job = ++jobSeq;
            // cancel whatever the previous upload was doing
            if (asr && workerReady) { try { asr.abort(); } catch (e) { /* noop */ } }

            file = f;
            overrides = {};
            manualRegions = [];
            edits = {}; items = []; selectedId = null; pcmAudio = null;
            invalidateAuto();
            resetHistory();
            markIn = null; stopAt = null; nowWordIdx = -1; keyStats = {};
            auditionRegion = null;
            capMergeReported = false;
            tierWallDurS = 0;
            // the preview's measurements and the legacy-edit offer are per file
            sync.reported = false; sync.legacyAsked = false; sync.lagS = []; sync.fails = 0; noSyncReported = false;
            var oldLegacy = $('pc-legacy-note'); if (oldLegacy) oldLegacy.remove(); legacyNote = null;
            var oldNotice = $('pc-tier-notice'); if (oldNotice) oldNotice.remove();
            clearRetryNotice();
            // streamed-scan state must not leak from the previous file
            detector = null; lastStreamPaint = 0;
            if (streamPaintTimer) { clearTimeout(streamPaintTimer); streamPaintTimer = null; }
            words = []; hits = []; regions = [];
            channels = 2; srcRate = 44100; duration = 0; pcm = null; srcStartS = 0;
            masterPeaks = null; peaksCache = null;
            // лейн «только голос» бросает окна прежнего файла сразу, а не когда
            // распознавание нового закончится (его разделение уже никому не нужно)
            stopVoiceSrc();
            if (voiceLane) { try { voiceLane.update({ active: false, file: f, regions: [] }); } catch (e) { /* noop */ } }
            resetZoom();
            // a previous upload's media element must not survive into a run
            // whose extract fails before setupPreview() replaces it
            if (mediaEl) { try { mediaEl.pause(); mediaEl.remove(); } catch (e) { /* noop */ } mediaEl = null; }
            cancelAnimationFrame(rafId);
            var tBox = $('pc-transcript'); if (tBox) tBox.innerHTML = '';
            $('pc-results').style.display = 'none';
            syncWide();
            updateExportEnabled();
            fileExt = (f.name.split('.').pop() || '').toLowerCase();
            isVideoFile = /^(mp4|mov|m4v|webm|mkv|avi|wmv|flv|ts|3gp)$/.test(fileExt) || (f.type || '').indexOf('video') === 0;
            applyMediaMode();   // file-adaptive UI: show video-cue OR audio-export block
            var oldWall = $('pc-wall-notice'); if (oldWall) oldWall.remove();
            var wallNow = copyWallRefusal(null, 'load');
            if (wallNow) showWallNotice(wallNow);

            // uploader collapses into the shared file-info card
            var zone = $('upload-zone');
            if (zone) zone.hidden = true;
            if (window.ToolFileInfo) {
                ToolFileInfo.show('pc-fileinfo', f, {
                    onRemove: resetAll,
                    onReplace: function (nf) { processFile(nf); }
                });
            }

            // Длительность из заголовка (~6 мс) — ДО модели, извлечения и
            // прочей необратимой работы: файл, который не помещается в память,
            // должен получить честный ответ сразу, а не через 5 минут краха.
            //
            // ⚡phase ПЕРЕД await, а не после: иначе на первом же `await` фаза
            // ещё держит значение ПРОШЛОГО файла ('ready'/'idle'), и всё, что
            // ждёт «тул освободился», считает новый прогон завершённым —
            // добавленный в это окно регион падает мимо (duration ещё 0).
            // Поймано контрактом profanity-video-container на 2-м файле.
            setPhase('extract');
            var durHint = await probeDurationFast(f);
            if (job !== jobSeq) return;
            if (!await maybeConfirmTooLong(durHint, job)) { resetAll(); return; }
            if (job !== jobSeq) return;

            $('pc-workspace').classList.add('active');
            prog.jobStart = performance.now();
            prog.stage = 'extract'; prog.realPct = 0; prog.rtf = 0; prog.totalS = 0;
            startTicker();
            setStage(i18n.stage_extract, 1);
            if (window.__ae) __ae('tool_use', { tool: slug, video: isVideoFile ? 1 : 0, sizeMB: Math.round(f.size / 1048576) });

            // A saved session for this very file: no model, no recognition.
            sessionKey = sessionKeyFor(f);
            sessionStartedAt = Date.now();
            var pn = $('pc-pending-note'); if (pn) pn.remove();
            var rn = $('pc-restored-note'); if (rn) rn.remove();
            var plist = $('pc-projects'); if (plist) plist.hidden = true;
            var saved = null;
            // a project — opened from a .tmbcensor file, or waiting for its recording
            var project = opts.project || null;
            if (!project && pendingProject && !opts.fresh) {
                project = await projectForPick(f);
                if (job !== jobSeq) return;
            }
            var pw = $('pc-project-wait'); if (pw) pw.remove();
            pendingProject = null;
            if (project && prj()) {
                saved = prj().toSession(project);
            } else if (sessionKey && !opts.fresh) {
                // «Continue» on a listed project: that one first, when it is this recording
                if (preferredSessionKey) {
                    var pref = null;
                    try { pref = await sessionStore().get(preferredSessionKey); } catch (e) { pref = null; }
                    if (job !== jobSeq) return;
                    if (usableSession(pref) && pref.fileSize === f.size) { saved = pref; saved.key = preferredSessionKey; }
                }
                preferredSessionKey = '';
                if (!saved) {
                    try { saved = await sessionStore().get(sessionKey); } catch (e) { saved = null; }
                    if (job !== jobSeq) return;
                    if (!usableSession(saved)) saved = null;
                }
                if (!saved) {
                    try { saved = await sameFileSession(f); } catch (e) { saved = null; }
                    if (job !== jobSeq) return;
                }
            }

            try {
                setPhase('extract');
                var workerP = saved ? null : ensureWorker();   // model download in parallel with extract
                if (workerP) workerP.catch(function () { /* surfaced below via await; avoid unhandledrejection */ });
                var extracted = await extractPcm();
                if (job !== jobSeq) return;            // superseded by a newer upload
                pcm = extracted;
                duration = pcm.length / SR;
                prog.totalS = duration;
                buildPeaks();
                // Source channel count + sample rate for the export filtergraph
                // (the silence/beep atrim+concat must match the original layout
                // & rate or the re-mux misaligns). ONE FFmpeg probe of the real
                // file gives both. The old path sliced the first 512 KB and ran
                // decodeAudioData on it for the channel count — but (a) a
                // moov-at-end MP4 head doesn't decode, so channels silently fell
                // back to stereo on exactly those files, and (b) the headless
                // slice tripped the global decodeAudioData→FFmpeg fallback,
                // firing a spurious "moov atom not found" tool_error on EVERY
                // video upload (telemetry 2026-06-29: ~180 errors / 48 h, the
                // tool's #1 error). One probe, both values, correct for
                // moov-at-end, zero false errors.
                try {
                    var as = await FFmpegProcessor.probeAudioStream(file);
                    if (as) {
                        if (as.channels > 0) channels = as.channels;
                        if (as.sampleRate > 7999) srcRate = as.sampleRate;
                        if (typeof as.startSec === 'number' && isFinite(as.startSec)) srcStartS = as.startSec;
                    }
                } catch (e) { /* keep defaults (stereo / 44100) */ }
                if (job !== jobSeq) return;

                // Извлечение закончено — движок больше не нужен до самого
                // экспорта (минуты спустя, и не в каждой сессии). Инстанс,
                // раздутый большим видео, держит сотни МБ (WASM-память назад
                // не отдаётся), и рядом вот-вот развернётся ASR-модель; на
                // iPhone эта сумма — смерть вкладки без единого события
                // (замер: 60% беззвучных смертей iOS+видео, ни один файл
                // >9 МБ до распознавания не дожил). Экспортная ветка сама
                // поднимает движок заново через идемпотентный init().
                try { FFmpegProcessor.release(); } catch (e) { /* не критично */ }
                // Длину до работы не узнали (заголовок молчит, превью тоже) —
                // тогда её впервые знает извлечённый звук, и потолки судят ею:
                // иначе предупреждение о тарифе не показано, а стена на
                // скачивании не взведена (tierWallDurS = 0).
                if (!(durHint > 0) && duration > 0) {
                    if (window.__ae) __ae('feature_use', { tool: slug, feature: 'duration_gate_late', dur_s: Math.round(duration) });
                    if (!await maybeConfirmTooLong(duration, job)) { if (job === jobSeq) resetAll(); return; }
                    if (job !== jobSeq) return;
                }
                // …и только теперь можно разворачивать ORT-сессию: её создание
                // — второй горб памяти, он не должен совпасть с первым.
                if (asr && asr.go) asr.go();

                setupPreview();
                // the saved transcript belongs to THIS audio only if the length matches
                if (saved && Math.abs((saved.duration || 0) - duration) < 1.0) {
                    restoreSession(saved);
                    if (project) {
                        await applyProjectExtras(project);
                        if (job !== jobSeq) return;
                        // the project now lives in this browser too, under this recording
                        saveSessionNow();
                    }
                    return;
                }
                if (project) {
                    // another recording (or another cut of it): its words would land elsewhere
                    window.__toast((i18n.project_len_mismatch || 'The project was made on a recording of :p, this one is :d — recognising it anew.')
                        .replace(':p', fmtTime(saved ? saved.duration : 0)).replace(':d', fmtTime(duration)), 'warning');
                    if (window.__ae) __ae('feature_use', { tool: slug, feature: 'project_open', ok: 0, code: 'LENGTH', dp: Math.round(saved ? saved.duration : 0), d: Math.round(duration) });
                }
                if (!workerP) {
                    workerP = ensureWorker();
                    if (asr && asr.go) asr.go();
                }
                setStage(i18n.stage_model, 1);
                await workerP;
                if (job !== jobSeq) return;

                // Honest pre-flight: duration + backend are now both known.
                // A long job is announced BEFORE it burns the user's time.
                if (!await maybeConfirmLongJob(job)) {
                    if (job !== jobSeq) return;
                    resetAll();
                    return;
                }
                if (job !== jobSeq) return;

                setPhase('asr');
                progReal('asr', 0, { rtf: observedRtf, totalS: duration });
                setStage(i18n.stage_asr, 0);
                var asrT0 = Date.now();
                var res = await runAsr(pcm, job);
                if (job !== jobSeq) return;
                // Whisper has no partial-word stream: seat the transcript from the
                // final list. (GigaAM already filled `words` via words_partial —
                // re-seating from its identical final list is a no-op repaint.)
                if ((res.words || []).length !== words.length) renderTranscript(res.words || []);
                var asrRtf = (res.stats && res.stats.rtf) || (duration / Math.max(0.001, (performance.now() - prog.jobStart) / 1000));
                observedRtf = asrRtf;
                saveSetting('observed_rtf', String(Math.round(observedRtf * 10) / 10));
                // End-to-end ASR rate (mel+decode, excl. extract/model) for the
                // pre-flight estimate, keyed per engine+backend. observed_rtf
                // above keeps its historical meaning (progress-bar seed).
                var e2e = duration / Math.max(0.001, (Date.now() - asrT0) / 1000);
                saveSetting(rtfKey(), String(Math.round(e2e * 100) / 100));
                // 'auto' → adopt the language Whisper actually decided on, so the
                // lexicon matches the speech and the UI can name it honestly.
                applyDetectedLang(res.detectedLang);
                if (window.__ae) __ae('feature_use', {
                    tool: slug, feature: 'asr_done', rtf: Math.round(asrRtf * 10) / 10,
                    audioS: Math.round((res.stats && res.stats.audioS) || duration), words: res.words.length,
                    engine: asr.engine, lang: activeLang || 'unknown', picked: settings.lang
                });

                setPhase('ready');
                setStage('', -1);
                // Полный проход — устройство смогло: серия обрывов кончилась.
                // Счётчик, баннер и событие crash_retry_succeeded (мера пользы
                // совета «закройте вкладки и повторите») — у общего примитива.
                try { if (window.__crashMarker) window.__crashMarker.passed(slug); } catch (e) { /* noop */ }
                $('pc-results').style.display = 'block';
                syncWide();
                var noSpeech = $('pc-no-speech');
                if (noSpeech) noSpeech.style.display = words.length ? 'none' : '';
                renderWave();
                updatePlayhead();
                syncZoomUI();
                rescan();
                if (window.__ae) {
                    // how the censors were placed (ProfanityRegions.hitRegion().how):
                    // moved — nothing sounded where the recogniser wrote the word;
                    // pinned — both edges are pauses the audio shows; fluent — the
                    // end is the halfway guess (speech ran on into the next word)
                    var placed = { moved: 0, pinned: 0, fluent: 0 };
                    Object.keys(autoCache).forEach(function (k) {
                        var how = (autoCache[k] && autoCache[k].how) || '';
                        if (how.indexOf('m') >= 0) placed.moved++;
                        if (how.indexOf('s') >= 0 && how.indexOf('e') >= 0) placed.pinned++;
                        if (how.indexOf('f') >= 0) placed.fluent++;
                    });
                    __ae('feature_use', {
                        tool: slug, feature: 'detect_done', found: hits.length, words: words.length,
                        lang: activeLang || 'unknown', engine: asr.engine,
                        fuzzy: hits.filter(function (h) { return h.kind === 'fuzzy'; }).length,
                        moved: placed.moved, pinned: placed.pinned, fluent: placed.fluent
                    });
                }
                lexSeenSignal();
                lexListSignalsSoon(3000);
                saveSessionNow();
            } catch (err) {
                if (job !== jobSeq) return;            // stale failure from a superseded job
                setStage('', -1);
                setPhase('idle');
                if (String(err && err.message) !== 'aborted') {
                    reportAndToast(err, slug + ':process');
                    if (isRetryable(err)) showRetryNotice(err);
                }
            }
        }

        function resetAll() {
            flushSessionSave();
            jobSeq++;
            if (asr && workerReady) { try { asr.abort(); } catch (e) { /* noop */ } }
            try { if (FFmpegProcessor.cancel) FFmpegProcessor.cancel(); } catch (e) { /* noop */ }
            setStage('', -1);
            setPhase('idle');
            if (mediaEl) { try { mediaEl.pause(); mediaEl.remove(); } catch (e) { /* noop */ } mediaEl = null; }
            cancelAnimationFrame(rafId);
            stopCustomSrc();
            if (srcNode) { try { srcNode.disconnect(); } catch (e) { /* noop */ } srcNode = null; }
            // tear the WebAudio graph down completely: the oscillator would
            // otherwise keep running (and the context keep the audio device)
            // while the tool sits at the upload zone. The next file's play
            // handler rebuilds everything from the !audioCtx branch.
            // customBuffer/customFile survive on purpose — the chosen censor
            // sound is a setting, not per-file state.
            if (beepOsc) { try { beepOsc.stop(); beepOsc.disconnect(); } catch (e) { /* noop */ } beepOsc = null; }
            if (beepGain) { try { beepGain.disconnect(); } catch (e) { /* noop */ } beepGain = null; }
            stopVoiceSrc();
            if (voiceGain) { try { voiceGain.disconnect(); } catch (e) { /* noop */ } voiceGain = null; }
            if (gainNode) { try { gainNode.disconnect(); } catch (e) { /* noop */ } gainNode = null; }
            if (audioCtx) { try { audioCtx.close(); } catch (e) { /* noop */ } audioCtx = null; }
            if (mediaUrl) { URL.revokeObjectURL(mediaUrl); mediaUrl = null; }
            file = null; pcm = null; masterPeaks = null; peaksCache = null;
            words = []; hits = []; regions = []; manualRegions = []; overrides = {};
            edits = {}; items = []; selectedId = null; pcmAudio = null;
            invalidateAuto();
            voiceLaneUpdate();   // лейн «только голос» бросает окна прежнего файла
            resetHistory();
            renderInspector();
            resetZoom();
            auditionRegion = null;
            capMergeReported = false;
            tierWallDurS = 0;
            // the preview's measurements and the legacy-edit offer are per file
            sync.reported = false; sync.legacyAsked = false; sync.lagS = []; sync.fails = 0; noSyncReported = false;
            var oldLegacy = $('pc-legacy-note'); if (oldLegacy) oldLegacy.remove(); legacyNote = null;
            var oldNotice = $('pc-tier-notice'); if (oldNotice) oldNotice.remove();
            var oldWallN = $('pc-wall-notice'); if (oldWallN) oldWallN.remove();
            clearRetryNotice();
            detector = null; lastStreamPaint = 0;
            if (streamPaintTimer) { clearTimeout(streamPaintTimer); streamPaintTimer = null; }
            duration = 0; isVideoFile = false;
            applyMediaMode();   // re-hide both media-specific control blocks
            var tBox = $('pc-transcript'); if (tBox) tBox.innerHTML = '';
            $('pc-results').style.display = 'none';
            syncWide();
            $('pc-workspace').classList.remove('active');
            if (window.ToolFileInfo) ToolFileInfo.hide('pc-fileinfo');
            var zone = $('upload-zone');
            if (zone) {
                zone.hidden = false;
                if (zone.__uploaderReset) zone.__uploaderReset();
            }
            // back at the start: the saved projects are named again (the one
            // just closed included — its last edits were saved on the way out)
            setTimeout(renderProjectsList, 1500);
        }

        /* The editor takes the free width beside the 900 px column when the
         * main column then gets MORE than it had — on a 2560 px screen the
         * column used 900 of 2296 px («полэкрана не задействовано», chat 640).
         * The measuring and the width are the shared WideWorkspace's
         * (public/js/wide-workspace.js); this tool says only WHEN (the editor
         * is open) and how wide its side column is, and redraws the wave. */
        var wideCtl = null, wideReported = false;
        function syncWide() {
            if (!wideCtl) {
                var ws = $('pc-workspace');
                if (!ws || !window.WideWorkspace) return;
                wideCtl = window.WideWorkspace.attach(ws, {
                    when: function () {
                        var results = $('pc-results');
                        return !!(results && results.style.display === 'block' && ws.classList.contains('active'));
                    },
                    // the side column (.pc-side, CSS clamp(340px, 24%, 420px)) + its gap
                    reserve: function (want) { return Math.min(420, Math.max(340, want * 0.24)) + 16; },
                    // the wave must gain a real width, not a few pixels for a new layout
                    minGain: 120,
                    onChange: function (on, want, flipped) {
                        if (!flipped) return;
                        // the wave measures its box: redraw at the new width
                        peaksCache = null; ovPeaks = null;
                        renderWave(); updatePlayhead();
                        if (on && !wideReported && window.__ae) {
                            wideReported = true;
                            __ae('feature_use', { tool: slug, feature: 'wide_editor', w: want, vw: window.innerWidth });
                        }
                    }
                });
                return;
            }
            wideCtl.sync();
        }

        /* «Скачать MP3» — the button names what it gives (CLAUDE.md, the
           format at the download); a video keeps its own container. */
        function syncExportLabel() {
            var btn = $('pc-export-btn'), fmtSel = $('pc-format');
            if (!btn || btn.getAttribute('aria-busy') === 'true') return;
            if (file && !isVideoFile && fmtSel) btn.textContent = (i18n.export_btn_fmt || 'Download :fmt').replace(':fmt', fmtSel.value.toUpperCase());
            else btn.textContent = i18n.export_btn || btn.textContent;
        }
        function updateExportEnabled() {
            var btn = $('pc-export-btn');
            if (btn) btn.disabled = phase !== 'ready' || !regions.length;
            syncExportLabel();
            var canProject = phase === 'ready' && !!file && words.length > 0 && !!window.ProfanityCensorProject;
            var ps = $('pc-project-save'), psm = $('pc-project-save-media'), psz = $('pc-project-media-size');
            if (ps) ps.disabled = !canProject;
            if (psm) psm.disabled = !canProject;
            if (psz) psz.textContent = file ? '+' + fmtSize(file.size) : '';
            var wavBtn = $('pc-export-wav');
            if (wavBtn) wavBtn.disabled = phase !== 'ready' || !regions.length;
            var rep = $('pc-report-buttons');
            if (rep) rep.style.display = (activeHitIndices().length || manualRegions.length) ? '' : 'none';
        }

        /* ---------------- settings-panel visibility ---------------- */
        // «Только голос» при своём звуке — флажок остаётся, но говорит, что не
        // действует: молча выключенная настройка читается как сломанная.
        function syncVoiceOnlyUi() {
            var cb = $('pc-voice-only'), note = $('pc-voice-only-custom');
            if (cb) cb.checked = settings.voiceOnly;
            if (note) note.hidden = !(settings.voiceOnly && settings.mode === 'custom');
        }
        function updateSoundRows() {
            var freq = $('pc-freq-row'), vol = $('pc-vol-row'), custom = $('pc-custom-row');
            if (freq) freq.classList.toggle('hidden', settings.mode !== 'beep');
            if (vol) vol.classList.toggle('hidden', settings.mode !== 'beep' && settings.mode !== 'custom');
            // auto level is a beep thing; with it on, the slider is not in charge
            var autoRow = $('pc-beep-auto-row'), autoHint = $('pc-beep-auto-hint'), volIn = $('pc-vol');
            var beepAutoOn = settings.mode === 'beep' && settings.beepAuto;
            if (autoRow) autoRow.hidden = settings.mode !== 'beep';
            if (autoHint) autoHint.hidden = !beepAutoOn;
            if (volIn) volIn.disabled = beepAutoOn;
            if (custom) custom.classList.toggle('active', settings.mode === 'custom');
            var prev = $('pc-sound-preview');
            if (prev) prev.style.display = (settings.mode === 'beep' || settings.mode === 'custom') ? '' : 'none';
        }

        /* ---------------- wiring ---------------- */
        function bindUi() {
            var zone = $('upload-zone');
            if (window.__bindUploaderFile) {
                window.__bindUploaderFile(zone, function (f) { if (f) processFile(f); });
            } else {
                zone.addEventListener('tool-uploader:file', function (e) {
                    var f = e.detail && e.detail.file;
                    if (f) processFile(f);
                });
            }

            var cancelBtn = $('pc-cancel');
            if (cancelBtn) cancelBtn.addEventListener('click', function () {
                jobSeq++;
                if (asr && workerReady) { try { asr.abort(); } catch (e) { /* noop */ } }
                try { if (FFmpegProcessor.cancel) FFmpegProcessor.cancel(); } catch (e) { /* noop */ }
                // экспорт с «только голосом» может стоять на разделении окон
                try { if (voiceLane && voiceLane.cancel) voiceLane.cancel(); } catch (e) { /* noop */ }
                setStage('', -1);
                setPhase('idle');
            });

            var tBox = $('pc-transcript');
            if (tBox) tBox.addEventListener('click', function (e) {
                var span = e.target.closest('.pc-word');
                if (!span) return;
                toggleWord(Number(span.dataset.i));
            });

            var playBtn = $('pc-play');
            if (playBtn) playBtn.addEventListener('click', togglePlay);

            bindWaveform();
            bindEditorKeys();
            // the tab closing or hidden within the save's debounce keeps the last edit
            window.addEventListener('pagehide', flushSessionSave);
            document.addEventListener('visibilitychange', function () { if (document.hidden) flushSessionSave(); });
            var wideRaf = 0;
            window.addEventListener('resize', function () {
                if (wideRaf) return;
                wideRaf = requestAnimationFrame(function () { wideRaf = 0; syncWide(); });
            });

            var rateBtn = $('pc-rate');
            if (rateBtn) rateBtn.addEventListener('click', cycleRate);
            var tallBtn = $('pc-wave-tall'), waveWrap = $('pc-wave-wrap');
            function applyTall(on) {
                if (waveWrap) waveWrap.classList.toggle('tall', on);
                if (tallBtn) tallBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
            }
            applyTall(loadSetting('waveTall', '0') === '1');
            if (tallBtn) tallBtn.addEventListener('click', function () {
                var on = !(waveWrap && waveWrap.classList.contains('tall'));
                applyTall(on);
                saveSetting('waveTall', on ? '1' : '0');
                if (window.__ae) __ae('feature_use', { tool: slug, feature: 'wave_tall', on: on ? 1 : 0 });
            });
            var undoBtn = $('pc-undo'), redoBtn = $('pc-redo');
            if (undoBtn) undoBtn.addEventListener('click', undo);
            if (redoBtn) redoBtn.addEventListener('click', redo);
            syncUndoUI();

            // the bar under the wave: the censor in hand and what to do with it
            var selBar = $('pc-sel');
            if (selBar) selBar.addEventListener('click', function (e) {
                var b = e.target.closest('button');
                if (!b || b.disabled) return;
                var act = b.getAttribute('data-act');
                if (act === 'prev') stepSelection(-1);
                else if (act === 'next') stepSelection(1);
                else if (!selectedId) return;
                else if (act === 'nudge') {
                    // Shift = a coarser step, as in every editor
                    var step = (e.shiftKey ? 0.1 : 0.02) * (b.getAttribute('data-dir') === '-' ? -1 : 1);
                    nudgeItem(selectedId, b.getAttribute('data-edge'), step);
                } else if (act === 'ear') { var it = itemById(selectedId); if (it) auditionHit(it.start, it.end); }
                else if (act === 'around') { var ia = itemById(selectedId); if (ia) playAround(ia); }
                else if (act === 'reset') resetItem(selectedId, 'inspector');
                else if (act === 'delete') deleteItem(selectedId, 'inspector');
            });

            // mode pills (VideoCensor-style segmented control) / sliders / tiers / lists
            var modeWrap = $('pc-mode');
            function applyMode(m) {
                settings.mode = m;
                saveSetting('mode', m);
                if (modeWrap) modeWrap.querySelectorAll('[data-mode]').forEach(function (b) {
                    b.classList.toggle('active', b.getAttribute('data-mode') === m);
                });
                updateSoundRows();
                if (gateState !== 'ready' && m === 'custom' && mediaEl && !mediaEl.paused) ensureCustomSrc();
                if (m !== 'custom') stopCustomSrc();
                pushGateParams();
                // свой звук с «только голосом» не сочетается (другой граф)
                syncVoiceOnlyUi();
                voiceLaneUpdate();
            }
            if (modeWrap) {
                modeWrap.querySelectorAll('[data-mode]').forEach(function (btn) {
                    btn.classList.toggle('active', btn.getAttribute('data-mode') === settings.mode);
                    btn.addEventListener('click', function () { applyMode(btn.getAttribute('data-mode')); });
                });
            }
            var freq = $('pc-freq'), freqOut = $('pc-freq-out');
            if (freq) {
                freq.value = String(settings.beepFreq);
                if (freqOut) freqOut.textContent = settings.beepFreq + ' Hz';
                freq.addEventListener('input', function () {
                    settings.beepFreq = parseInt(freq.value, 10) || 1000;
                    if (freqOut) freqOut.textContent = settings.beepFreq + ' Hz';
                    saveSetting('beepFreq', String(settings.beepFreq));
                    if (beepOsc) beepOsc.frequency.value = settings.beepFreq;
                    pushGateParams();
                });
            }
            var vol = $('pc-vol'), volOut = $('pc-vol-out');
            if (vol) {
                vol.value = String(settings.beepVol);
                if (volOut) volOut.textContent = settings.beepVol + '%';
                vol.addEventListener('input', function () {
                    settings.beepVol = parseInt(vol.value, 10) || 50;
                    if (volOut) volOut.textContent = settings.beepVol + '%';
                    saveSetting('beepVol', String(settings.beepVol));
                    levelRegions();
                    // the replacement sound's level is the same slider
                    if (settings.mode === 'custom') pushGateCustom();
                });
            }
            var autoCb = $('pc-beep-auto');
            if (autoCb) {
                autoCb.checked = settings.beepAuto;
                autoCb.addEventListener('change', function () {
                    settings.beepAuto = autoCb.checked;
                    saveSetting('beepAuto', autoCb.checked ? '1' : '0');
                    levelRegions();
                    updateSoundRows();
                    if (window.__ae) __ae('feature_use', { tool: slug, feature: 'beep_auto', on: autoCb.checked ? 1 : 0 });
                });
            }
            var voCb = $('pc-voice-only');
            if (voCb) {
                voCb.checked = settings.voiceOnly;
                voCb.addEventListener('change', function () {
                    settings.voiceOnly = voCb.checked;
                    saveSetting('voiceOnly', voCb.checked ? '1' : '0');
                    syncVoiceOnlyUi();
                    voiceLaneUpdate();
                    pushGateParams();
                    if (!voCb.checked) stopVoiceSrc();
                    if (window.__ae) __ae('feature_use', { tool: slug, feature: 'voice_only', action: voCb.checked ? 'on' : 'off',
                        video: isVideoFile ? 1 : 0, regions: regions.length, dur_s: Math.round(duration) });
                });
            }
            var pick = $('pc-custom-pick'), customInput = $('pc-custom-input');
            if (pick && customInput) {
                pick.addEventListener('click', function () { customInput.click(); });
                customInput.addEventListener('change', function () {
                    if (customInput.files && customInput.files[0]) loadCustomSound(customInput.files[0]);
                    customInput.value = '';
                });
            }
            var sndPrev = $('pc-sound-preview');
            if (sndPrev) sndPrev.addEventListener('click', previewCensorSound);
            // Repeat the replacement sound to fill a censor — off by default:
            // one leaf rustle is one leaf rustle (chat 640)
            var loopCb = $('pc-custom-loop');
            if (loopCb) {
                loopCb.checked = !!settings.customLoop;
                loopCb.addEventListener('change', function () {
                    settings.customLoop = loopCb.checked;
                    saveSetting('customLoop', loopCb.checked ? '1' : '0');
                    pushGateCustom();
                    if (window.__ae) __ae('feature_use', { tool: slug, feature: 'custom_loop', on: loopCb.checked ? 1 : 0 });
                });
            }
            var prevToggle = $('pc-preview-toggle');
            if (prevToggle) prevToggle.addEventListener('change', pushGateParams);

            [['pc-tier2', function (el) { settings.tier2 = el.checked; saveSetting('tier2', el.checked ? '1' : '0'); rescan(); }],
             ['pc-tier3', function (el) { settings.tier3 = el.checked; saveSetting('tier3', el.checked ? '1' : '0'); rescan(); }],
             ['pc-blacklist', function (el) { settings.blacklist = el.value; saveSetting('blacklist', el.value); rescan(); lexListSignalsSoon(8000); }],
             ['pc-whitelist', function (el) { settings.whitelist = el.value; saveSetting('whitelist', el.value); rescan(); lexListSignalsSoon(8000); }]
            ].forEach(function (b) {
                var el = $(b[0]);
                if (!el) return;
                var isList = (b[0] === 'pc-blacklist' || b[0] === 'pc-whitelist');
                if (b[0] === 'pc-blacklist') el.value = settings.blacklist;
                else if (b[0] === 'pc-whitelist') el.value = settings.whitelist;
                else el.checked = settings[b[0] === 'pc-tier2' ? 'tier2' : 'tier3'];
                el.addEventListener('change', function () { b[1](el); });
                // ⚡У <textarea> событие `change` приходит ТОЛЬКО при потере
                // фокуса. Пока юзер печатает слова и смотрит на расшифровку,
                // не происходит РОВНО НИЧЕГО — отсюда «пишу туда слова, но не
                // срабатывает» (Kaypon #1243). Импорт файлом при этом работал,
                // потому что он зовёт rescan() напрямую — тот же тул вёл себя
                // двумя разными способами. Слушаем `input` (каждое нажатие и
                // вставка) с дебаунсом: rescan пересобирает детектор и метит
                // весь транскрипт (у часового видео это 6000+ слов), на каждую
                // букву так делать нельзя. `change` оставлен — он безвреден и
                // ловит вставку через контекстное меню в старых браузерах.
                if (isList) {
                    var t = 0;
                    el.addEventListener('input', function () {
                        clearTimeout(t);
                        t = setTimeout(function () { b[1](el); }, 300);
                    });
                }
            });

            // Coverage slider (VideoCensor parity) — % of word censored.
            var cov = $('pc-coverage'), covOut = $('pc-coverage-out');
            if (cov) {
                cov.value = String(settings.coverage);
                if (covOut) covOut.textContent = settings.coverage + '%';
                cov.addEventListener('input', function () {
                    settings.coverage = parseInt(cov.value, 10) || 100;
                    if (covOut) covOut.textContent = settings.coverage + '%';
                    saveSetting('coverage', String(settings.coverage));
                    invalidateAuto();
                    refreshAll();
                });
            }

            // Video censor-cue selector (video tool only). Pills sync into a
            // single settings.videoFx; no re-export until the user re-runs.
            var fxWrap = $('pc-videofx');
            if (fxWrap) {
                fxWrap.querySelectorAll('[data-fx]').forEach(function (btn) {
                    btn.classList.toggle('active', btn.getAttribute('data-fx') === settings.videoFx);
                    btn.addEventListener('click', function () {
                        settings.videoFx = btn.getAttribute('data-fx');
                        saveSetting('videoFx', settings.videoFx);
                        fxWrap.querySelectorAll('[data-fx]').forEach(function (b2) {
                            b2.classList.toggle('active', b2 === btn);
                        });
                    });
                });
            }

            // .txt/.csv word-list import for both lists (VideoCensor parity).
            // Parses comma / newline / semicolon separated words, dedups,
            // appends to whatever's already typed.
            function wireListImport(btnId, inputId, taId, settingKey) {
                var btn = $(btnId), inp = $(inputId), ta = $(taId);
                if (!btn || !inp || !ta) return;
                btn.addEventListener('click', function () { inp.click(); });
                inp.addEventListener('change', function () {
                    var f = inp.files && inp.files[0];
                    inp.value = '';
                    if (!f) return;
                    if (f.size > 512 * 1024) { window.__toast(i18n.list_too_big || 'File too large', 'warning'); return; }
                    var rd = new FileReader();
                    rd.onload = function () {
                        var incoming = String(rd.result || '')
                            .split(/[\r\n,;]+/).map(function (s) { return s.trim(); }).filter(Boolean);
                        var existing = ta.value.split(/[\r\n,;]+/).map(function (s) { return s.trim(); }).filter(Boolean);
                        var seen = {}, merged = [];
                        existing.concat(incoming).forEach(function (w) {
                            var k = w.toLowerCase();
                            if (!seen[k]) { seen[k] = 1; merged.push(w); }
                        });
                        ta.value = merged.join('\n');
                        settings[settingKey] = ta.value;
                        saveSetting(settingKey, ta.value);
                        rescan();
                        lexListSignalsSoon(2000);
                        if (window.__toast) window.__toast((i18n.list_imported || 'Imported') + ': ' + incoming.length, 'success');
                    };
                    rd.readAsText(f);
                });
            }
            renderListCounts();   // сохранённый с прошлого визита список тоже должен быть виден
            wireListImport('pc-blacklist-import', 'pc-blacklist-file', 'pc-blacklist', 'blacklist');
            wireListImport('pc-whitelist-import', 'pc-whitelist-file', 'pc-whitelist', 'whitelist');

            var br = $('pc-bitrate');
            if (br) {
                br.value = String(settings.bitrate);
                br.addEventListener('change', function () {
                    settings.bitrate = parseInt(br.value, 10) || 192;
                    saveSetting('bitrate', String(settings.bitrate));
                });
            }
            var fmtSel = $('pc-format');
            if (fmtSel) {
                var savedFmt = loadSetting('format', 'mp3');
                if (['mp3', 'wav', 'm4a', 'ogg', 'flac'].indexOf(savedFmt) !== -1) fmtSel.value = savedFmt;
                var syncBr = function () {
                    // lossless has no bitrate: the field goes, label and all
                    var lossless = fmtSel.value === 'wav' || fmtSel.value === 'flac';
                    var field = $('pc-bitrate-field');
                    if (field) field.hidden = lossless; else if (br) br.style.display = lossless ? 'none' : '';
                    syncExportLabel();
                };
                fmtSel.addEventListener('change', function () { saveSetting('format', fmtSel.value); syncBr(); });
                syncBr();
            }

            // Media tabs: clicking the inactive tab navigates to the other
            // page (its href). Clicking the ALREADY-active tab is a no-op — you
            // are already in that mode (possibly via auto-detect on this same
            // URL), so don't reload and discard a loaded file. The visual
            // active state is driven by updateMediaTabs().
            var mtabs = $('pc-mediatabs');
            if (mtabs) {
                mtabs.addEventListener('click', function (e) {
                    var a = e.target.closest ? e.target.closest('[data-media]') : null;
                    if (!a) return;
                    if (a.classList.contains('active')) { e.preventDefault(); return; }
                    // inactive → allow navigation (href). If a file is loaded,
                    // the user is deliberately switching tools; the new page
                    // starts fresh, which is the expected "other tool" semantics.
                });
            }
            updateMediaTabs();

            var exp = $('pc-export-btn');
            if (exp) exp.addEventListener('click', function () { exportFile(); });
            // «Скачать» под плеером — тот же экспорт; её вид — копия основной кнопки
            // (включена ли, «Обработка…» со спиннером, «Скачать MP3»), чтобы ни одна
            // ветка exportFile не знала о второй кнопке.
            var near = $('pc-export-near');
            if (exp && near) {
                var mirrorNear = function () {
                    near.disabled = exp.disabled;
                    near.textContent = exp.textContent;
                    if (exp.getAttribute('aria-busy') === 'true') near.setAttribute('aria-busy', 'true');
                    else near.removeAttribute('aria-busy');
                };
                new MutationObserver(mirrorNear).observe(exp, { attributes: true, childList: true, characterData: true, subtree: true });
                mirrorNear();
                near.addEventListener('click', function () {
                    if (window.__ae) __ae('feature_use', { tool: slug, feature: 'export_near' });
                    exportFile();
                });
            }
            var ps = $('pc-project-save'), psm = $('pc-project-save-media');
            if (ps) ps.addEventListener('click', function () { saveProject(false, 'btn'); });
            if (psm) psm.addEventListener('click', function () { saveProject(true, 'btn'); });
            var expWav = $('pc-export-wav');
            if (expWav) expWav.addEventListener('click', function () { exportFile({ audioOnly: true }); });
            ['txt', 'csv', 'srt', 'json'].forEach(function (k) {
                var b = $('pc-report-' + k);
                if (b) b.addEventListener('click', function () { downloadReport(k); });
            });

            updateSoundRows();
        }

        bindUi();
        renderLangState(null);
        pendingSessionNote();
        renderProjectsList();
        // the project door beside the upload zone: a .tmbcensor opens the work;
        // a recording picked there by mistake goes where it was meant to go
        if (window.__projectDoor) {
            try {
                window.__projectDoor.mount('[data-project-door]', {
                    slug: slug, onFile: openProjectFile,
                    onOther: function (f) { processFile(f); }
                });
            } catch (e) { /* the door is a convenience */ }
        }

        /* Обнаружение прошлой жёсткой смерти. Сам вердикт — у общего примитива:
           он проверяет, жив ли хозяин маркера (вторая вкладка, которая ещё
           считает, — не крах), снимает маркер на pagehide и возвращает его
           после bfcache. Здесь — только окно свежести, место и тексты баннера.
           Со ВТОРОГО обрыва подряд текст меняется с «попробуйте снова» на
           «этому устройству, скорее всего, не хватает памяти»: один раз —
           могло не повезти с фоновыми вкладками, два — это уже про устройство,
           и звать на третий круг нечестно. */
        (function crashRecovery() {
            if (!window.__crashMarker || typeof window.__crashMarker.check !== 'function') return;
            try {
                window.__crashMarker.check(slug, {
                    maxAgeS: crashMarkerMaxAgeS(),
                    mount: $('upload-zone'),
                    texts: { title: i18n.crash_title, body: i18n.crash_body, body_repeat: i18n.crash_body_repeat },
                    // имя баннера, под которым его знают стенды этого тула
                    onCrash: function (info) { if (info && info.banner) info.banner.id = 'pc-crash-note'; }
                });
            } catch (e) { /* детектор просто молчит */ }
        })();

        return {
            processFile: processFile,
            reset: resetAll,
            addManualRegion: addManualRegion,
            selectItem: selectItem,
            undo: undo,
            redo: redo,
            changeLanguage: changeLanguage,
            /* «Глушить только голос»: лейн (vocal-only-export.js) приносит
               разделитель и подложку, ядро — граф, предпросмотр и экспорт. */
            setVoiceLane: function (lane) {
                voiceLane = lane || null;
                syncVoiceOnlyUi();
                voiceLaneUpdate();
            },
            // лейн разделяет окна в фазе ready — метка для детектора падений
            // (экспорт метит себя сам, его не перебиваем)
            voiceBusy: function (on) {
                if (phase !== 'ready') return;
                if (on) hotMark('voice'); else hotClear();
            },
            _state: function () {
                return {
                    words: words, hits: hits, regions: regions, manualRegions: manualRegions,
                    items: items, edits: edits, selectedId: selectedId,
                    undoDepth: undoStack.length, redoDepth: redoStack.length, media: mediaEl,
                    auditionRegion: auditionRegion, phase: phase, settings: settings,
                    activeLang: activeLang, engine: asr ? asr.engine : null,
                    duration: duration,
                    // увеличение дорожки — для сторожей: zoom, его потолок и
                    // ширина ленты (в ней и живут координаты фрагментов)
                    zoom: zoom, maxZoom: maxZoom(), waveW: waveW,
                    scrollLeft: ($('pc-wave-wrap') || {}).scrollLeft || 0,
                    /* Исходный файл и его тип — для ДОПОЛНИТЕЛЬНЫХ лейнов
                       страниц: у /profanity-remover это «музыка остаётся»
                       (разделение на стемы), у /profanity-remover-video —
                       чистка субтитров. Оба работают ПОВЕРХ движка и ничего в
                       нём не меняют, поэтому им нужен только доступ к тому,
                       что движок уже держит. */
                    file: file, isVideoFile: isVideoFile, slug: slug,
                    srcRate: srcRate, channels: channels, voiceOnly: voiceOnlyActive(),
                    srcStartS: srcStartS, zeroCopy: zeroCopyCapable(),
                    // preview graph — test hooks (pc-preview-align-scratch taps
                    // these to record what the preview really outputs)
                    audioCtx: audioCtx, gainNode: gainNode, beepGain: beepGain,
                    voiceGain: voiceGain, voiceSrcId: voiceSrcId,
                    lookahead: previewLookahead(),
                    // the measured preview: the gate node (stands record its
                    // output) and where it believes the file is
                    gateNode: gateNode, gateState: gateState,
                    sync: { locked: sync.locked, map: sync.map, fails: sync.fails, lagS: syncLagS(), gen: sync.gen, hist: sync.hist.slice() }
                };
            }
        };
    }

    window.ProfanityCensorTool = { create: create };
})(window);
